<script setup lang="ts">
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'

  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'

  import AutoSavingIndicator from '@/components/admin/AutoSavingIndicator.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import AgentActionWidget from '@/components/agent-actions/AgentActionWidget.vue'
  import AiAssistantWidget from '@/components/agent-actions/AiAssistantWidget.vue'
  import AiLauncherButton from '@/components/agent-actions/AiLauncherButton.vue'
  import CreateAppointmentModal from '@/components/app/cases/appointmentCreationModal.vue'
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import SelectDocumentsDialog from '@/components/app/cases/documents/SelectDocumentsDialog.vue'
  import SelectUnifiedDocumentsDialog from '@/components/app/cases/documents/SelectUnifiedDocumentsDialog.vue'
  import TemplateDialog from '@/components/app/cases/documents/TemplateDialog.vue'
  import LegacyCaseHeader from '@/components/app/cases/legacy/LegacyCaseHeader.vue'
  import LegacyEntityForm from '@/components/app/cases/legacy/LegacyEntityForm.vue'
  import LegacyOpponentCards from '@/components/app/cases/legacy/LegacyOpponentCards.vue'
  import PendingProposedChangesDialog from '@/components/app/cases/PendingProposedChangesDialog.vue'
  import EmbeddableOfficePreview from '@/components/app/office/EmbeddableOfficePreview.vue'
  import TrashIcon from '@/components/icons/TrashIcon.vue'
  import NewPdfViewer from '@/components/pdf/NewPdfViewer.vue'
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import SelectPdfFormsDialog from '@/components/pdf/SelectPdfFormsDialog.vue'
  import SignModal from '@/components/pdf/SignModal.vue'
  import EmailDeliveryStatusModal from '@/components/app/cases/delivery/EmailDeliveryStatusModal.vue'
  import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
  } from '@/components/ui/accordion'
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Switch } from '@/components/ui/switch'
  import { useToast } from '@/components/ui/toast'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useDocumentThumbnail } from '@/composables/useDocumentThumbnail'
  import {
    type AttachedDocument,
    DocumentTypeIdEnum,
    type FormDocumentProperties,
    type QuestionnaireDocumentProperties
  } from '@/composables/useEmailPopover'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useOfficeDocument } from '@/composables/useOfficeDocument'
  import { usePdfFormValidation } from '@/composables/usePdfFormValidation'
  import { useTranslation } from '@/composables/useTranslation'
  import { useAgentActionRating } from '@/composables/useAgentActionRating'
  import { useAiAssistantWidget } from '@/composables/useAiAssistantWidget'
  import { createEmailActionFlow } from '@/composables/ai-flows/emailActionFlow'
  import {
    useAiAssistantCaseDetailMetrics,
    watchAiAssistantFloatingViewOpened
  } from '@/composables/useAiAssistantCaseDetailMetrics'
  import { copyToClipboard, buildPortalUrl } from '@/utils/clipboard'
  import { isStringNotEmpty } from '@/utils/stringUtils'
  import router from '@/router'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore.ts'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import { useNewPdfStore } from '@/store/newPdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import {
    AiSettingsService,
    AgentActionsService,
    AppointmentProposalsService,
    AppointmentsService,
    AppointmentStatusEnum,
    type CustomerCase as Case,
    CasesService,
    type CustomerAppointment,
    type CustomerAppointmentProposal,
    type CustomerEmail,
    type CustomerEmailTemplate,
    type CustomerForm,
    type CustomerQuestionnaire,
    type CustomerQuestionnaireSubmission,
    DocumentsService,
    DocumentStatusEnum,
    DocumentTypeEnum,
    EmailsService,
    EmailTemplatesService,
    FlagEnum,
    FormsService,
    type PatchedCustomerCaseRequest,
    type Document as PdfDocument,
    PdfFormSubmissionsService,
    QuestionnairesService,
    QuestionnaireSubmissionsService,
    StateEnum,
    type TForm,
    type CustomerUser as User,
    type PDFFormTemplate,
    UnifiedDocumentTemplatesService,
    type UnifiedDocumentTemplate,
    EmailTemplateType,
    ResourceTypeEnum
  } from '@/types/generated'
  import { buildPrefillDataFromFormFields, prefillFormFields } from '@/utils/entityPrefillHelpers'
  import { useCaseChannelSubscription } from '@/composables/useChannelSubscription'
  import {
    Calendar1Icon,
    CalendarIcon,
    CalendarPlusIcon,
    CircleXIcon,
    Clock5Icon,
    CloudDownloadIcon,
    EllipsisVerticalIcon,
    Eye,
    FileIcon,
    InfoIcon,
    Link2,
    MapPinIcon,
    PencilIcon,
    UserIcon,
    CheckIcon,
    MailIcon,
    XCircleIcon,
    AlertTriangleIcon,
    ShieldXIcon,
    EyeIcon,
    MousePointerClickIcon,
    Loader2,
    Sparkles,
    ThumbsUp,
    ThumbsDown,
    X,
    RotateCw
  } from 'lucide-vue-next'
  import { AccordionHeader } from 'radix-vue'
  import { computed, h, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { ROUTES } from '@/router/routes'

  // Extended type for questionnaire submissions with additional properties needed for the UI
  type RequestedQuestionnaire = CustomerQuestionnaireSubmission & {
    document_type: string
    filename: string
    created_at: string
  }

  // Union type for all requested documents (questionnaires and PDFs)
  type RequestedDocuments = RequestedQuestionnaire | PdfDocument

  export interface DocumentFormState {
    fieldValues: Map<string, any>
    validationErrors: Map<string, string>
  }

  const { t } = useTranslation({
    en: {
      acceptance: 'Acceptance',
      acceptanceTemplate: 'Case Acceptance',
      close: 'Close',
      discard: 'Discard',
      discardErrorTitle: 'Could not discard suggestion',
      discardErrorDescription: 'Please try again.',
      edited: 'Edited',
      rejectionTemplate: 'Case Rejection',
      reviewEmail: 'Review email',
      sendEmail: 'Send email',
      addDocument: 'Add document',
      aiChat: 'AI Chat',
      aiWidgetEmailTitle: 'I have prepared an email for {clientName}!',
      aiWidgetEmailTitleFallback: 'I have prepared an email for you!',
      aiWidgetEmailDescription:
        'For this case, I have prepared an email using the {templateName} template.{attachments}',
      aiWidgetEmailDescriptionFallback:
        'I have prepared an email from a template for this case, including relevant attachments.',
      aiWidgetAttachedItems: ' Attached are the {items}.',
      aiWidgetQuestionnaire: '{name} questionnaire',
      aiWidgetPdfForm: 'PDF form {name}',
      aiWidgetItemJoiner: ' and ',
      aiWidgetReviewTitle: 'Email draft opened',
      aiWidgetReviewDescription:
        'Please review the prepared text and any attachments, and make adjustments as needed.',
      aiWidgetSuccessTitle: 'AI assistant has sent the message',
      aiWidgetSuccessDescription: 'You can find the message history below the emails.',
      allDocumentsCompleted: 'Great, all requested documents have been completed.',
      API: 'API',
      APPOINTMENT: 'Appointment',
      appointmentDetails: 'Appointment Details',
      appointments: 'Appointments',
      bookingLinkSent: 'Booking link sent',
      cancel: 'Cancel',
      cancelAppointment: 'Cancel',
      cancelledAppointment: 'Cancelled',
      confirmAppointment: 'Confirm',
      caseWorker: 'Case Worker',
      CHAT_BOT: 'Chat Bot',
      city: 'City',
      clerk: 'Clerk',
      noClerkAssigned: 'Not assigned',
      client: 'Client',
      closeQuestionnaire: 'Close Questionnaire',
      copyLink: 'Copy Link',
      completeRequest: 'Complete Request',
      completed: 'COMPLETED',
      confirmedAppointment: 'Confirmed',
      conflictOfInterest: 'Conflict of Interest',
      contactPerson: 'Contact Person',
      contactPersonFirstName: 'Contact Person First Name',
      contactPersonLastName: 'Contact Person Last Name',
      contactPersonTitle: 'Contact Person Title',
      createAppointment: 'Create Appointment',
      createCase: 'Create Case',
      createFile: 'Create File',
      createKleosCase: 'Create Kleos Case',
      createLinkToDocumentTemplate: 'Create link to document template',
      createLinkToQuestionnaire: 'Create link to questionnaire',
      copyLinkToClientPortal: 'Copy link to client portal',
      createLinkQuestionnaireDescription:
        'Share questionnaires with external people to collect additional information that will be attached to this inquiry. Warning: Do not reuse these links beyond this inquiry. To create reusable public links, use Settings.',
      createLinkDocumentTemplateDescription:
        'Share document templates with external people to collect filled-out forms that will be attached to this inquiry. Warning: Do not reuse these links beyond this inquiry. To create reusable public links, use Settings.',
      created: 'Created',
      date: 'Date',
      delete: 'Delete',
      deleteRequest: 'Delete Request',
      deleteRequestConfirmation: 'Are you sure you want to delete this request?',
      deleteRequestWarning: 'This action cannot be undone.',
      deleted: 'DELETED',
      documents: 'Documents',
      documentTemplate: 'Document Template',
      pdfFormTemplate: 'PDF Form Template',
      documentUploaded: 'Document Uploaded',
      documentUploadedSuccessfully: 'The document was successfully uploaded.',
      documentsAndQuestionnaires: 'Document Templates & Questionnaires',
      download: 'Download',
      edit: 'Edit',
      downloadAllDocuments: 'Download All Documents',
      email: 'Email',
      EMAIL: 'Email',
      entityType: 'Entity Type',
      emailContent: 'Email content...',
      emailHistory: 'Email History',
      entities: 'Entities',
      entitiesOpenedInNewTab: 'Participant details opened in a new tab.',
      exportRAMICROFile: 'Export RA-MICRO File',
      firstName: 'First Name',
      general: 'General',
      lawSpecificData: 'Law-Specific Data',
      houseNumber: 'House',
      inPerson: 'In Person',
      inProgress: 'IN PROGRESS',
      inquiry: 'Inquiry',
      institution: 'Institution',
      institutionName: 'Institution Name',
      internalNotes: 'Internal Notes',
      lastName: 'Last Name',
      lawField: 'Law field',
      lawTopic: 'Law topic',
      legalArea: 'Legal Area',
      legalForm: 'Legal Form',
      legalInsurance: 'Legal Insurance',
      legalInsuranceName: 'Legal Insurance Name',
      legalInsuranceNumber: 'Legal Insurance Number',
      legalPerson: 'Legal person',
      location: 'Location',
      MANUAL: 'Manual',
      name: 'Name',
      naturalPerson: 'Natural person',
      new: 'NEW',
      noAppointments: 'No appointments available',
      noAttachments: 'No attachments added yet.',
      noDataAvailable: 'No data available',
      noDocumentsReceived: 'No documents have been received for this request yet.',
      noEmailHistory: 'No email history available.',
      noRequestedDocuments: 'No documents have been requested yet.',
      noResultsFound: 'No results found',
      noService: 'No Service',
      notesPlaceholder: 'Add internal notes about this case...',
      oclock: '',
      online: 'Online-Appointment',
      opponent: 'Opponent',
      pending: 'Pending',
      phone: 'Phone',
      PHONE: 'Phone',
      postalCode: 'Postal Code',
      preview: 'Preview',
      QUESTIONNAIRE: 'Questionnaire',
      questionnaire: 'Questionnaire',
      questionnaireDescription: 'Select one or more questionnaires',
      questionnaireSubmission: 'Questionnaire Submission',
      questionnaireTitle: 'Questionnaire',
      receivedDocuments: 'Received Documents',
      rejected: 'REJECTED',
      rejectRequest: 'Reject Request',
      rejection: 'Rejection',
      reopenRequest: 'Set Request to "Open"',
      requestedDocuments: 'Requested Documents',
      enableEmailReminder: 'Email Reminders',
      enableEmailReminderTitle: 'Enable email reminders',
      enableEmailReminderDescription:
        'When Email Reminders are enabled, this toggle is enabled by default for all cases, you can disable it for this specific case.',
      requestFrom: 'Request from',
      requestInProgress: 'Request in Progress',
      searchAppointments: 'Search appointments...',
      searchQuestionnaire: 'Search questionnaire...',
      searchTemplate: 'Search template',
      selectTemplate: 'Select Template',
      selectTemplateDescription: 'Choose an email template from the list',
      send: 'Send',
      service: 'Service',
      sortBy: 'Sort by',
      status: 'Status',
      specificTitle: 'Specific Title',
      street: 'Street',
      subject: 'Subject',
      summarizeWithAI: 'Summarize inquiry with AI',
      summaryOfInquiry: 'Summary of Inquiry',
      telephoneAppointment: 'Telephone Appointment',
      tentativeAppointment: 'Tentative',
      time: 'Time',
      title: 'Title',
      type: 'Channel',
      unknown: 'Unknown',
      uploadDocuments: 'Upload Documents',
      uploadDocumentsDescription: 'Please upload your documents here.',
      viewDetails: 'View Details',
      error: 'Error',
      failedToUpdateEmailReminder: 'Failed to update the email reminder setting',
      failedToSyncEntityData: 'Failed to sync entity data. Please try again.',
      documentInfoNotFound: 'Document information not found',
      questionnaireInfoNotFound: 'Questionnaire information not found',
      clientPortalLinkNotAvailable: 'Client portal link is not available for this case',
      documentLinkNotAvailable:
        'Link not available. Please create a new link via "Create link to document template".',
      unsupportedDocumentType: 'This document type is not supported',
      linkCopyFailed: 'Link could not be copied',
      deliveryEventSent: 'Sent',
      deliveryEventDelivered: 'Delivered',
      deliveryEventOpened: 'Opened',
      deliveryEventClicked: 'Clicked',
      deliveryEventHardBounced: 'Not Deliverable',
      deliveryEventSoftBounced: 'Not Deliverable',
      deliveryEventBlocked: 'Not Deliverable',
      deliveryEventSpam: 'Spam',
      deliveryEventUnsubscribed: 'Unsubscribed',
      deliveryEventUnknown: 'Unknown',
      deliveryEventPending: 'Checking delivery...',
      warning: 'Warning',
      noClientEmailWarning:
        'No client email address is set. Please add a client email before sending.',
      noClientInfoWarning:
        'Please add client email and at least first name or last name before creating an appointment.',
      calendarNotSynced: 'Calendar not synced',
      calendarNotSyncedWarning:
        'This appointment was not synced to the calendar. Please add it manually.',
      rejectEmailWarning: 'Note: Sending this email will automatically reject the case.',
      requiredFieldsNotFilled: 'Please fill all required fields in PDF forms.',
      signaturesNotFilled: 'Please fill all signature fields in PDF forms.',
      validationError: 'validation error',
      validationErrors: 'validation errors',
      aiSuggestedTemplate: 'AI suggested template',
      aiPreloadedAttachments: 'AI pre-loaded attachments',
      aiSuggestedAction: 'Did the AI select the right template?',
      aiLabel: 'AI',
      aiSuggestsTemplate: 'The AI suggests using the template "{templateName}".',
      switchToAiSuggestion: 'Switch',
      negativeFeedback: 'What went wrong?',
      selectReasonPlaceholder: 'Select a reason...',
      instructionsNotFollowed: 'Instructions not followed',
      payloadNotCorrect: 'Suggestion not correct',
      actionNotNeeded: 'Action not needed',
      other: 'Other',
      additionalComments: 'Additional comments (optional)',
      submitFeedback: 'Submit feedback',
      feedbackTitle: 'Why was the AI response not helpful?',
      feedbackDescription: 'Select a reason or briefly describe what did not fit.',
      feedbackReasoningLink: 'Show AI reasoning',
      feedbackSelectPlaceholder: 'Select reason',
      feedbackTextareaPlaceholder: 'Description',
      feedbackSubmitLabel: 'Submit feedback',
      feedbackCancelLabel: 'Cancel',
      feedbackSuccessTitle: 'Feedback sent',
      feedbackSuccessDescription: 'Thank you very much! Your feedback helps us to improve the AI.',
      feedbackErrorTitle: 'Feedback could not be submitted',
      feedbackErrorDescription: 'Please try again.'
    },
    de: {
      acceptance: 'Aufnahme',
      acceptanceTemplate: 'Mandatsaufnahme',
      close: 'Schließen',
      discard: 'Verwerfen',
      discardErrorTitle: 'Vorschlag konnte nicht verworfen werden',
      discardErrorDescription: 'Bitte versuchen Sie es erneut.',
      edited: 'Bearbeitet',
      rejectionTemplate: 'Mandatsablehnung',
      reviewEmail: 'E-Mail prüfen',
      sendEmail: 'E-Mail senden',
      addDocument: 'Dokumente hinzufügen',
      aiChat: 'KI Chat',
      aiWidgetEmailTitle: 'Ich habe Ihnen eine E-Mail an {clientName} vorbereitet!',
      aiWidgetEmailTitleFallback: 'Ich habe Ihnen eine E-Mail vorbereitet!',
      aiWidgetEmailDescription:
        'Für diesen Fall habe ich eine E-Mail aus der Vorlage {templateName} vorbereitet.{attachments}',
      aiWidgetEmailDescriptionFallback:
        'Für diesen Fall habe ich eine E-Mail aus einer Vorlage vorbereitet, einschließlich relevanter Anhänge.',
      aiWidgetAttachedItems: ' Angehängt sind {items}.',
      aiWidgetQuestionnaire: 'der {name}-Fragebogen',
      aiWidgetPdfForm: 'das PDF-Formular {name}',
      aiWidgetItemJoiner: ' sowie ',
      aiWidgetReviewTitle: 'E-Mail-Entwurf geöffnet',
      aiWidgetReviewDescription:
        'Bitte prüfen Sie den vorbereiteten Text sowie mögliche Anhänge und nehmen Sie bei Bedarf Anpassungen vor.',
      aiWidgetSuccessTitle: 'KI-Assistent hat die Nachricht gesendet',
      aiWidgetSuccessDescription: 'Sie finden den Verlauf Ihrer Nachrichten unterhalb der E-Mails.',
      allDocumentsCompleted: 'Prima, alle angeforderten Dokumente wurden bereits ausgefüllt.',
      API: 'API',
      APPOINTMENT: 'Terminbuchung',
      appointmentDetails: 'Termindetails',
      appointments: 'Termine',
      bookingLinkSent: 'Buchungslink gesendet',
      cancel: 'Abbrechen',
      cancelAppointment: 'Absagen',
      cancelledAppointment: 'Abgesagt',
      confirmAppointment: 'Bestätigen',
      caseWorker: 'Sachbearbeiter',
      CHAT_BOT: 'Chat Bot',
      city: 'Ort',
      clerk: 'Sachbearbeiter',
      noClerkAssigned: 'Nicht zugewiesen',
      client: 'Mandant',
      closeQuestionnaire: 'Fragebogen abschließen',
      copyLink: 'Link kopieren',
      completeRequest: 'Anfrage abschließen',
      completed: 'ABGESCHLOSSEN',
      confirmedAppointment: 'Bestätigt',
      conflictOfInterest: 'Interessenkollision',
      contactPerson: 'Kontaktperson',
      contactPersonFirstName: 'Kontaktperson Vorname',
      contactPersonLastName: 'Kontaktperson Nachname',
      contactPersonTitle: 'Kontaktperson Titel',
      createAppointment: 'Termin erstellen',
      createCase: 'Akte anlegen',
      createFile: 'Akte anlegen',
      createKleosCase: 'Kleos Akte anlegen',
      createLinkToDocumentTemplate: 'Link zu Dokumentenvorlage erzeugen',
      createLinkToQuestionnaire: 'Link zu Fragebogen erzeugen',
      copyLinkToClientPortal: 'Link zum Mandantenportal kopieren',
      createLinkQuestionnaireDescription:
        'Teilen Sie Fragebögen mit externen Personen, um zusätzliche Informationen zu sammeln, die dieser Anfrage beigefügt werden. Verwenden Sie diese Links nicht über diese Anfrage hinaus erneut. Um wiederverwendbare öffentliche Links zu erstellen, nutzen Sie die Einstellungen.',
      createLinkDocumentTemplateDescription:
        'Teilen Sie Dokumentenvorlagen mit externen Personen, um ausgefüllte Dokumente zu erhalten, die dieser Anfrage beigefügt werden. Verwenden Sie diese Links nicht über diese Anfrage hinaus erneut. Um wiederverwendbare öffentliche Links zu erstellen, nutzen Sie die Einstellungen.',
      created: 'Erstellt',
      date: 'Datum',
      delete: 'Löschen',
      deleteRequest: 'Anfrage löschen',
      deleteRequestConfirmation: 'Sind Sie sicher, dass Sie diese Anfrage löschen möchten?',
      deleteRequestWarning: 'Dieser Vorgang kann nicht rückgängig gemacht werden.',
      deleted: 'GELÖSCHT',
      documents: 'Dokumente',
      documentTemplate: 'Dokumentenvorlage',
      pdfFormTemplate: 'PDF-Formularvorlage',
      documentUploaded: 'Dokument hochgeladen',
      documentUploadedSuccessfully: 'Das Dokument wurde erfolgreich hochgeladen.',
      documentsAndQuestionnaires: 'Dokumentenvorlagen & Fragebögen',
      download: 'Herunterladen',
      edit: 'Bearbeiten',
      downloadAllDocuments: 'Alle Dokumente Herunterladen',
      email: 'E-Mail',
      EMAIL: 'E-Mail',
      entityType: 'Rechtliche Person',
      emailContent: 'E-Mail-Inhalt...',
      emailHistory: 'E-Mail Verlauf',
      entities: 'Beteiligte',
      entitiesOpenedInNewTab: 'Beteiligtendetails wurden in einem neuen Tab geöffnet.',
      exportRAMICROFile: 'RA-MICRO Datei exportieren',
      firstName: 'Vorname',
      general: 'Allgemein',
      lawSpecificData: 'Rechtsspezifische Daten',
      houseNumber: 'Hausnummer',
      inPerson: 'Vor Ort',
      inProgress: 'IN BEARBEITUNG',
      inquiry: 'Anfrage',
      institution: 'Institution',
      institutionName: 'Institutionsname',
      internalNotes: 'Interne Notizen',
      lastName: 'Nachname',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      legalArea: 'Rechtsgebiet',
      legalForm: 'Rechtsform',
      legalInsurance: 'Rechtsschutzversicherung',
      legalInsuranceName: 'Versicherungsgesellschaft',
      legalInsuranceNumber: 'Versicherungsnummer',
      legalPerson: 'Rechtliche Person',
      location: 'Ort',
      MANUAL: 'Manuell',
      name: 'Name',
      naturalPerson: 'Natürliche Person',
      new: 'NEU',
      noAppointments: 'Keine Termine vorhanden',
      noAttachments: 'Noch keine Anhänge hinzugefügt.',
      noDataAvailable: 'Keine Daten verfügbar',
      noDocumentsReceived: 'Sie haben bisher zu dieser Anfrage noch keine Dokumente erhalten.',
      noEmailHistory: 'Kein E-Mail-Verlauf verfügbar.',
      noRequestedDocuments: 'Es wurden bisher keine Dokumente angefordert.',
      noResultsFound: 'Keine Ergebnisse gefunden',
      noService: 'Kein Service',
      notesPlaceholder: 'Interne Notizen zu diesem Fall hinzufügen...',
      oclock: 'Uhr',
      online: 'Online-Termin',
      opponent: 'Gegner',
      pending: 'Ausstehend',
      phone: 'Telefon',
      PHONE: 'Telefon',
      postalCode: 'PLZ',
      preview: 'Ansehen',
      QUESTIONNAIRE: 'Fragebogen',
      questionnaire: 'Fragebogen',
      questionnaireDescription: 'Wählen Sie einen oder mehrere Fragebögen aus',
      questionnaireSubmission: 'Fragebogen Einreichung',
      questionnaireTitle: 'Fragebogen',
      receivedDocuments: 'Erhaltene Dokumente',
      rejected: 'ABGELEHNT',
      rejectRequest: 'Anfrage ablehnen',
      rejection: 'Ablehnung',
      reopenRequest: 'Anfrage auf "offen" setzen',
      requestedDocuments: 'Angeforderte Dokumente',
      enableEmailReminder: 'E-Mail Erinnerungen für Mandant',
      enableEmailReminderTitle: 'E-Mail Erinnerungen aktivieren',
      enableEmailReminderDescription:
        'Schalten Sie hier E-Mail Erinnerungen für diese Anfrage ein oder aus.',
      requestFrom: 'Anfrage von',
      requestInProgress: 'Anfrage in Bearbeitung',
      searchAppointments: 'Termine durchsuchen...',
      searchQuestionnaire: 'Fragebogen suchen...',
      searchTemplate: 'Vorlage suchen',
      selectTemplate: 'Vorlage auswählen',
      selectTemplateDescription: 'Wählen Sie eine E-Mail Vorlage aus der Liste',
      send: 'Senden',
      service: 'Service',
      sortBy: 'Sortieren nach',
      specificTitle: 'Titel',
      status: 'Status',
      street: 'Straße',
      subject: 'Betreff',
      summarizeWithAI: 'Anfrage mit KI zusammenfassen',
      summaryOfInquiry: 'Zusammenfassung der Anfrage',
      telephoneAppointment: 'Telefon-Termin',
      tentativeAppointment: 'Unbestätigt',
      time: 'Zeit',
      title: 'Anrede',
      type: 'Kanal',
      unknown: 'Unbekannt',
      uploadDocuments: 'Dokumente hochladen',
      uploadDocumentsDescription: 'Dokumente hier hochladen bitte.',
      viewDetails: 'Details anzeigen',
      error: 'Fehler',
      failedToUpdateEmailReminder: 'Aktualisierung der E-Mail-Erinnerungsfunktion fehlgeschlagen',
      failedToSyncEntityData:
        'Fehler beim Synchronisieren der Daten. Bitte versuchen Sie es erneut.',
      documentInfoNotFound: 'Dokumentinformationen nicht gefunden',
      questionnaireInfoNotFound: 'Fragebogeninformationen nicht gefunden',
      clientPortalLinkNotAvailable: 'Link zum Mandantenportal ist für diesen Fall nicht verfügbar',
      documentLinkNotAvailable:
        'Link nicht verfügbar. Bitte erstellen Sie einen neuen Link über "Link zu Dokumentenvorlage erzeugen".',
      unsupportedDocumentType: 'Dieser Dokumenttyp wird nicht unterstützt',
      linkCopyFailed: 'Link konnte nicht kopiert werden',
      deliveryEventSent: 'Gesendet',
      deliveryEventDelivered: 'Zugestellt',
      deliveryEventOpened: 'Geöffnet',
      deliveryEventClicked: 'Angeklickt',
      deliveryEventHardBounced: 'Nicht zustellbar',
      deliveryEventSoftBounced: 'Nicht zustellbar',
      deliveryEventBlocked: 'Nicht zustellbar',
      deliveryEventSpam: 'Als Spam markiert',
      deliveryEventUnsubscribed: 'Abgemeldet',
      deliveryEventUnknown: 'Unbekannt',
      deliveryEventPending: 'Prüfe Zustellung',
      warning: 'Warnung',
      noClientEmailWarning:
        'Keine E-Mail-Adresse des Mandanten hinterlegt. Bitte fügen Sie eine E-Mail-Adresse hinzu, bevor Sie eine E-Mail senden.',
      noClientInfoWarning:
        'Bitte fügen Sie E-Mail-Adresse und mindestens Vorname oder Nachname des Mandanten hinzu, bevor Sie einen Termin erstellen.',
      calendarNotSynced: 'Kalender nicht synchronisiert',
      calendarNotSyncedWarning:
        'Dieser Termin wurde nicht mit dem Kalender synchronisiert. Bitte fügen Sie ihn manuell hinzu.',
      rejectEmailWarning:
        'Hinweis: Mit dem Versand dieser E-Mail wird der Fall automatisch abgelehnt.',
      requiredFieldsNotFilled: 'Bitte alle Pflichtfelder im PDF-Formular ausfüllen.',
      signaturesNotFilled: 'Bitte alle Unterschriftsfelder im PDF-Formular ausfüllen.',
      validationError: 'Validierungsfehler',
      validationErrors: 'Validierungsfehler',
      aiSuggestedTemplate: 'KI vorgeschlagene Vorlage',
      aiPreloadedAttachments: 'KI vorausgewählte Anhänge',
      aiSuggestedAction: 'Hat die KI die richtige Vorlage ausgewählt?',
      aiLabel: 'KI',
      aiSuggestsTemplate: 'Die KI schlägt vor, die Vorlage "{templateName}" zu verwenden.',
      switchToAiSuggestion: 'Wechseln',
      negativeFeedback: 'Was ist schiefgelaufen?',
      selectReasonPlaceholder: 'Grund auswählen...',
      instructionsNotFollowed: 'Anweisungen nicht befolgt',
      payloadNotCorrect: 'Vorschlag nicht korrekt',
      actionNotNeeded: 'Aktion nicht benötigt',
      other: 'Sonstiges',
      additionalComments: 'Zusätzliche Kommentare (optional)',
      submitFeedback: 'Feedback senden',
      feedbackTitle: 'Warum war die Antwort der KI nicht hilfreich?',
      feedbackDescription:
        'Wählen Sie einen Grund aus oder beschreiben Sie kurz, was nicht gepasst hat.',
      feedbackReasoningLink: 'KI-Überlegungen anzeigen',
      feedbackSelectPlaceholder: 'Grund auswählen',
      feedbackTextareaPlaceholder: 'Beschreibung',
      feedbackSubmitLabel: 'Feedback senden',
      feedbackCancelLabel: 'Abbrechen',
      feedbackSuccessTitle: 'Feedback gesendet',
      feedbackSuccessDescription: 'Vielen Dank! Ihr Feedback hilft uns, die KI zu verbessern.',
      feedbackErrorTitle: 'Feedback konnte nicht gesendet werden',
      feedbackErrorDescription: 'Bitte versuchen Sie es erneut.'
    },
    fr: {
      acceptance: 'Acceptation',
      acceptanceTemplate: 'Acceptation du mandat',
      close: 'Fermer',
      discard: 'Rejeter',
      discardErrorTitle: 'Impossible de rejeter la suggestion',
      discardErrorDescription: 'Veuillez réessayer.',
      edited: 'Modifié',
      rejectionTemplate: 'Refus du mandat',
      reviewEmail: "Vérifier l'e-mail",
      sendEmail: "Envoyer l'e-mail",
      addDocument: 'Ajouter un document',
      aiChat: 'Chat IA',
      aiWidgetEmailTitle: "J'ai préparé un e-mail pour {clientName} !",
      aiWidgetEmailTitleFallback: "J'ai préparé un e-mail pour vous !",
      aiWidgetEmailDescription:
        "Pour ce dossier, j'ai préparé un e-mail à partir du modèle {templateName}.{attachments}",
      aiWidgetEmailDescriptionFallback:
        "Pour ce dossier, j'ai préparé un e-mail à partir d'un modèle, avec les pièces jointes pertinentes.",
      aiWidgetAttachedItems: ' En pièces jointes : {items}.',
      aiWidgetQuestionnaire: 'le questionnaire {name}',
      aiWidgetPdfForm: 'le formulaire PDF {name}',
      aiWidgetItemJoiner: ' et ',
      aiWidgetReviewTitle: "Brouillon d'e-mail ouvert",
      aiWidgetReviewDescription:
        'Veuillez vérifier le texte préparé ainsi que les pièces jointes éventuelles et apporter des modifications si nécessaire.',
      aiWidgetSuccessTitle: "L'assistant IA a envoyé le message",
      aiWidgetSuccessDescription: "Vous trouverez l'historique de vos messages sous les e-mails.",
      allDocumentsCompleted: 'Parfait, tous les documents demandés ont été complétés.',
      API: 'API',
      APPOINTMENT: 'Rendez-vous',
      appointmentDetails: 'Détails du rendez-vous',
      appointments: 'Rendez-vous',
      bookingLinkSent: 'Lien de réservation envoyé',
      cancel: 'Annuler',
      cancelAppointment: 'Annuler',
      cancelledAppointment: 'Annulé',
      confirmAppointment: 'Confirmer',
      caseWorker: 'Responsable du dossier',
      CHAT_BOT: 'Chat Bot',
      city: 'Ville',
      clerk: 'Responsable',
      noClerkAssigned: 'Non attribué',
      client: 'Client',
      closeQuestionnaire: 'Clôturer le questionnaire',
      copyLink: 'Copier le lien',
      completeRequest: 'Clôturer la demande',
      completed: 'TERMINÉ',
      confirmedAppointment: 'Confirmé',
      conflictOfInterest: "Conflit d'intérêts",
      contactPerson: 'Personne de contact',
      contactPersonFirstName: 'Prénom de la personne de contact',
      contactPersonLastName: 'Nom de la personne de contact',
      contactPersonTitle: 'Titre de la personne de contact',
      createAppointment: 'Créer un rendez-vous',
      createCase: 'Créer un dossier',
      createFile: 'Créer un dossier',
      createKleosCase: 'Créer un dossier Kleos',
      createLinkToDocumentTemplate: 'Créer un lien vers un modèle de document',
      createLinkToQuestionnaire: 'Créer un lien vers un questionnaire',
      copyLinkToClientPortal: 'Copier le lien vers le portail client',
      createLinkQuestionnaireDescription:
        'Partagez des questionnaires avec des personnes externes pour recueillir des informations supplémentaires qui seront jointes à cette demande. Attention : ne réutilisez pas ces liens au-delà de cette demande. Pour créer des liens publics réutilisables, utilisez les Paramètres.',
      createLinkDocumentTemplateDescription:
        'Partagez des modèles de documents avec des personnes externes pour recueillir des formulaires remplis qui seront joints à cette demande. Attention : ne réutilisez pas ces liens au-delà de cette demande. Pour créer des liens publics réutilisables, utilisez les Paramètres.',
      created: 'Créé',
      date: 'Date',
      delete: 'Supprimer',
      deleteRequest: 'Supprimer la demande',
      deleteRequestConfirmation: 'Êtes-vous sûr de vouloir supprimer cette demande ?',
      deleteRequestWarning: 'Cette action est irréversible.',
      deleted: 'SUPPRIMÉ',
      documents: 'Documents',
      documentTemplate: 'Modèle de document',
      pdfFormTemplate: 'Modèle de formulaire PDF',
      documentUploaded: 'Document téléversé',
      documentUploadedSuccessfully: 'Le document a été téléversé avec succès.',
      documentsAndQuestionnaires: 'Modèles de documents & Questionnaires',
      download: 'Télécharger',
      edit: 'Modifier',
      downloadAllDocuments: 'Télécharger tous les documents',
      email: 'E-mail',
      EMAIL: 'E-mail',
      entityType: "Type d'entité",
      emailContent: "Contenu de l'e-mail...",
      emailHistory: 'Historique des e-mails',
      entities: 'Entités',
      entitiesOpenedInNewTab: 'Les détails des participants ont été ouverts dans un nouvel onglet.',
      exportRAMICROFile: 'Exporter le fichier RA-MICRO',
      firstName: 'Prénom',
      general: 'Général',
      lawSpecificData: 'Données juridiques spécifiques',
      houseNumber: 'Numéro',
      inPerson: 'En personne',
      inProgress: 'EN COURS',
      inquiry: 'Demande',
      institution: 'Institution',
      institutionName: "Nom de l'institution",
      internalNotes: 'Notes internes',
      lastName: 'Nom',
      lawField: 'Domaine juridique',
      lawTopic: 'Sujet juridique',
      legalArea: 'Domaine juridique',
      legalForm: 'Forme juridique',
      legalInsurance: 'Assurance protection juridique',
      legalInsuranceName: "Nom de l'assurance protection juridique",
      legalInsuranceNumber: "Numéro d'assurance protection juridique",
      legalPerson: 'Personne morale',
      location: 'Lieu',
      MANUAL: 'Manuel',
      name: 'Nom',
      naturalPerson: 'Personne physique',
      new: 'NOUVEAU',
      noAppointments: 'Aucun rendez-vous disponible',
      noAttachments: 'Aucune pièce jointe ajoutée pour le moment.',
      noDataAvailable: 'Aucune donnée disponible',
      noDocumentsReceived: "Aucun document n'a encore été reçu pour cette demande.",
      noEmailHistory: "Aucun historique d'e-mails disponible.",
      noRequestedDocuments: "Aucun document n'a encore été demandé.",
      noResultsFound: 'Aucun résultat trouvé',
      noService: 'Aucun service',
      notesPlaceholder: 'Ajouter des notes internes sur ce dossier...',
      oclock: '',
      online: 'Rendez-vous en ligne',
      opponent: 'Partie adverse',
      pending: 'En attente',
      phone: 'Téléphone',
      PHONE: 'Téléphone',
      postalCode: 'Code postal',
      preview: 'Aperçu',
      QUESTIONNAIRE: 'Questionnaire',
      questionnaire: 'Questionnaire',
      questionnaireDescription: 'Sélectionnez un ou plusieurs questionnaires',
      questionnaireSubmission: 'Soumission du questionnaire',
      questionnaireTitle: 'Questionnaire',
      receivedDocuments: 'Documents reçus',
      rejected: 'REFUSÉ',
      rejectRequest: 'Refuser la demande',
      rejection: 'Refus',
      reopenRequest: 'Remettre la demande en "ouverte"',
      requestedDocuments: 'Documents demandés',
      enableEmailReminder: 'Rappels par e-mail',
      enableEmailReminderTitle: 'Activer les rappels par e-mail',
      enableEmailReminderDescription:
        'Lorsque les rappels par e-mail sont activés, cette option est activée par défaut pour tous les dossiers. Vous pouvez la désactiver pour ce dossier spécifique.',
      requestFrom: 'Demande de',
      requestInProgress: 'Demande en cours',
      searchAppointments: 'Rechercher des rendez-vous...',
      searchQuestionnaire: 'Rechercher un questionnaire...',
      searchTemplate: 'Rechercher un modèle',
      selectTemplate: 'Sélectionner un modèle',
      selectTemplateDescription: "Choisissez un modèle d'e-mail dans la liste",
      send: 'Envoyer',
      service: 'Service',
      sortBy: 'Trier par',
      status: 'Statut',
      specificTitle: 'Titre spécifique',
      street: 'Rue',
      subject: 'Objet',
      summarizeWithAI: "Résumer la demande avec l'IA",
      summaryOfInquiry: 'Résumé de la demande',
      telephoneAppointment: 'Rendez-vous téléphonique',
      tentativeAppointment: 'Provisoire',
      time: 'Heure',
      title: 'Titre',
      type: 'Canal',
      unknown: 'Inconnu',
      uploadDocuments: 'Téléverser des documents',
      uploadDocumentsDescription: 'Veuillez téléverser vos documents ici.',
      viewDetails: 'Voir les détails',
      error: 'Erreur',
      failedToUpdateEmailReminder: 'Échec de la mise à jour du paramètre de rappel par e-mail',
      failedToSyncEntityData: 'Échec de la synchronisation des données. Veuillez réessayer.',
      documentInfoNotFound: 'Informations du document introuvables',
      questionnaireInfoNotFound: 'Informations du questionnaire introuvables',
      clientPortalLinkNotAvailable:
        "Le lien vers le portail client n'est pas disponible pour ce dossier",
      documentLinkNotAvailable:
        'Lien non disponible. Veuillez créer un nouveau lien via "Créer un lien vers un modèle de document".',
      unsupportedDocumentType: "Ce type de document n'est pas pris en charge",
      linkCopyFailed: "Le lien n'a pas pu être copié",
      deliveryEventSent: 'Envoyé',
      deliveryEventDelivered: 'Livré',
      deliveryEventOpened: 'Ouvert',
      deliveryEventClicked: 'Cliqué',
      deliveryEventHardBounced: 'Non livrable',
      deliveryEventSoftBounced: 'Non livrable',
      deliveryEventBlocked: 'Non livrable',
      deliveryEventSpam: 'Spam',
      deliveryEventUnsubscribed: 'Désabonné',
      deliveryEventUnknown: 'Inconnu',
      deliveryEventPending: 'Vérification de la livraison...',
      warning: 'Avertissement',
      noClientEmailWarning:
        "Aucune adresse e-mail du client n'est définie. Veuillez ajouter une adresse e-mail avant d'envoyer.",
      noClientInfoWarning:
        "Veuillez ajouter l'adresse e-mail et au moins le prénom ou le nom du client avant de créer un rendez-vous.",
      calendarNotSynced: 'Calendrier non synchronisé',
      calendarNotSyncedWarning:
        "Ce rendez-vous n'a pas été synchronisé avec le calendrier. Veuillez l'ajouter manuellement.",
      rejectEmailWarning: "Remarque : l'envoi de cet e-mail refusera automatiquement le dossier.",
      requiredFieldsNotFilled:
        'Veuillez remplir tous les champs obligatoires dans les formulaires PDF.',
      signaturesNotFilled:
        'Veuillez remplir tous les champs de signature dans les formulaires PDF.',
      validationError: 'erreur de validation',
      validationErrors: 'erreurs de validation',
      aiSuggestedTemplate: "Modèle suggéré par l'IA",
      aiPreloadedAttachments: "Pièces jointes présélectionnées par l'IA",
      aiSuggestedAction: "L'IA a-t-elle sélectionné le bon modèle ?",
      aiLabel: 'IA',
      aiSuggestsTemplate: 'L\'IA suggère d\'utiliser le modèle "{templateName}".',
      switchToAiSuggestion: 'Changer',
      negativeFeedback: "Qu'est-ce qui n'a pas fonctionné ?",
      selectReasonPlaceholder: 'Sélectionner une raison...',
      instructionsNotFollowed: 'Instructions non suivies',
      payloadNotCorrect: 'Suggestion incorrecte',
      actionNotNeeded: 'Action non nécessaire',
      other: 'Autre',
      additionalComments: 'Commentaires supplémentaires (facultatif)',
      submitFeedback: 'Soumettre le retour',
      feedbackTitle: "Pourquoi la réponse de l'IA n'était-elle pas utile ?",
      feedbackDescription:
        'Sélectionnez une raison ou décrivez brièvement ce qui ne convenait pas.',
      feedbackReasoningLink: "Afficher le raisonnement de l'IA",
      feedbackSelectPlaceholder: 'Sélectionner une raison',
      feedbackTextareaPlaceholder: 'Description',
      feedbackSubmitLabel: 'Envoyer le retour',
      feedbackCancelLabel: 'Annuler',
      feedbackSuccessTitle: 'Retour envoyé',
      feedbackSuccessDescription: "Merci beaucoup ! Vos retours nous aident à améliorer l'IA.",
      feedbackErrorTitle: "Le retour n'a pas pu être envoyé",
      feedbackErrorDescription: 'Veuillez réessayer.'
    }
  })

  const { toast } = useToast()
  const newPdfStore = useNewPdfStore()
  const signatureStore = useSignatures()

  // AI action rating composable
  const {
    actionRatings: aiActionRatings,
    isSubmittingRating: isSubmittingAiRating,
    ratingModalActionUuid: aiRatingModalActionUuid,
    ratingNegativeVariant: aiRatingNegativeVariant,
    ratingNegativeComment: aiRatingNegativeComment,
    initializeRatingsFromActions: initializeAiRatings,
    handlePositiveRating: handleAiPositiveRatingBase,
    openNegativeRatingModal: openAiNegativeRatingModal,
    closeNegativeRatingModal: closeAiNegativeRatingModal,
    submitNegativeRating: submitAiNegativeRatingBase
  } = useAgentActionRating()

  const caseStore = useCaseStore()
  const userStore = useUserStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  const { widgetAnalyticsOptions, trackEvent } = useAiAssistantCaseDetailMetrics(caseRecord)

  // Use separate appointments and documents from store (loaded via dedicated endpoints)
  const appointments = computed(() => caseStore.appointments)
  const caseDocuments = computed(() => caseStore.documents)
  const officeEditingFlag = useFeatureFlag(FlagEnum.OFFICE_DOCUMENT_EDITING)
  const officePreviewFlag = useFeatureFlag(FlagEnum.OFFICE_DOCUMENT_PREVIEWING)
  const multipleOpponentsFlag = useFeatureFlag(FlagEnum.MULTIPLE_OPPONENTS)
  const agenticWorkflowsFlag = useFeatureFlag(FlagEnum.AGENTIC_WORKFLOWS)
  const aiAssistantFlag = useFeatureFlag(FlagEnum.AI_ASSISTANT)
  const multipleOpponentsFlagChecked = ref(false)
  const {
    isOfficeFile: isOfficeFileUtil,
    isEditableDocument: isEditableDocumentUtil,
    openOfficeEditor
  } = useOfficeDocument()
  const { getThumbnailUrl } = useDocumentThumbnail()
  const isLoadingDocuments = ref(false)
  const isInitializing = ref(true)

  // Email functionality properties
  const emailSubject = ref('')
  const emailContent = ref('')
  const emailTemplates = ref<CustomerEmailTemplate[]>([])
  const selectedEmailTemplate = ref<CustomerEmailTemplate | null>(null)
  const selectedTemplateId = ref('')
  const emailAttachments = ref<AttachedDocument[]>([])
  const isSendingEmail = ref(false)
  const showPendingChangesDialog = ref(false)

  // Email history variables
  const emails = ref<CustomerEmail[]>([])
  const isLoadingEmails = ref(true)
  const pendingEmailUuids = ref<Set<string>>(new Set())
  const emailHistoryAccordionValue = ref('email-history')

  // Document selection state
  const showDocumentAttachDialog = ref(false)
  const showQuestionnaireAttachDialog = ref(false)
  const selectedDocument = ref<CustomerForm | CustomerQuestionnaire | null>(null)
  const user = computed(() => userStore.user as User)
  const nonEmptyLawSpecificMappers = computed(() => caseStore.nonEmptyLawSpecificMappers)
  const entities = computed(() => caseRecord.value?.data?.entities)
  const hasPendingProposedChanges = computed(() => {
    return caseRecord.value?.data?.entities?.some(
      (entityRelationship) => entityRelationship.entity.proposed_changes.length > 0
    )
  })

  /**
   * Converts FormDocumentProperties to UnifiedDocumentTemplate format
   * for the SelectUnifiedDocumentsDialog component
   */
  const initialSelectedUnifiedDocuments = computed<UnifiedDocumentTemplate[]>(() => {
    return emailAttachments.value
      .filter(
        (attachment) =>
          attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
          attachment.documentProperties
      )
      .map((pdfAttachment): UnifiedDocumentTemplate => {
        const props = pdfAttachment.documentProperties as FormDocumentProperties & {
          template_type?: string
          form_fields?: any[]
        }

        return {
          id: props.id,
          template_type: props.template_type || 'legacy_form',
          title: props.title,
          description: props.description,
          file: props.file || '',
          created_at: props.created_at,
          modified_at: props.modified_at,
          created_by: props.created_by,
          modified_by: props.modified_by,
          external_document_creation_token: '',
          form_fields: props.form_fields || []
        }
      })
  })

  /**
   * Extracts form field values from form_fields array for PDF forms
   * Used to restore field values when re-opening the dialog
   */
  const initialFieldValuesForDialog = computed<Map<string, Record<string, any>>>(() => {
    const fieldValuesMap = new Map<string, Record<string, any>>()

    emailAttachments.value
      .filter((attachment) => attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE)
      .forEach((attachment) => {
        const props = attachment.documentProperties as FormDocumentProperties & {
          template_type?: string
          form_fields?: any[]
        }
        if (props.template_type === 'pdf_form' && props.form_fields) {
          // Extract values from form_fields array
          const values: Record<string, any> = {}
          props.form_fields.forEach((field) => {
            if (field.uuid && field.value !== undefined) {
              values[field.uuid] = field.value
            }
          })
          fieldValuesMap.set(props.id, values)
        }
      })

    return fieldValuesMap
  })

  /**
   * Converts PDF template attachments to TForm format for SelectPdfFormsDialog
   * (used when feature flag is disabled)
   */
  const initialSelectedFormsForDialog = computed<TForm[]>(() => {
    return emailAttachments.value
      .filter((attachment) => attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE)
      .map((attachment) => {
        const props = attachment.documentProperties as FormDocumentProperties
        return {
          id: props.id,
          organisation: props.organisation,
          created_at: props.created_at,
          modified_at: props.modified_at,
          tag: props.tag,
          title: props.title,
          description: props.description,
          file: props.file,
          due_at: props.due_at,
          document_name: props.document_name
        } as TForm
      })
  })

  // Navigation logic
  const pages = computed(() => {
    return [
      {
        title: () => h('span', t('general')),
        route: ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(':uuid', caseRecord.value?.uuid ?? '')
      },
      {
        title: () =>
          h('span', { class: 'flex gap-2 items-center' }, [
            t('lawSpecificData'), // Text for the second page
            nonEmptyLawSpecificMappers.value.length > 0
              ? h('span', { class: 'badge-square' }, `${nonEmptyLawSpecificMappers.value.length}`)
              : null
          ]),
        route: ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(
          ':uuid',
          caseRecord.value?.uuid ?? ''
        )
      },
      {
        title: () =>
          h('span', { class: 'flex gap-2 items-center' }, [
            t('entities'),
            entities.value && entities.value?.length > 0
              ? h('span', { class: 'badge-square' }, `${entities.value.length}`)
              : null
          ]),
        route: ROUTES.APP.CASES.CASE_DETAIL.ENTITIES.replace(
          ':uuid',
          caseRecord.value?.uuid ?? ''
        ).replace('/:entityUuid?', '')
      },
      {
        title: () => h('span', t('aiChat')),
        route: ROUTES.APP.CASES.CASE_DETAIL.AI_CHAT.replace(':uuid', caseRecord.value?.uuid ?? '')
      }
    ]
  })

  const activePage = computed(() => {
    const currentPath = router.currentRoute.value.fullPath
    return pages.value.find((page) => {
      // For entities page, match both with and without entityUuid
      if (page.route.includes('entities')) {
        return currentPath.includes('entities')
      }
      // For law-specific-data page
      if (page.route.includes('law-specific-data')) {
        return currentPath.includes('law-specific-data')
      }
      // For chat page
      if (page.route.includes('chat')) {
        return currentPath.includes('chat')
      }
      // For general page
      return page.route === currentPath
    })
  })

  const getTabId = (route: string) => {
    if (route.includes('entities')) return 'entities'
    if (route.includes('law-specific-data')) return 'law-specific-data'
    if (route.includes('chat')) return 'ai-chat'
    return 'allgemein'
  }

  const selectedClerks = ref<(string | number)[]>([])

  // Law field and topics
  const { fetchLawFields, getLawFieldOptions, getLawTopicOptions } = useLawFieldStore()
  const selectedLawField = ref('')
  const selectedLawTopic = ref('')
  const lawFieldOptions = computed<Option[]>(() => {
    return getLawFieldOptions(user.value)
  })
  const lawTopicOptions = computed<Option[]>(() => {
    if (!selectedLawField.value) {
      return []
    }
    return getLawTopicOptions(selectedLawField.value)
  })

  const isAppointmentCreateModalOpen = ref(false)

  const hasClientInfo = computed(() => {
    const client = caseRecord.value?.data?.client
    if (!client) return false

    const email = client.email
    const firstName = client.first_name
    const lastName = client.last_name

    const hasEmail = isStringNotEmpty(email)
    const hasFirstName = isStringNotEmpty(firstName)
    const hasLastName = isStringNotEmpty(lastName)

    return hasEmail && (hasFirstName || hasLastName)
  })

  const isDocumentUploadModalOpen = ref(false)
  const showUnifiedTemplateModal = ref(false)
  const showQuestionnaireModal = ref(false)
  const unifiedTemplates = ref<any[]>([])

  const questionnaires = ref<any[]>([])
  const forms = ref<TForm[]>([])
  const receivedDocsSortBy = ref('name')
  const requestedDocsSortBy = ref('name')

  const loadingQuestionnaires = ref(false)
  const loadingUnifiedTemplates = ref(false)
  const isLoadingForms = ref(false)

  // Pagination state for questionnaires
  const questionnaireCurrentPage = ref(1)
  const questionnaireTotalCount = ref(0)
  const questionnairePageSize = 20
  const questionnaireSearchQuery = ref('')

  // PDF Viewer state
  const showPdfViewer = ref(false)
  const showPdfEditor = ref(false)
  const showPdfForms = ref(true)
  const currentPdfUrl = ref('')
  const currentPdfFilename = ref('')
  const currentDocumentUuid = ref('')

  const showOfficePreview = ref(false)
  const currentOfficeDocument = ref<PdfDocument | null>(null)
  const currentOfficeToken = ref<string | undefined>(undefined)

  // Questionnaire submission modal state
  const showQuestionnaireSubmissionModal = ref(false)
  const selectedQuestionnaireSubmission = ref<RequestedQuestionnaire | null>(null)

  // AI Summary state
  const inquirySummary = ref('')
  const isGeneratingSummary = ref(false)

  // Delivery status modal state
  const showDeliveryStatusModal = ref(false)
  const selectedEmailForDeliveryStatus = ref<CustomerEmail | null>(null)

  // AI Assistant Widget — orchestrates action queue, step machine, and per-type flows
  const aiWidget = useAiAssistantWidget({
    caseUuid: computed(() => caseRecord.value?.uuid),
    enabled: computed(() => isAiAssistantEnabled.value || isAgentActionWidgetEnabled.value),
    onActionsFetched: (actions) => initializeAiRatings(actions),
    ...widgetAnalyticsOptions
  })
  // Convenience aliases used in template and below
  const aiSuggestedAction = aiWidget.currentAction
  const isAiPreloaded = aiWidget.isAiPreloaded
  const hasPendingAgentActions = aiWidget.hasPendingActions

  // Register the email action flow strategy
  aiWidget.registerActionFlow(
    'SEND_CLIENT_TEMPLATE_EMAIL',
    createEmailActionFlow({
      onReview: async (action) => {
        if (action.payload) await applyAiSuggestedEmailAction(action.payload)
        const wasOpen = emailHistoryAccordionValue.value === 'email-history'
        if (!wasOpen) {
          emailHistoryAccordionValue.value = 'email-history'
          await nextTick()
        }
        await new Promise<void>((resolve) => {
          setTimeout(
            () => {
              scrollToEmailHistory()
              resolve()
            },
            wasOpen ? 0 : 300
          )
        })
      },
      onSend: () => handleSendEmail(false),
      formatQuestionnaire: (name) => t('aiWidgetQuestionnaire', { name }),
      formatPdfForm: (name) => t('aiWidgetPdfForm', { name })
    })
  )

  watchAiAssistantFloatingViewOpened(
    aiWidget.isOpen,
    () => aiWidget.hasPendingActions.value,
    caseRecord,
    trackEvent
  )

  /**
   * Wrapper for positive rating that also refreshes the AgentActionWidget.
   *
   * :param actionUuid: UUID of the action to rate
   */
  const handleAiPositiveRating = async (actionUuid: string) => {
    await handleAiPositiveRatingBase(actionUuid)
    agentActionWidgetRef.value?.refresh()
  }

  /**
   * Wrapper for negative rating submission that also refreshes the AgentActionWidget.
   */
  const submitAiNegativeRating = async () => {
    await submitAiNegativeRatingBase()
    agentActionWidgetRef.value?.refresh()
  }

  // Widget title — changes per step
  const aiWidgetTitle = computed(() => {
    if (aiWidget.step.value === 'review') return t('aiWidgetReviewTitle')
    if (aiWidget.step.value === 'success') return t('aiWidgetSuccessTitle')
    // Summary step
    const client = caseRecord.value?.data?.client
    const clientName = [client?.first_name?.[0], client?.last_name].filter(Boolean).join('. ')
    if (aiSuggestedAction.value && clientName) {
      return t('aiWidgetEmailTitle', { clientName: `\n${clientName}` })
    }
    return t('aiWidgetEmailTitleFallback')
  })

  // Widget description — changes per step and action type
  const aiWidgetDescription = computed(() => {
    if (aiWidget.step.value === 'review') return t('aiWidgetReviewDescription')
    if (aiWidget.step.value === 'success') return t('aiWidgetSuccessDescription')
    // Summary step — built from resolved summary parts
    const summary = aiWidget.summaryParts.value
    if (summary) {
      const attachments =
        summary.details.length > 0
          ? t('aiWidgetAttachedItems', { items: summary.details.join(t('aiWidgetItemJoiner')) })
          : ''
      return t('aiWidgetEmailDescription', { templateName: summary.label, attachments })
    }
    return t('aiWidgetEmailDescriptionFallback')
  })

  // Close handler — dismiss reasoning modal + close/dismiss widget
  const handleAiWidgetClose = () => {
    agentActionWidgetRef.value?.closeReasoningModal?.()
    if (['success', 'feedback', 'feedback-success'].includes(aiWidget.step.value)) {
      aiWidget.handleSuccessDismiss()
    } else {
      aiWidget.close()
    }
  }

  // Discard handler — wraps composable with toast on error
  const handleAiDiscard = async () => {
    agentActionWidgetRef.value?.closeReasoningModal?.()
    try {
      await aiWidget.handleDiscard()
    } catch {
      toast({
        title: t('discardErrorTitle'),
        description: t('discardErrorDescription'),
        variant: 'destructive'
      })
    }
  }

  // Thumbs up on success step
  const handleAiWidgetThumbsUp = async () => {
    const actionUuid = aiSuggestedAction.value?.uuid
    if (actionUuid) await handleAiPositiveRating(actionUuid)
  }

  // Thumbs down on success step — show inline feedback form
  const handleAiWidgetThumbsDown = () => {
    aiWidget.step.value = 'feedback'
  }

  // Negative feedback reason options — shared between widget dropdown and legacy modal
  const negativeFeedbackReasonOptions = computed(() => [
    { value: 'INSTRUCTIONS_NOT_FOLLOWED', label: t('instructionsNotFollowed') },
    { value: 'PAYLOAD_NOT_CORRECT', label: t('payloadNotCorrect') },
    { value: 'ACTION_NOT_NEEDED', label: t('actionNotNeeded') },
    { value: 'OTHER', label: t('other') }
  ])

  // Submit inline feedback from the widget
  const isSubmittingFeedback = ref(false)

  const handleAiWidgetFeedbackSubmit = async (payload: { reason: string; comment: string }) => {
    const actionUuid = aiSuggestedAction.value?.uuid
    if (!actionUuid || isSubmittingFeedback.value) return

    isSubmittingFeedback.value = true
    try {
      await AgentActionsService.agentActionsRateCreate({
        requestBody: {
          agent_action_uuid: actionUuid,
          rating: '0.0',
          rating_negative_variant: payload.reason as any,
          rating_negative_comment: payload.comment || undefined
        }
      })
      aiActionRatings.value[actionUuid] = 'negative'
      agentActionWidgetRef.value?.refresh()
      // Show feedback confirmation
      aiWidget.step.value = 'feedback-success'
    } catch (error) {
      console.error('Error submitting feedback:', error)
      toast({
        title: t('feedbackErrorTitle'),
        description: t('feedbackErrorDescription'),
        variant: 'destructive'
      })
    } finally {
      isSubmittingFeedback.value = false
    }
  }

  // Cancel feedback — in discard mode advance to next action, otherwise return to success step
  const handleAiWidgetFeedbackCancel = async () => {
    agentActionWidgetRef.value?.closeReasoningModal?.()
    if (aiWidget.discardMode.value) {
      await aiWidget.handleDiscardFeedbackCancel()
    } else {
      aiWidget.step.value = 'success'
    }
  }

  // Form refs
  const notesFormRef = ref()

  // Get questionnaire submissions for received documents
  const receivedDocuments = computed(() => {
    const receivedDocs = []

    // Add questionnaire submissions from caseDocuments as pdf
    // Add requested pdfs also from caseDocuments
    if (caseDocuments.value && Array.isArray(caseDocuments.value)) {
      const pdfDocuments = caseDocuments.value.filter(
        (doc) =>
          (doc.document_type === DocumentTypeEnum.DOKUMENTENVORLAGE &&
            doc.document_status === DocumentStatusEnum.RECEIVED) ||
          (doc.document_type === DocumentTypeEnum.FRAGEBOGEN &&
            doc.document_status === DocumentStatusEnum.RECEIVED) ||
          (doc.document_type === DocumentTypeEnum.KI_GENERIERTES_DOKUMENT &&
            doc.document_status === DocumentStatusEnum.COMPLETED) ||
          doc.document_type === DocumentTypeEnum.UPLOAD
      )
      receivedDocs.push(...pdfDocuments)
    }

    // Add completed PDF form submissions
    if (
      caseRecord.value?.pdf_form_submissions &&
      Array.isArray(caseRecord.value.pdf_form_submissions)
    ) {
      const completedSubmissions = caseRecord.value.pdf_form_submissions
        .filter((submission) => submission.submission_status === 'completed')
        .map((submission) => ({
          uuid: submission.uuid,
          title: submission.template_name || 'PDF Form',
          filename: `${submission.template_name || 'form'}.pdf`,
          created_at: submission.completed_at || submission.created_at,
          download: {
            url: submission.flattened_pdf || submission.processed_pdf,
            filename: `${submission.template_name || 'form'}.pdf`
          },
          images: submission.images || [],
          document_type: DocumentTypeIdEnum.PDF_FORM_SUBMISSION,
          document_status: 'completed'
        }))
      receivedDocs.push(...completedSubmissions)
    }

    // Sort the combined results
    const sorted = receivedDocs.sort((a, b) => {
      if (receivedDocsSortBy.value === 'name') {
        if (!a.title || !b.title) return 0
        return a.title.localeCompare(b.title)
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })

    return sorted
  })

  // Get form requests for requested documents
  const requestedDocuments = computed<RequestedDocuments[]>(() => {
    const requestedDocs: RequestedDocuments[] = []

    // Add questionnaire requests
    if (
      caseRecord.value?.questionnaire_submissions &&
      Array.isArray(caseRecord.value.questionnaire_submissions)
    ) {
      const submissions = caseRecord.value.questionnaire_submissions.filter(
        (submission) => !submission.closed
      )

      // Map submissions to the RequestedQuestionnaire type
      const questionnaireItems = submissions.map((submission) => ({
        ...submission,
        document_type: DocumentTypeEnum.FRAGEBOGEN,
        filename: submission.title || 'Fragebogen',
        created_at: submission.created_at,
        title: submission.title || 'Fragebogen'
      })) as RequestedQuestionnaire[]

      requestedDocs.push(...questionnaireItems)
    }

    // Add pdf requests
    if (caseDocuments.value && Array.isArray(caseDocuments.value)) {
      const pdfDocs = caseDocuments.value.filter(
        (document) =>
          document.document_type === DocumentTypeEnum.DOKUMENTENVORLAGE &&
          document.document_status === DocumentStatusEnum.REQUESTED
      ) as (PdfDocument & { upload_origin?: string })[]

      requestedDocs.push(...pdfDocs)
    }

    // Add in-progress PDF form submissions
    if (
      caseRecord.value?.pdf_form_submissions &&
      Array.isArray(caseRecord.value.pdf_form_submissions)
    ) {
      const inProgressSubmissions = caseRecord.value.pdf_form_submissions
        .filter((submission) => submission.submission_status === 'in_progress')
        .map((submission) => ({
          uuid: submission.uuid,
          title: submission.template_name || 'PDF Form',
          filename: `${submission.template_name || 'form'}.pdf`,
          created_at: submission.created_at,
          download: {
            url: submission.flattened_pdf || submission.processed_pdf,
            filename: `${submission.template_name || 'form'}.pdf`
          },
          images: submission.images || [],
          document_type: DocumentTypeIdEnum.PDF_FORM_SUBMISSION,
          document_status: 'in_progress'
        }))
      requestedDocs.push(...(inProgressSubmissions as any))
    }

    // Sort by date (newest first)
    return requestedDocs.sort((a, b) => {
      if (requestedDocsSortBy.value === 'name') {
        const aTitle = a.title || ''
        const bTitle = b.title || ''
        return aTitle.localeCompare(bTitle)
      }

      // Use safe date handling with fallbacks
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0
      return bDate - aDate
    })
  })

  const caseCreated = computed(() => {
    return caseRecord.value?.created_at
      ? new Date(caseRecord.value.created_at).toLocaleString('de-DE', {
          weekday: 'short',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric'
        })
      : ''
  })

  const caseType = computed(() => {
    if (!caseRecord.value?.type) return ''
    switch (caseRecord.value.type) {
      case 'API':
        return t('API')
      case 'CHAT BOT':
        return t('CHAT_BOT')
      case 'QUESTIONNAIRE':
        return t('QUESTIONNAIRE')
      case 'EMAIL':
        return t('EMAIL')
      case 'MANUAL':
        return t('MANUAL')
      case 'PHONE':
        return t('PHONE')
      case 'APPOINTMENT':
        return t('APPOINTMENT')
      default:
        return ''
    }
  })

  const clerkOptions = computed<Option[]>(() => {
    if (!caseRecord.value?.org?.users) return []
    return caseRecord.value.org.users
      .map((user) => ({
        value: user.uuid as string | number,
        title: `${user.first_name} ${user.last_name}`
      }))
      .sort((a, b) => a.title.localeCompare(b.title))
  })

  const hasLegalInsurance = computed({
    get() {
      return caseRecord.value?.data?.client?.legal_insurance ?? undefined
    },
    set(value) {
      if (caseRecord.value?.data?.client) {
        caseRecord.value.data.client.legal_insurance = value
      }
    }
  })

  const legalInsuranceName = computed({
    get() {
      return caseRecord.value?.data?.client?.legal_insurance_name ?? undefined
    },
    set(value) {
      if (caseRecord.value?.data?.client) {
        caseRecord.value.data.client.legal_insurance_name = value
      }
    }
  })

  const legalInsuranceNumber = computed({
    get() {
      return caseRecord.value?.data?.client?.legal_insurance_number ?? undefined
    },
    set(value) {
      if (caseRecord.value?.data?.client) {
        caseRecord.value.data.client.legal_insurance_number = value
      }
    }
  })

  const internalNotes = computed({
    get() {
      return caseRecord.value?.note ?? ''
    },
    set(value) {
      if (caseRecord.value) {
        caseRecord.value.note = value
      }
    }
  })

  const submitCaseUpdate = async (updates: Partial<PatchedCustomerCaseRequest>) => {
    if (!caseRecord.value?.uuid) return
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: updates
      })
    } catch (error) {
      console.error('Error saving case update:', error)
    }
  }

  const handleCaseInfoSave = (values: any) => {
    const updates: Partial<PatchedCustomerCaseRequest> = {}
    if (values.clerks !== undefined) {
      ;(updates as any).clerk_ids = values.clerks
    }

    if (values.lawField !== undefined || values.lawTopic !== undefined) {
      updates.data = { law: {} } as any

      if (values.lawField !== undefined) {
        ;(updates.data as any).law.field = values.lawField
      }
      if (values.lawTopic !== undefined) {
        const options = getLawTopicOptions(values.lawField)
        if (!options.find((option) => option.value === values.lawTopic)) {
          ;(updates.data as any).law.topic = ''
        } else {
          ;(updates.data as any).law.topic = values.lawTopic
        }
      }
    }

    submitCaseUpdate(updates)
  }

  const handleFieldValuesChanged = (fieldValues: Record<string, any>) => {
    if (!selectedDocument.value) return
    const docId = (selectedDocument.value as any).id || (selectedDocument.value as any).uuid
    const docState = getOrCreateDocumentState(docId)
    Object.entries(fieldValues).forEach(([key, value]) => {
      docState.fieldValues.set(key, value)
    })
    // Find the attachment in emailAttachments
    const attachmentIndex = emailAttachments.value.findIndex((attachment) => {
      if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
        const props = attachment.documentProperties as FormDocumentProperties & {
          template_type?: string
          form_fields?: any[]
        }
        return props.id === docId
      }
      return false
    })
    if (attachmentIndex !== -1) {
      const attachment = emailAttachments.value[attachmentIndex]
      const props = attachment.documentProperties as FormDocumentProperties & {
        template_type?: string
        form_fields?: any[]
        formFieldValues?: Record<string, any>
      }
      // Update form_fields with new values embedded
      if (props.form_fields) {
        props.form_fields = props.form_fields.map((field) => ({
          ...field,
          value: fieldValues[field.uuid] ?? field.value
        }))
      }
      // Update formFieldValues for next time the editor is opened
      props.formFieldValues = fieldValues
      // Update the attachment in the array
      emailAttachments.value[attachmentIndex] = {
        ...attachment,
        documentProperties: props
      }
      validatePdfFormsAttachments()
    }
  }

  const handleInsuranceInfoSave = (values: any) => {
    const updates: Partial<PatchedCustomerCaseRequest> = {}
    updates.data = { client: {} } as any
    if (values.has_legal_insurance !== undefined) {
      ;(updates.data as any).client.legal_insurance = values.has_legal_insurance
    }
    if (values.legal_insurance_name !== undefined) {
      ;(updates.data as any).client.legal_insurance_name = values.legal_insurance_name
    }
    if (values.legal_insurance_number !== undefined) {
      ;(updates.data as any).client.legal_insurance_number = values.legal_insurance_number
    }
    submitCaseUpdate(updates)
  }

  const handleNotesSave = (values: any) => {
    const updates: Partial<PatchedCustomerCaseRequest> = {}
    if (values.notes !== undefined) {
      updates.note = values.notes
    }
    submitCaseUpdate(updates)
  }

  const refreshCaseRecord = async (uuid?: string) => {
    const caseUuid = uuid || caseRecord.value?.uuid
    if (caseUuid) {
      await caseStore.loadCase(caseUuid)
    }
  }

  const handleFileUploaded = async (documentUuid: string) => {
    if (caseRecord.value?.uuid) {
      await caseStore.loadCase(caseRecord.value.uuid)
      toast({
        title: t('documentUploaded'),
        description: t('documentUploadedSuccessfully'),
        variant: 'default'
      })
      // Close modal after successful upload
      isDocumentUploadModalOpen.value = false
    }
  }

  const handleDocumentUpdated = async (updatedDocument: any) => {
    console.log('Document updated:', updatedDocument)

    // Update the document in the documents array if it exists
    if (caseDocuments.value) {
      const docIndex = caseDocuments.value.findIndex((d) => d.uuid === updatedDocument.uuid)
      if (docIndex !== -1) {
        caseDocuments.value[docIndex] = {
          ...caseDocuments.value[docIndex],
          ...updatedDocument
        }
        console.log('Updated document in documents array:', caseDocuments.value[docIndex])
      }
    }

    // Update the selected document if this is the document being viewed
    if (
      selectedDocument.value &&
      ((selectedDocument.value as any).uuid === updatedDocument.uuid ||
        (selectedDocument.value as any).id === updatedDocument.uuid)
    ) {
      selectedDocument.value = {
        ...selectedDocument.value,
        ...updatedDocument
      }
      console.log('Updated selected document:', selectedDocument.value)
    }

    // Refresh the case data to get the latest changes
    await refreshCaseRecord()
  }

  const fetchForms = async () => {
    if (forms.value.length > 0) return
    isLoadingForms.value = true
    try {
      const response = await FormsService.formsList({
        tag: 'MASTER',
        limit: 100
      })
      forms.value = response.results || []
    } catch (error) {
      console.error('Error fetching forms:', error)
      forms.value = []
    } finally {
      isLoadingForms.value = false
    }
  }

  const fetchQuestionnaires = async (page: number = 1, searchQuery?: string) => {
    loadingQuestionnaires.value = true
    try {
      const offset = (page - 1) * questionnairePageSize
      const response = await QuestionnairesService.questionnairesList({
        limit: questionnairePageSize,
        offset,
        search: searchQuery || undefined
      })
      // Filter out Jupus templates - only show actual questionnaires
      questionnaires.value = (response.results || []).filter((q) => !q.is_jupus_template)
      questionnaireTotalCount.value = response.count || 0
      questionnaireCurrentPage.value = page
    } catch (error) {
      console.error('Error fetching questionnaires:', error)
      toast({
        title: 'Fehler',
        description: 'Fragebögen konnten nicht geladen werden',
        variant: 'destructive'
      })
    } finally {
      loadingQuestionnaires.value = false
    }
  }

  const handleQuestionnairePage = async (page: number) => {
    await fetchQuestionnaires(page, questionnaireSearchQuery.value || undefined)
  }

  const handleQuestionnaireSearch = async (query: string) => {
    questionnaireSearchQuery.value = query
    await fetchQuestionnaires(1, query || undefined)
  }

  const handleGenerateUnifiedTemplateLink = async () => {
    showUnifiedTemplateModal.value = true
    await fetchUnifiedTemplates()
  }

  const fetchUnifiedTemplates = async () => {
    loadingUnifiedTemplates.value = true
    try {
      const response = await UnifiedDocumentTemplatesService.unifiedDocumentTemplatesList()
      unifiedTemplates.value = response.results || []
    } catch (error) {
      console.error('Error fetching unified templates:', error)
      toast({
        title: 'Fehler',
        description: 'Vorlagen konnten nicht geladen werden',
        variant: 'destructive'
      })
    } finally {
      loadingUnifiedTemplates.value = false
    }
  }

  const handleGenerateQuestionnaireLink = async () => {
    showQuestionnaireModal.value = true
    await fetchQuestionnaires()
  }

  const isEmailReminderForOutstandingDocumentsEnabled = computed({
    get() {
      return caseRecord.value?.send_outstanding_document_email_reminder ?? false
    },
    set(value: boolean) {
      handleEmailReminderForOutstandingDocumentsChange(value)
    }
  })

  const loadingChangeEmailReminderStatus = ref(false)

  const handleEmailReminderForOutstandingDocumentsChange = async (value: boolean) => {
    if (!caseRecord.value?.uuid) return
    caseStore.caseRecord!.send_outstanding_document_email_reminder = value
    loadingChangeEmailReminderStatus.value = true
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          send_outstanding_document_email_reminder: value
        }
      })
      caseStore.caseRecord!.send_outstanding_document_email_reminder = value
    } catch (error) {
      console.error('Error updating case:', error)
      toast({
        title: t('error'),
        description: t('failedToUpdateEmailReminder'),
        variant: 'destructive'
      })
      caseStore.caseRecord!.send_outstanding_document_email_reminder = !value
    }
    loadingChangeEmailReminderStatus.value = false
  }

  const createLinkUnifiedTemplate = async (template: any) => {
    try {
      await copyToClipboard(async () => {
        let shareableLink: string

        if (template.template_type === 'legacy_form') {
          const response = await FormsService.formsThirdPartyTokenRetrieve({
            id: template.id
          })
          shareableLink = `${window.location.origin}/client/portal/external/pdf/${response.creation_token}/${caseRecord.value?.uuid}`
        } else if (template.template_type === 'pdf_form') {
          const response = await PdfFormSubmissionsService.pdfFormSubmissionsExternalCreate({
            requestBody: {
              template_uuid: template.id,
              case_uuid: caseRecord.value?.uuid || ''
            }
          })
          shareableLink = response.submission_url
        } else {
          throw new Error('Unknown template type')
        }

        return shareableLink
      })

      toast({
        title: 'Link kopiert',
        description: 'Der Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })

      showUnifiedTemplateModal.value = false
    } catch (error) {
      console.error('Error generating shareable link:', error)
      toast({
        title: 'Fehler',
        description: 'Der Link konnte nicht generiert werden',
        variant: 'destructive'
      })
    } finally {
      await refreshCaseRecord()
    }
  }

  const createLinkQuestionnaire = async (questionnaire: any) => {
    try {
      await copyToClipboard(async () => {
        const response = await QuestionnairesService.questionnairesThirdPartyCreate({
          requestBody: {
            questionnaire_id: questionnaire.uuid,
            case_id: caseRecord.value?.uuid || ''
          }
        })
        return buildPortalUrl(
          `/client/portal/external/questionnaire/${response.short_id}`,
          response.third_party_access_token
        )
      })

      toast({
        title: 'Link kopiert',
        description: 'Der Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })

      showQuestionnaireModal.value = false
    } catch (error) {
      console.error('Error generating shareable link:', error)
      toast({
        title: 'Fehler',
        description: 'Der Link konnte nicht generiert werden',
        variant: 'destructive'
      })
    } finally {
      await refreshCaseRecord()
    }
  }

  // Format date for appointments
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Format time for appointments
  const formatTime = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return ''
    const start = new Date(startDate)
    const end = new Date(endDate)
    const startHours = start.getHours().toString().padStart(2, '0')
    const startMinutes = start.getMinutes().toString().padStart(2, '0')
    const endHours = end.getHours().toString().padStart(2, '0')
    const endMinutes = end.getMinutes().toString().padStart(2, '0')
    return `${startHours}:${startMinutes} ${t('oclock')} - ${endHours}:${endMinutes} ${t('oclock')}`
  }

  // Format date for documents with time
  const formatDocumentDate = (dateString: string) => {
    const date = new Date(dateString)
    const formattedDate = date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${formattedDate} ${hours}:${minutes} Uhr`
  }

  // Get appointment status text
  const getAppointmentStatusText = (appointment: CustomerAppointment) => {
    switch (appointment.status) {
      case AppointmentStatusEnum.CONFIRMED:
        return t('confirmedAppointment')
      case AppointmentStatusEnum.TENTATIVE:
        return t('tentativeAppointment')
      case AppointmentStatusEnum.CANCELLED:
        return t('cancelledAppointment')
    }
  }

  const deleteAppointmentProposal = async (proposal: CustomerAppointmentProposal) => {
    if (!proposal.uuid) return
    try {
      await AppointmentProposalsService.appointmentProposalsDestroy({
        uuid: proposal.uuid
      })
      await refreshCaseRecord()
    } catch (error) {
      console.error('Error deleting proposal:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error deleting proposal:${error}`
      })
    }
  }

  const cancelAppointmentBooking = async (appointment: CustomerAppointment) => {
    if (!appointment.uuid) return

    const originalStatus = appointment.status
    // @ts-expect-error - Optimistic update on read-only generated type
    appointment.status = AppointmentStatusEnum.CANCELLED

    try {
      await AppointmentsService.appointmentsCancelCreate({
        uuid: appointment.uuid
      })
      await refreshCaseRecord()
    } catch (error) {
      // @ts-expect-error - Revert Optimistic update on read-only generated type
      appointment.status = originalStatus
      console.error('Error cancelling appointment:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error cancelling appointment:${error}`
      })
    }
  }

  const confirmAppointmentBooking = async (appointment: CustomerAppointment) => {
    if (!appointment.uuid) return

    const originalStatus = appointment.status
    // @ts-expect-error - Optimistic update on read-only generated type
    appointment.status = AppointmentStatusEnum.CONFIRMED

    try {
      const response = await AppointmentsService.appointmentsConfirmCreate({
        uuid: appointment.uuid
      })
      await refreshCaseRecord()

      // Check if calendar sync failed
      if ((response as any).calendar_sync_failed || (response as any).warning) {
        toast({
          title: 'Termin bestätigt',
          description:
            (response as any).warning ||
            'Termin wurde bestätigt, konnte aber nicht zum Kalender synchronisiert werden. Bitte fügen Sie ihn manuell hinzu.',
          variant: 'default'
        })
      } else {
        toast({
          title: 'Erfolg',
          description: 'Termin wurde bestätigt',
          variant: 'default'
        })
      }
    } catch (error) {
      // @ts-expect-error - Revert Optimistic update on read-only generated type
      appointment.status = originalStatus
      console.error('Error confirming appointment:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error confirming appointment: ${error}`
      })
    }
  }

  const deleteAppointmentBooking = async (appointment: CustomerAppointment) => {
    if (!appointment.uuid) return
    try {
      await AppointmentsService.appointmentsDestroy({
        uuid: appointment.uuid
      })
      await refreshCaseRecord()
    } catch (error) {
      console.error('Error deleting appointment:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error deleting appointment:${error}`
      })
    }
  }

  // Alias for backward compat — the composable owns fetching now
  const fetchAiSuggestedEmailAction = () => aiWidget.fetchActions()

  // Apply AI suggested email action payload to the email form
  const applyAiSuggestedEmailAction = async (payload: any) => {
    if (!payload) return

    try {
      // Pre-select template
      if (payload.template_uuid) {
        // Make sure templates are loaded first
        if (emailTemplates.value.length === 0) {
          await fetchEmailTemplates()
        }

        const template = emailTemplates.value.find((t) => t.uuid === payload.template_uuid)
        if (template) {
          selectedTemplateId.value = template.uuid
          selectedEmailTemplate.value = template
          emailSubject.value = template.subject || ''
          emailContent.value = template.html || ''
          // Wait for the selectedEmailTemplate watcher to run initializeAttachmentsFromTemplate
          // before adding payload-specific extras, otherwise the watcher overwrites them.
          await nextTick()
        }
      }

      // Pre-select questionnaires
      if (payload.questionnaire_uuids && payload.questionnaire_uuids.length > 0) {
        // Fetch questionnaires if not already loaded (without pagination for AI preload)
        if (questionnaires.value.length === 0) {
          loadingQuestionnaires.value = true
          try {
            const response = await QuestionnairesService.questionnairesList({
              limit: 100
            })
            questionnaires.value = (response.results || []).filter((q) => !q.is_jupus_template)
          } catch (error) {
            console.error('Error fetching questionnaires:', error)
          } finally {
            loadingQuestionnaires.value = false
          }
        }

        const selectedQuestionnaires = questionnaires.value.filter((q) =>
          payload.questionnaire_uuids.includes(q.uuid)
        )

        if (selectedQuestionnaires.length > 0) {
          handleQuestionnairesSelect(selectedQuestionnaires)
        }
      }

      // Pre-select PDF forms
      if (payload.pdf_form_uuids && payload.pdf_form_uuids.length > 0) {
        // Fetch forms if not already loaded
        if (forms.value.length === 0) {
          await fetchForms()
        }

        const selectedForms = forms.value.filter((f) => payload.pdf_form_uuids.includes(f.id))

        if (selectedForms.length > 0) {
          await fetchUnifiedTemplates()
          const selectedUnifiedTemplates = unifiedTemplates.value.filter((t) =>
            payload.pdf_form_uuids.includes(t.id)
          )
          if (selectedUnifiedTemplates.length > 0) {
            await handleUnifiedDocumentsSelect(selectedUnifiedTemplates)
          }
        }
      }

      // Set AI preloaded flag at the END after all attachments are handled
      // This prevents the attachment handlers from resetting it
      isAiPreloaded.value = true
      aiWidget.markReviewed()
    } catch (error) {
      console.error('Error applying AI suggested email action:', error)
    }
  }

  // Fetch email templates
  const fetchEmailTemplates = async () => {
    if (emailTemplates.value.length > 0) return
    try {
      const response = await EmailTemplatesService.emailTemplatesList({
        limit: 1000
      })
      emailTemplates.value = response.results as CustomerEmailTemplate[]

      // Select the first template by default if available and no AI suggestion
      if (emailTemplates.value.length > 0 && !isAiPreloaded.value) {
        const firstTemplate = emailTemplates.value[0]
        selectedTemplateId.value = firstTemplate.uuid
        selectedEmailTemplate.value = firstTemplate
        emailSubject.value = firstTemplate.subject || ''
        emailContent.value = firstTemplate.html || ''
      }
    } catch (error) {
      console.error('Error fetching email templates:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'E-Mail-Vorlagen konnten nicht geladen werden'
      })
    }
  }

  // Track if user has deviated from AI suggestion
  const userDeviatedFromAi = computed(() => {
    if (!aiSuggestedAction.value?.payload?.template_uuid) return false
    return (
      !isAiPreloaded.value &&
      selectedTemplateId.value !== aiSuggestedAction.value.payload.template_uuid
    )
  })

  // Get AI suggested template name
  const aiSuggestedTemplateName = computed(() => {
    if (!aiSuggestedAction.value?.payload?.template_uuid) return ''
    const template = emailTemplates.value.find(
      (tmpl) => tmpl.uuid === aiSuggestedAction.value.payload.template_uuid
    )
    return template?.name || ''
  })

  // Get AI suggestion message with template name
  const aiSuggestionMessage = computed(() => {
    const templateName = aiSuggestedTemplateName.value
    if (!templateName) return ''
    return t('aiSuggestsTemplate', { templateName })
  })

  // Re-apply AI suggestion
  const reapplyAiSuggestion = async () => {
    if (!aiSuggestedAction.value?.payload) return
    await applyAiSuggestedEmailAction(aiSuggestedAction.value.payload)
  }

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    selectedTemplateId.value = templateId
    if (templateId) {
      const template = emailTemplates.value.find((t) => t.uuid === templateId)
      if (template) {
        selectedEmailTemplate.value = template
        emailSubject.value = template.subject || ''
        emailContent.value = template.html || ''
      }
      // Clear AI preloaded flag if user manually changes template
      if (isAiPreloaded.value && templateId !== aiSuggestedAction.value?.payload?.template_uuid) {
        isAiPreloaded.value = false
        aiWidget.markEdited()
      }
      // Re-enable AI preloaded flag if user selects the AI suggested template
      if (
        !isAiPreloaded.value &&
        aiSuggestedAction.value?.payload?.template_uuid &&
        templateId === aiSuggestedAction.value.payload.template_uuid
      ) {
        isAiPreloaded.value = true
      }
    }
  }

  // Handle document attachment
  const handleAttachDocumentTemplate = async () => {
    showDocumentAttachDialog.value = true
  }

  const handleAttachQuestionnaire = async () => {
    showQuestionnaireAttachDialog.value = true
    await fetchQuestionnaires()
  }

  const handleDocumentsSelect = async (documents: TForm[]) => {
    // Clear AI preloaded flag if user manually changes attachments
    if (isAiPreloaded.value && aiSuggestedAction.value) {
      const aiPdfFormUuids = aiSuggestedAction.value.payload?.pdf_form_uuids || []
      const currentPdfFormIds = documents.map((doc) => doc.id)

      if (
        aiPdfFormUuids.length !== currentPdfFormIds.length ||
        !aiPdfFormUuids.every((uuid) => currentPdfFormIds.includes(uuid))
      ) {
        isAiPreloaded.value = false
        aiWidget.markEdited()
      }
    }

    // First, remove all the PDF template documents that are not in the new selection
    emailAttachments.value = emailAttachments.value.filter((attachment) => {
      // Keep non-PDF documents
      if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
        return true
      }

      // For PDF documents, check if they're in the new selection
      const formProps = attachment.documentProperties as FormDocumentProperties
      return documents.some((doc) => doc.id === formProps.id)
    })

    // Then add any new documents that aren't already attached
    const currentFormIds = emailAttachments.value
      .filter((doc) => doc.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE)
      .map((doc) => (doc.documentProperties as FormDocumentProperties).id)

    // Find documents that aren't already attached
    const newDocuments = documents.filter((doc) => !currentFormIds.includes(doc.id))

    // Convert new documents to the attachment format and add them
    if (newDocuments.length > 0) {
      const convertedDocs = newDocuments.map((doc) => {
        return {
          documentType: {
            id: DocumentTypeIdEnum.PDF_TEMPLATE,
            name: 'Dokumentenvorlage'
          },
          documentProperties: {
            id: doc.id,
            organisation: doc.organisation,
            created_at: doc.created_at,
            modified_at: doc.modified_at,
            tag: doc.tag,
            title: doc.title,
            description: doc.description,
            file: doc.file,
            due_at: doc.due_at,
            document_name: doc.document_name,
            annotationData: {},
            signatures: {}
          } as FormDocumentProperties
        } as AttachedDocument
      })

      // Create a new array to ensure reactivity
      emailAttachments.value = [...emailAttachments.value, ...convertedDocs]

      validatePdfFormsAttachments()

      // Load PDFs using the batch loader
      if (newDocuments.length > 0) {
        newPdfStore
          .batchLoadPdfs(newDocuments)
          .then((results) => {
            emailAttachments.value.forEach((attachment: AttachedDocument) => {
              attachment.documentProperties =
                attachment.documentProperties as FormDocumentProperties
              if (
                attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
                results[attachment.documentProperties.id] === true
              ) {
                const currentAnnotations = newPdfStore.getAnnotations(
                  attachment.documentProperties.id
                )
                attachment.documentProperties.annotationData = [...currentAnnotations]

                const formSignatures = signatureStore.getFormSignatures(
                  attachment.documentProperties.id,
                  currentAnnotations
                )
                attachment.documentProperties.signatures = formSignatures
              }
            })
          })
          .catch((error) => {
            console.error('Error in batch loading PDFs:', error)
          })
      }
    }
  }

  const handleFieldValueChanged = (payload: {
    documentId: string
    fieldId: string
    value: any
  }) => {
    const { documentId, fieldId, value } = payload

    const docState = getOrCreateDocumentState(documentId)
    docState.fieldValues.set(fieldId, value)

    const attachment = emailAttachments.value.find((att) => {
      if (att.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
        const props = att.documentProperties as FormDocumentProperties
        return props.id === documentId
      }
      return false
    })

    if (attachment) {
      const formProps = attachment.documentProperties as FormDocumentProperties & {
        form_fields?: any[]
      }
      if (formProps.form_fields) {
        const field = formProps.form_fields.find((f: any) => f.uuid === fieldId)
        if (field) {
          field.value = value
        }
      }
    }
  }

  const handleValidationChanged = (payload: {
    documentId: string
    fieldId: string
    error: string | undefined
  }) => {
    const { documentId, fieldId, error } = payload

    const docState = getOrCreateDocumentState(documentId)

    if (error) {
      docState.validationErrors.set(fieldId, error)
    } else {
      docState.validationErrors.delete(fieldId)
    }

    pdfFormDocumentValidationState.value.set(documentId, docState.validationErrors.size)
  }

  const initializeFieldValuesForDocument = (docId: string, formFields: any[]) => {
    const docState = getOrCreateDocumentState(docId)

    if (docState.fieldValues.size === 0) {
      formFields.forEach((field: any) => {
        if (field.uuid && field.value !== undefined && field.value !== null) {
          docState.fieldValues.set(field.uuid, field.value)
        }
      })
    }
  }

  const handleUnifiedDocumentsSelect = async (documents: UnifiedDocumentTemplate[]) => {
    // Clear AI preloaded flag if user manually changes attachments
    if (isAiPreloaded.value && aiSuggestedAction.value) {
      const aiPdfFormUuids = aiSuggestedAction.value.payload?.pdf_form_uuids || []
      const currentPdfFormIds = documents.map((doc) => doc.id)

      if (
        aiPdfFormUuids.length !== currentPdfFormIds.length ||
        !aiPdfFormUuids.every((uuid) => currentPdfFormIds.includes(uuid))
      ) {
        isAiPreloaded.value = false
        aiWidget.markEdited()
      }
    }

    // First, remove all the PDF template documents that are not in the new selection
    emailAttachments.value = emailAttachments.value.filter((attachment) => {
      // Keep non-PDF template documents (e.g., questionnaires)
      if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
        return true
      }

      // For PDF template documents, check if they're in the new selection
      const formProps = attachment.documentProperties as FormDocumentProperties
      return documents.some((doc) => doc.id === formProps.id)
    })

    // Update existing documents with new form_fields (which include values)
    emailAttachments.value.forEach((attachment) => {
      if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
        const formProps = attachment.documentProperties as FormDocumentProperties & {
          template_type?: string
          form_fields?: any[]
          formFieldValues?: Record<string, any>
        }
        const doc = documents.find((d) => d.id === formProps.id)
        if (doc && doc.template_type === 'pdf_form' && doc.form_fields) {
          // Update the form_fields with the latest values from the dialog
          formProps.form_fields = doc.form_fields

          // Extract field values from form_fields and update formFieldValues
          // This ensures NewPdfViewer gets the correct values via initialFieldValues
          const fieldValues: Record<string, any> = {}
          doc.form_fields.forEach((field: any) => {
            if (field.uuid && field.value !== undefined) {
              fieldValues[field.uuid] = field.value
            }
          })
          formProps.formFieldValues = fieldValues

          initializeFieldValuesForDocument(formProps.id, doc.form_fields)
        }
      }
    })

    // Then add any new documents that aren't already attached
    const currentFormIds = emailAttachments.value
      .filter((doc) => doc.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE)
      .map((doc) => (doc.documentProperties as FormDocumentProperties).id)

    // Find documents that aren't already attached
    const newDocuments = documents.filter((doc) => !currentFormIds.includes(doc.id))

    // Convert new documents to the attachment format and add them
    if (newDocuments.length > 0) {
      const convertedDocs = newDocuments.map((doc) => {
        // Extract field values for PDF forms
        const formFieldValues: Record<string, any> = {}
        if (doc.template_type === 'pdf_form' && doc.form_fields) {
          doc.form_fields.forEach((field: any) => {
            if (field.uuid && field.value !== undefined) {
              formFieldValues[field.uuid] = field.value
            }
          })

          initializeFieldValuesForDocument(doc.id, doc.form_fields)
        }

        return {
          documentType: {
            id: DocumentTypeIdEnum.PDF_TEMPLATE,
            name: 'Dokumentenvorlage'
          },
          documentProperties: {
            id: doc.id,
            organisation: doc.created_by?.org?.uuid || '',
            created_at: doc.created_at,
            modified_at: doc.modified_at,
            tag: 'MASTER',
            title: doc.title || '',
            description: doc.description || '',
            file: doc.file,
            due_at: null,
            document_name: doc.file,
            template_type: doc.template_type, // Store template type for email sending
            form_fields: doc.template_type === 'pdf_form' ? doc.form_fields : undefined, // Store form fields with values
            formFieldValues:
              doc.template_type === 'pdf_form' && Object.keys(formFieldValues).length > 0
                ? formFieldValues
                : undefined,
            annotationData: {},
            signatures: {}
          } as FormDocumentProperties & {
            template_type: string
            form_fields?: any[]
            formFieldValues?: Record<string, any>
          }
        } as AttachedDocument
      })

      // Create a new array to ensure reactivity
      emailAttachments.value = [...emailAttachments.value, ...convertedDocs]

      // Load PDFs using the batch loader - extract TForm objects from convertedDocs
      const tFormDocuments: TForm[] = convertedDocs.map((doc) => {
        const props = doc.documentProperties as FormDocumentProperties
        return {
          id: props.id,
          organisation: props.organisation,
          created_at: props.created_at,
          modified_at: props.modified_at,
          tag: props.tag,
          title: props.title || '',
          description: props.description || '',
          file: props.file,
          due_at: props.due_at,
          document_name: props.document_name
        }
      })

      newPdfStore
        .batchLoadPdfs(tFormDocuments)
        .then((results) => {
          emailAttachments.value.forEach((attachment: AttachedDocument) => {
            attachment.documentProperties = attachment.documentProperties as FormDocumentProperties
            const formProps = attachment.documentProperties as FormDocumentProperties & {
              template_type?: string
            }

            // Only extract annotations for legacy forms (not new PDF forms with form_fields)
            if (
              attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
              results[attachment.documentProperties.id] === true &&
              formProps.template_type !== 'pdf_form'
            ) {
              const currentAnnotations = newPdfStore.getAnnotations(
                attachment.documentProperties.id
              )
              attachment.documentProperties.annotationData = [...currentAnnotations]

              const formSignatures = signatureStore.getFormSignatures(
                attachment.documentProperties.id,
                currentAnnotations
              )
              attachment.documentProperties.signatures = formSignatures
            }
          })
        })
        .catch((error) => {
          console.error('Error in batch loading PDFs:', error)
        })
    }

    validatePdfFormsAttachments()
  }

  const handleQuestionnairesSelect = (questionnaires: CustomerQuestionnaire[]) => {
    if (!questionnaires || questionnaires.length === 0) return

    questionnaires.forEach((questionnaire) => {
      // Check if questionnaire is already attached
      const exists = emailAttachments.value.some(
        (attachment) =>
          attachment.documentType.id === DocumentTypeIdEnum.QUESTIONNAIRE &&
          (attachment.documentProperties as QuestionnaireDocumentProperties).id ===
            questionnaire.uuid
      )

      if (!exists) {
        const attachment: AttachedDocument = {
          documentType: {
            id: DocumentTypeIdEnum.QUESTIONNAIRE,
            name: 'Fragebogen'
          },
          documentProperties: {
            ...questionnaire,
            id: questionnaire.uuid
          } as QuestionnaireDocumentProperties
        }

        emailAttachments.value.push(attachment)
      }
    })

    // Clear AI preloaded flag if user manually changes attachments
    if (isAiPreloaded.value && aiSuggestedAction.value) {
      const aiQuestionnaireUuids = aiSuggestedAction.value.payload?.questionnaire_uuids || []
      const currentQuestionnaireUuids = emailAttachments.value
        .filter((att) => att.documentType.id === DocumentTypeIdEnum.QUESTIONNAIRE)
        .map((att) => (att.documentProperties as QuestionnaireDocumentProperties).id)

      if (
        aiQuestionnaireUuids.length !== currentQuestionnaireUuids.length ||
        !aiQuestionnaireUuids.every((uuid) => currentQuestionnaireUuids.includes(uuid))
      ) {
        isAiPreloaded.value = false
        aiWidget.markEdited()
      }
    }
  }

  const handleQuestionnairePreview = (questionnaire: CustomerQuestionnaire) => {
    if (questionnaire && questionnaire.uuid) {
      window.open(`/client/portal/questionnaire/preview/${questionnaire.uuid}`, '_blank')
    }
  }

  const handleUnifiedTemplatePreview = (template: any) => {
    if (template && template.file) {
      window.open(template.file, '_blank')
    }
  }

  const openPdfEditor = (form: AttachedDocument) => {
    const props = form.documentProperties as FormDocumentProperties & {
      template_type?: string
      form_fields?: any[]
    }

    // Check if this is a PDF form - show form fields instead of annotations
    if (props.template_type === 'pdf_form') {
      // Extract field values from form_fields array
      const fieldValues: Record<string, any> = {}
      if (props.form_fields) {
        props.form_fields.forEach((field) => {
          if (field.uuid && field.value !== undefined) {
            fieldValues[field.uuid] = field.value
          }
        })
      }

      selectedDocument.value = {
        ...props,
        id: props.id,
        uuid: props.id,
        title: props.title,
        file: props.file,
        url: props.file,
        formFieldValues: fieldValues // Store extracted values for NewPdfViewer
      } as any
      showPdfForms.value = false // Hide legacy forms mode
      showPdfEditor.value = true
    } else {
      // Legacy form - show annotations
      selectedDocument.value = form.documentProperties as CustomerForm
      showPdfForms.value = true
      showPdfEditor.value = true
    }
  }

  const removeAttachment = (attachment: AttachedDocument) => {
    // Get the document identifier based on its type
    let docIdentifier: string | undefined

    if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
      docIdentifier = (attachment.documentProperties as QuestionnaireDocumentProperties)?.uuid
    } else {
      docIdentifier = (attachment.documentProperties as FormDocumentProperties)?.id
    }

    if (!docIdentifier) {
      console.error('Could not determine document identifier for removal', attachment)
      return
    }

    emailAttachments.value = emailAttachments.value.filter((attachment) => {
      if (attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE) {
        return (
          (attachment.documentProperties as QuestionnaireDocumentProperties)?.uuid !== docIdentifier
        )
      } else {
        return (attachment.documentProperties as FormDocumentProperties)?.id !== docIdentifier
      }
    })

    validatePdfFormsAttachments()

    // Clear AI preloaded flag if user removes attachments
    if (isAiPreloaded.value) {
      isAiPreloaded.value = false
      aiWidget.markEdited()
    }

    // Also remove from the PDF store if it was loaded
    if (
      attachment.documentType?.id === DocumentTypeIdEnum.PDF_TEMPLATE &&
      docIdentifier &&
      newPdfStore.selectedPdfs.has(docIdentifier)
    ) {
      newPdfStore.removePdf(docIdentifier)
    }
  }

  const getAttachmentKey = (attachment: AttachedDocument): string => {
    if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
      return `form-${(attachment.documentProperties as FormDocumentProperties).id}`
    } else {
      return `questionnaire-${(attachment.documentProperties as QuestionnaireDocumentProperties).id}`
    }
  }

  const formatAttachmentName = (attachment: AttachedDocument): string => {
    if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
      const formProps = attachment.documentProperties as FormDocumentProperties
      const fileName = formProps.document_name?.split('/').pop()
      return formProps.title || fileName || 'Unnamed Document'
    } else {
      const questionnaireProps = attachment.documentProperties as QuestionnaireDocumentProperties
      return questionnaireProps.title || 'Unnamed Questionnaire'
    }
  }

  const syncEntityDataToBackend = async (): Promise<boolean> => {
    try {
      const clientData = caseRecord.value?.data?.client
      if (!clientData) {
        return true
      }

      const updatePayload: PatchedCustomerCaseRequest = {
        data: {
          client: {
            email: clientData.email || '',
            first_name: clientData.first_name || '',
            last_name: clientData.last_name || ''
          }
        } as any
      }

      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value?.uuid || '',
        requestBody: updatePayload
      })

      return true
    } catch (error) {
      console.error('Error syncing entity data to backend:', error)
      toast({
        title: t('error'),
        description: t('failedToSyncEntityData'),
        variant: 'destructive'
      })
      return false
    }
  }

  const handlePendingChangesCancel = () => {
    showPendingChangesDialog.value = false
  }

  const handlePendingChangesReview = () => {
    showPendingChangesDialog.value = false
    if (caseRecord.value?.uuid) {
      const route = router.resolve({
        path: `/app/cases/${caseRecord.value.uuid}/entities`
      })
      const reviewWindow = window.open(route.href, '_blank')
      if (!reviewWindow) {
        return
      }
      reviewWindow.opener = null
      toast({
        description: t('entitiesOpenedInNewTab')
      })
    }
  }

  const handleSendEmail = async (forceReject: boolean = false) => {
    if (isSendingEmail.value) return

    if (hasPendingProposedChanges.value) {
      showPendingChangesDialog.value = true
      return
    }

    // Check if client email is missing
    const clientEmail = caseRecord.value?.data?.client?.email
    if (!clientEmail || clientEmail.trim() === '') {
      toast({
        title: t('warning') || 'Warning',
        description:
          t('noClientEmailWarning') ||
          'No client email address is set. Please add a client email before sending.',
        variant: 'destructive'
      })
      return
    }

    try {
      isSendingEmail.value = true

      // Sync entity data (email, first_name, last_name) to backend before sending email
      const syncSuccess = await syncEntityDataToBackend()
      if (!syncSuccess) {
        return
      }

      // Ensure all PDF annotations and form field values are up to date in the attachments
      const pdfAttachments = emailAttachments.value.filter(
        (attachment) => attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE
      )

      for (const attachment of pdfAttachments) {
        const formProps = attachment.documentProperties as FormDocumentProperties & {
          template_type?: string
          form_fields?: any[]
        }

        // Handle PDF forms with form fields (new style)
        if (formProps.template_type === 'pdf_form') {
          // For PDF forms, we send form_fields array with values embedded
          // The form_fields with values are already stored in the attachment from the dialog
          // No need to update them here, the backend will process form_fields
          if (!formProps.form_fields) {
            formProps.form_fields = []
          }
        } else {
          // Handle legacy forms with annotations (old style)
          const currentAnnotations = newPdfStore.getAnnotations(formProps.id)
          formProps.annotationData = [...currentAnnotations]

          // Add signatures to the form properties
          const formSignatures = signatureStore.getFormSignatures(formProps.id, currentAnnotations)
          formProps.signatures = formSignatures
        }
      }

      // Make the API call
      const sentEmailResponse = await CasesService.casesSendEmailCreate({
        uuid: caseRecord.value?.uuid || '',
        requestBody: {
          subject: emailSubject.value,
          body: emailContent.value,
          attachments: emailAttachments.value,
          auto_reject: forceReject,
          email_template_uuid: selectedTemplateId.value || undefined
        }
      })

      // Add the sent email UUID to pending list for loading state
      if (sentEmailResponse.uuid) {
        pendingEmailUuids.value.add(sentEmailResponse.uuid)

        // Set a timeout to automatically remove from pending after 10 minutes
        // in case delivery events don't arrive (fallback safety measure)
        setTimeout(() => {
          if (pendingEmailUuids.value.has(sentEmailResponse.uuid)) {
            pendingEmailUuids.value.delete(sentEmailResponse.uuid)
          }
        }, 600000) // 10 minutes
      }

      emailAttachments.value = []
    } catch (error: any) {
      toast({
        title: 'Fehler',
        description:
          'E-Mail konnte nicht gesendet werden: ' +
          (error.body?.detail || error.message || JSON.stringify(error)),
        variant: 'destructive'
      })
    } finally {
      isSendingEmail.value = false
      // Refresh case data, emails, and agent actions (backend may have committed an action)
      await refreshCaseRecord()
      await fetchEmails(false)
      await fetchAiSuggestedEmailAction()

      // Refresh the agent action widget if it exists
      agentActionWidgetRef.value?.refresh()

      // Ensure accordion is expanded (only set if not already open to avoid flicker)
      const wasOpen = emailHistoryAccordionValue.value === 'email-history'
      if (!wasOpen) {
        emailHistoryAccordionValue.value = 'email-history'
        await nextTick()
        setTimeout(() => {
          scrollToEmailHistory()
        }, 300)
      } else {
        await nextTick()
        scrollToEmailHistory()
      }
    }
  }

  function scrollToEmailHistory() {
    const emailElement = document.getElementById('email')
    if (emailElement) {
      const elementPosition = emailElement.getBoundingClientRect().top + window.scrollY
      const offsetPosition = elementPosition - 20 // 20px offset from top

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  async function fetchEmails(showLoading = true) {
    if (showLoading) {
      isLoadingEmails.value = true
    }
    try {
      const caseUuid = caseRecord.value?.uuid
      if (caseUuid) {
        const response = await EmailsService.emailsList({
          _case: caseUuid,
          limit: 50
        })
        emails.value = response.results as CustomerEmail[]

        // Clean up pending emails that now have delivery events
        const currentEmailUuids = new Set(emails.value.map((email) => email.uuid))
        const pendingToRemove: string[] = []

        pendingEmailUuids.value.forEach((uuid) => {
          const email = emails.value.find((e) => e.uuid === uuid)
          // Remove from pending if email now has a delivery event
          if (email && email.relevant_delivery_event) {
            pendingToRemove.push(uuid)
          }
        })

        pendingToRemove.forEach((uuid) => {
          pendingEmailUuids.value.delete(uuid)
          console.log('🧹 Cleaned up pending email with delivery event:', uuid)
        })
      }
    } catch (error) {
      console.error('Error fetching emails:', error)
    } finally {
      isLoadingEmails.value = false
    }
  }

  // Helper function to get initials from a name
  function getInitials(name: string): string {
    if (!name) return ''
    const words = name.split(' ')
    const initials = words.map((word) => word.charAt(0).toUpperCase())
    return initials.join('')
  }

  // Helper function to get template type label
  const getTemplateTypeLabel = (type: EmailTemplateType) => {
    switch (type) {
      case EmailTemplateType.CREATE:
        return t('acceptanceTemplate')
      case EmailTemplateType.REJECT:
        return t('rejectionTemplate')
      default:
        return ''
    }
  }

  // Computed property for template options
  const templateOptions = computed(() => {
    if (!emailTemplates.value.length) {
      return [{ value: '', title: t('selectTemplate') }]
    }

    return emailTemplates.value.map((template) => ({
      value: template.uuid,
      title: `${template.name} (${getTemplateTypeLabel(template.type)})`
    }))
  })

  const getDocumentBadgeColor = (documentType: string | DocumentTypeEnum | DocumentTypeIdEnum) => {
    switch (documentType) {
      case DocumentTypeEnum.FRAGEBOGEN:
        return 'bg-[#17a2b8]'
      case DocumentTypeIdEnum.QUESTIONNAIRE:
        return 'bg-[#17a2b8]'

      case DocumentTypeEnum.DOKUMENTENVORLAGE:
        return 'bg-[#dc3545]'
      case DocumentTypeIdEnum.PDF_TEMPLATE:
        return 'bg-[#dc3545]'
      case DocumentTypeIdEnum.PDF_FORM_SUBMISSION:
        return 'bg-[#dc3545]'

      case DocumentTypeEnum.UPLOAD:
        return 'bg-black'
      default:
        return 'bg-[#84cc16]'
    }
  }

  // Open document preview in PDF viewer or Office preview
  const previewDocument = async (document: any) => {
    try {
      // Handle PDF form submissions differently - they have a direct download URL
      if (document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION) {
        const pdfUrl = document.download?.url
        if (pdfUrl) {
          currentPdfUrl.value = pdfUrl
          currentPdfFilename.value = document.filename
          currentDocumentUuid.value = document.uuid
          showPdfViewer.value = true
        } else {
          toast({
            variant: 'destructive',
            title: 'Fehler',
            description: 'PDF URL not available for this submission.'
          })
        }
        return
      }

      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        // Check if it's an Office file that should use the Office preview
        if (isOfficeFile(document)) {
          currentOfficeDocument.value = response as PdfDocument
          showOfficePreview.value = true
        } else {
          // Pass the full document object with all necessary properties for persistence
          selectedDocument.value = {
            ...response,
            id: document.uuid,
            uuid: document.uuid,
            title: document.filename,
            filename: document.filename,
            file: response.url,
            url: response.url,
            current_pdf_url: response.current_pdf_url || response.url,
            original_pdf_url: response.original_pdf_url || response.url,
            is_pdf_modified: response.is_pdf_modified || false,
            organisation: document.organisation,
            created_at: document.created_at,
            modified_at: document.modified_at,
            tag: document.tag,
            description: document.description,
            due_at: document.due_at,
            document_name: document.document_name
          } as any
          showPdfForms.value = false
          showPdfEditor.value = true
        }
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Cannot open document.'
        })
      }
    } catch (error) {
      console.error('Error opening document:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error opening document: ${error}`
      })
    }
  }

  // Open document in new tab
  const downloadDocument = async (document: any) => {
    try {
      // Handle PDF form submissions differently - they have a direct download URL
      if (document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION) {
        const pdfUrl = document.download?.url
        if (pdfUrl) {
          window.open(pdfUrl, '_blank')
        } else {
          toast({
            variant: 'destructive',
            title: 'Fehler',
            description: 'PDF URL not available for this submission.'
          })
        }
        return
      }

      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        window.open(response.url, '_blank')
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Cannot open document.'
        })
      }
    } catch (error: any) {
      console.log(error)
      if (error.status === 404) {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Es existieren keine Dokumente für diese Anfrage.'
        })
        return
      }
      console.error('Error opening document in new tab:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error opening document: ${error}`
      })
    }
  }

  const isOfficeFile = (document: any) => {
    return (
      (officeEditingFlag.isEnabled.value || officePreviewFlag.isEnabled.value) &&
      isOfficeFileUtil(document)
    )
  }

  const isEditableDocument = (document: { document_type: string; filename?: string }) => {
    return officeEditingFlag.isEnabled.value && isEditableDocumentUtil(document)
  }

  const openOfficeFile = (document: any) => {
    if (!officeEditingFlag.isEnabled.value) {
      previewDocument(document)
      return
    }
    openOfficeEditor(document)
  }

  const previewOfficeDocument = async (document: any) => {
    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response) {
        const tokenResponse = await DocumentsService.documentsCreateEditTokenCreate({
          uuid: document.uuid,
          requestBody: { resource_type: ResourceTypeEnum.DOCUMENT }
        })
        // Build the download URL from the token — must match what's embedded in the JWT.
        // The backend builds: {host}/d/download/{token}, replacing localhost with app:8000 in dev.
        let downloadUrl = `${window.location.origin}/d/download/${tokenResponse.token}`
        if (downloadUrl.includes('localhost')) {
          downloadUrl = downloadUrl.replace('localhost', 'app')
        }
        currentOfficeDocument.value = {
          ...response,
          url: downloadUrl
        } as PdfDocument
        currentOfficeToken.value = tokenResponse.onlyoffice_jwt
        showOfficePreview.value = true
      } else {
        toast({
          variant: 'destructive',
          title: 'Fehler',
          description: 'Cannot open document.'
        })
      }
    } catch (error) {
      console.error('Error opening Office document preview:', error)
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: `Error opening document: ${error}`
      })
    }
  }

  const openDocument = (document: RequestedDocuments) => {
    if (document.document_type === DocumentTypeEnum.FRAGEBOGEN) {
      openQuestionnaireSubmissionByDocumentUuid(document.uuid)
      return
    }
    if (isOfficeFile(document)) {
      openOfficeFile(document)
    } else {
      previewDocument(document)
    }
  }

  const openQuestionnaireSubmissionByDocumentUuid = (documentUuid: string) => {
    const submission = caseRecord.value?.questionnaire_submissions?.find(
      (s) => s.document_uuid === documentUuid
    )
    if (submission) {
      selectedQuestionnaireSubmission.value = submission as RequestedQuestionnaire
      showQuestionnaireSubmissionModal.value = true
    }
  }

  // Handle manual close of questionnaire
  const handleCloseQuestionnaire = async (questionnaire: any) => {
    try {
      await QuestionnaireSubmissionsService.questionnaireSubmissionsPartialUpdate({
        uuid: questionnaire.uuid,
        requestBody: {
          closed: true
        }
      })

      // Refresh the document lists to update the requested/received documents
      await refreshCaseRecord()

      toast({
        title: 'Questionnaire closed',
        description: 'The questionnaire has been closed successfully',
        variant: 'default'
      })
    } catch (e: any) {
      toast({
        title: 'Error closing questionnaire',
        description: `${e}`,
        variant: 'destructive'
      })
    }
  }

  // Copy document template link to clipboard
  const copyDocumentTemplateLink = async (document: RequestedDocuments) => {
    try {
      // For PDF form submissions
      if (document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION) {
        const uuid = document.uuid
        if (!uuid) {
          toast({
            title: t('error'),
            description: t('documentInfoNotFound'),
            variant: 'destructive'
          })
          return
        }

        // Check if this is an external PDF form submission
        const isExternal = 'is_external' in document && document['is_external'] === true

        let accessToken: string | null = null
        let shareableLink: string

        if (isExternal) {
          // For external PDF form submissions, use third_party_access_token
          const token =
            'third_party_access_token' in document ? document['third_party_access_token'] : null
          accessToken = typeof token === 'string' ? token : null

          shareableLink = buildPortalUrl(
            `/client/portal/external/pdf-templates/${uuid}`,
            accessToken
          )
        } else {
          // For non-external PDF form submissions, use case's user_access_token
          accessToken = caseRecord.value?.user_access_token || null

          shareableLink = buildPortalUrl(`/client/portal/pdf-form-submissions/${uuid}`, accessToken)
        }

        await copyToClipboard(shareableLink)

        toast({
          title: t('copyLink'),
          description: 'Link wurde in die Zwischenablage kopiert',
          variant: 'default'
        })
        return
      }

      // For legacy document templates (DOKUMENTENVORLAGE)
      if (document.document_type === DocumentTypeEnum.DOKUMENTENVORLAGE) {
        const documentUuid = document.uuid
        if (!documentUuid) {
          toast({
            title: t('error'),
            description: t('documentInfoNotFound'),
            variant: 'destructive'
          })
          return
        }

        let shareableLink: string | null = null

        if (caseRecord.value?.form_requests && Array.isArray(caseRecord.value.form_requests)) {
          const request = caseRecord.value.form_requests.find((r: any) => {
            if (r.document_uuid === documentUuid) {
              return true
            }
            // Check nested document.uuid
            if (r.document?.uuid === documentUuid) {
              return true
            }
            // Check form.document.uuid
            if (r.form?.document?.uuid === documentUuid) {
              return true
            }
            // Check form.document_uuid (if serialized)
            if (r.form?.document_uuid === documentUuid) {
              return true
            }
            return false
          })

          if (request?.token) {
            // Check if this is a third-party request
            const isThirdParty = request.is_third_party

            let accessToken: string | null = null

            if (isThirdParty) {
              accessToken = (request as any).third_party_access_token || null

              shareableLink = buildPortalUrl(
                `/client/portal/document/${request.token}`,
                accessToken
              )
            } else {
              accessToken = caseRecord.value?.user_access_token || null
              shareableLink = buildPortalUrl(
                `/client/portal/document/${request.token}`,
                accessToken
              )
            }
          }
        }

        if (!shareableLink) {
          toast({
            title: t('error'),
            description: t('documentLinkNotAvailable'),
            variant: 'destructive'
          })
          return
        }

        await copyToClipboard(shareableLink)

        toast({
          title: t('copyLink'),
          description: 'Link wurde in die Zwischenablage kopiert',
          variant: 'default'
        })
        return
      }

      toast({
        title: t('error'),
        description: t('unsupportedDocumentType'),
        variant: 'destructive'
      })
    } catch (error) {
      console.error('Error copying document template link:', error)
      toast({
        title: t('error'),
        description: t('linkCopyFailed'),
        variant: 'destructive'
      })
    }
  }

  // Copy client portal link to clipboard
  const copyClientPortalLink = async () => {
    try {
      let accessToken = caseRecord.value?.user_access_token

      // If not available, try to retrieve the case by UUID to get the token
      if (!accessToken && caseRecord.value?.uuid) {
        try {
          const response = await CasesService.casesRetrieve({
            uuid: caseRecord.value.uuid
          })
          accessToken = response.user_access_token
        } catch (error) {
          console.error('Error retrieving case for access token:', error)
        }
      }

      if (!accessToken) {
        toast({
          title: t('error'),
          description: t('clientPortalLinkNotAvailable'),
          variant: 'destructive'
        })
        return
      }

      const clientPortalLink = buildPortalUrl('/client/portal', accessToken)

      await copyToClipboard(clientPortalLink)

      toast({
        title: t('copyLink'),
        description: 'Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })
    } catch (error) {
      console.error('Error copying client portal link:', error)
      toast({
        title: t('error'),
        description: t('linkCopyFailed'),
        variant: 'destructive'
      })
    }
  }

  // Copy questionnaire link to clipboard
  const copyQuestionnaireLink = async (questionnaire: CustomerQuestionnaireSubmission) => {
    try {
      const shortId = questionnaire.short_id

      if (!shortId) {
        toast({
          title: t('error'),
          description: t('questionnaireInfoNotFound'),
          variant: 'destructive'
        })
        return
      }

      let accessToken: string | undefined
      let shareableLink: string

      if (questionnaire.is_third_party_questionnaire) {
        // For third-party questionnaires, try to retrieve access token via API
        if (questionnaire.uuid) {
          try {
            const response = await QuestionnaireSubmissionsService.questionnaireSubmissionsRetrieve(
              {
                uuid: questionnaire.uuid
              }
            )
            accessToken = (response as any).third_party_access_token
          } catch (error) {
            console.error('Error retrieving questionnaire submission:', error)
          }
        }

        // If not available from API, try to find it in caseRecord's questionnaire_submissions
        if (!accessToken && questionnaire.uuid && caseRecord.value?.questionnaire_submissions) {
          const submission = caseRecord.value.questionnaire_submissions.find(
            (s) => s.uuid === questionnaire.uuid
          )
          if (submission) {
            accessToken = (submission as any).third_party_access_token || undefined
          }
        }

        shareableLink = buildPortalUrl(
          `/client/portal/external/questionnaire/${shortId}`,
          accessToken
        )
      } else {
        // For non-third-party questionnaires, use case's user_access_token with non-external route
        accessToken = caseRecord.value?.user_access_token

        shareableLink = buildPortalUrl(`/client/portal/questionnaire/${shortId}`, accessToken)
      }

      await copyToClipboard(shareableLink)

      toast({
        title: t('copyLink'),
        description: 'Link wurde in die Zwischenablage kopiert',
        variant: 'default'
      })
    } catch (error) {
      console.error('Error copying questionnaire link:', error)
      toast({
        title: t('error'),
        description: t('linkCopyFailed'),
        variant: 'destructive'
      })
    }
  }

  const openEmailInNewTab = (email: CustomerEmail) => {
    if (!email || !email.pdf_url) return
    window.open(email.pdf_url, '_blank')
  }

  // Delivery event helper functions
  const getDeliveryEventStyle = (event: string) => {
    switch (event) {
      case 'SENT':
        return 'bg-blue-100 text-blue-800'
      case 'DELIVERED':
        return 'bg-green-100 text-green-800'
      case 'OPENED':
        return 'bg-purple-100 text-purple-800'
      case 'CLICKED':
        return 'bg-indigo-100 text-indigo-800'
      case 'HARD_BOUNCED':
      case 'SOFT_BOUNCED':
        return 'bg-red-100 text-red-800'
      case 'BLOCKED':
      case 'SPAM':
        return 'bg-orange-100 text-orange-800'
      case 'UNSUBSCRIBED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getDeliveryEventIcon = (event: string) => {
    switch (event) {
      case 'SENT':
        return MailIcon
      case 'DELIVERED':
        return CheckIcon
      case 'OPENED':
        return EyeIcon
      case 'CLICKED':
        return MousePointerClickIcon
      case 'HARD_BOUNCED':
      case 'SOFT_BOUNCED':
        return XCircleIcon
      case 'BLOCKED':
        return ShieldXIcon
      case 'SPAM':
        return AlertTriangleIcon
      case 'UNSUBSCRIBED':
        return XCircleIcon
      default:
        return MailIcon
    }
  }

  const getDeliveryEventLabel = (event: string) => {
    switch (event) {
      case 'SENT':
        return t('deliveryEventSent')
      case 'DELIVERED':
        return t('deliveryEventDelivered')
      case 'OPENED':
        return t('deliveryEventOpened')
      case 'CLICKED':
        return t('deliveryEventClicked')
      case 'HARD_BOUNCED':
        return t('deliveryEventHardBounced')
      case 'SOFT_BOUNCED':
        return t('deliveryEventSoftBounced')
      case 'BLOCKED':
        return t('deliveryEventBlocked')
      case 'SPAM':
        return t('deliveryEventSpam')
      case 'UNSUBSCRIBED':
        return t('deliveryEventUnsubscribed')
      default:
        return t('deliveryEventUnknown')
    }
  }
  const summarizeInquiryWithAI = async () => {
    try {
      const caseUuid = caseRecord.value?.uuid
      if (!caseUuid) {
        throw new Error('Case UUID is missing')
      }

      isGeneratingSummary.value = true

      const response = await CasesService.casesAiSummaryCreate({
        uuid: caseUuid
      })

      inquirySummary.value = response
    } catch (error) {
      console.error('Error summarizing inquiry:', error)
      toast({
        title: 'Fehler',
        description: 'Fehler beim Zusammenfassen der Anfrage',
        variant: 'destructive'
      })
    } finally {
      isGeneratingSummary.value = false
    }
  }

  const openDeliveryStatusModal = (email: CustomerEmail) => {
    selectedEmailForDeliveryStatus.value = email
    showDeliveryStatusModal.value = true
  }

  const updateLastSeenAt = async (uuid?: string) => {
    const caseUuid = uuid || caseRecord.value?.uuid
    if (!caseUuid) {
      console.warn('Cannot update last seen: case UUID is not available')
      return
    }

    try {
      await CasesService.casesPartialUpdate({
        uuid: caseUuid,
        requestBody: {
          last_activity_seen_at: new Date().toISOString()
        }
      })
    } catch (error) {
      console.error('Failed to update last activity seen at:', error)
    }
  }

  // Initialize attachments from email template
  const initializeAttachmentsFromTemplate = (template: CustomerEmailTemplate) => {
    if (!template) return

    const attachments: AttachedDocument[] = []

    // Add forms from template
    if (template.forms && template.forms.length > 0) {
      template.forms.forEach((form) => {
        attachments.push({
          documentType: {
            id: DocumentTypeIdEnum.PDF_TEMPLATE,
            name: 'Dokumentenvorlage'
          },
          documentProperties: {
            id: form.id,
            organisation: form.organisation,
            created_at: form.created_at,
            modified_at: form.modified_at,
            tag: form.tag,
            title: form.title,
            description: form.description,
            file: form.file,
            due_at: form.due_at,
            document_name: form.document_name,
            annotationData: {},
            signatures: {}
          } as FormDocumentProperties
        })
      })

      // Load PDFs using the batch loader
      newPdfStore
        .batchLoadPdfs(template.forms)
        .then((results) => {
          attachments.forEach((attachment: AttachedDocument) => {
            if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
              const formProps = attachment.documentProperties as FormDocumentProperties
              if (results[formProps.id] === true) {
                const currentAnnotations = newPdfStore.getAnnotations(formProps.id)

                formProps.annotationData = [...currentAnnotations]

                const formSignatures = signatureStore.getFormSignatures(
                  formProps.id,
                  currentAnnotations
                )
                formProps.signatures = formSignatures
              }
            }
          })
        })
        .catch((error) => {
          console.error('Error in batch loading PDFs:', error)
        })
    }

    // Add PDF form templates from template
    const pdfFormTemplates = (template as any).pdf_form_templates
    if (pdfFormTemplates && pdfFormTemplates.length > 0) {
      pdfFormTemplates.forEach((pdfTemplate: any) => {
        // Prefill form fields with case data
        const formFields = pdfTemplate.form_fields || []
        const caseData = {
          client: caseRecord.value?.data?.client,
          opponent: caseRecord.value?.data?.opponent
        }

        // Build prefill data from form fields and case data
        const prefillDataByRelationship = buildPrefillDataFromFormFields(formFields, caseData)

        // Prefill the form fields
        const fieldValues = prefillFormFields(formFields, prefillDataByRelationship, true)

        // Update form_fields with prefilled values
        const prefilledFormFields = formFields.map((field: any) => ({
          ...field,
          value: fieldValues[field.uuid] ?? field.value
        }))

        attachments.push({
          documentType: {
            id: DocumentTypeIdEnum.PDF_TEMPLATE,
            name: 'Dokumentenvorlage'
          },
          documentProperties: {
            id: pdfTemplate.uuid,
            organisation: pdfTemplate.org?.uuid || '',
            created_at: pdfTemplate.created_at,
            modified_at: pdfTemplate.modified_at,
            tag: 'MASTER',
            title: pdfTemplate.name || '',
            description: pdfTemplate.description || '',
            file: pdfTemplate.processed_pdf || pdfTemplate.original_pdf,
            due_at: null,
            document_name: pdfTemplate.name,
            template_type: 'pdf_form',
            form_fields: prefilledFormFields,
            formFieldValues: fieldValues, // Store field values for quick access in NewPdfViewer
            annotationData: {},
            signatures: {}
          } as FormDocumentProperties & {
            template_type: string
            form_fields?: any[]
            formFieldValues?: Record<string, any>
          }
        })
      })
    }

    // Add questionnaires from template
    if (template.questionnaires && template.questionnaires.length > 0) {
      template.questionnaires.forEach((questionnaire) => {
        attachments.push({
          documentType: {
            id: DocumentTypeIdEnum.QUESTIONNAIRE,
            name: 'Fragebogen'
          },
          documentProperties: {
            ...questionnaire,
            id: questionnaire.uuid
          } as QuestionnaireDocumentProperties
        })
      })
    }

    // Update the attachments
    emailAttachments.value = attachments

    validatePdfFormsAttachments()
  }

  // Feature flag for agent action widget
  const isAgentActionWidgetEnabled = ref(false)
  const isAiAssistantEnabled = ref(false)
  const aiSettings = ref(null)
  const agentActionWidgetRef = ref<InstanceType<typeof AgentActionWidget> | null>(null)

  // Check feature flags and AI settings on mount
  const checkAiFeatureFlags = async () => {
    const [featureFlagEnabled, aiAssistantFlagEnabled, response] = await Promise.all([
      agenticWorkflowsFlag.checkFlag(),
      aiAssistantFlag.checkFlag(),
      AiSettingsService.aiSettingsRetrieve({ uuid: 'current' })
    ])
    aiSettings.value = response

    // Enable widget only if both feature flag is enabled AND AI workflows are enabled
    isAgentActionWidgetEnabled.value = featureFlagEnabled && aiSettings.value.ai_workflows_enabled
    isAiAssistantEnabled.value = aiAssistantFlagEnabled && aiSettings.value.ai_workflows_enabled
  }

  // Store current document ID being validated
  const currentValidatingDocId = ref<string>('')

  const getAttachmentFieldValue = (fieldId?: string): any => {
    if (!fieldId || !currentValidatingDocId.value) return ''

    const attachment = emailAttachments.value.find((att) => {
      if (att.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
        const props = att.documentProperties as FormDocumentProperties & {
          template_type?: string
          form_fields?: any[]
        }
        // Only match new PDF forms with template_type === 'pdf_form'
        return props.id === currentValidatingDocId.value && props.template_type === 'pdf_form'
      }
      return false
    })

    if (!attachment) return ''

    const props = attachment.documentProperties as FormDocumentProperties & {
      form_fields?: any[]
    }

    if (props.form_fields) {
      const formField = props.form_fields.find((f: any) => f.uuid === fieldId)
      return formField?.value ?? ''
    }

    return ''
  }

  const { validateAllFields, clearErrors, validationErrors } =
    usePdfFormValidation(getAttachmentFieldValue)

  // Store validation error count per document (docId -> error count)
  const pdfFormDocumentValidationState = ref<Map<string, number>>(new Map())

  const documentFormState = ref<Map<string, DocumentFormState>>(new Map())

  const getOrCreateDocumentState = (docId: string): DocumentFormState => {
    if (!documentFormState.value.has(docId)) {
      documentFormState.value.set(docId, {
        fieldValues: new Map(),
        validationErrors: new Map()
      })
    }
    return documentFormState.value.get(docId)!
  }

  const validatePdfFormsAttachments = () => {
    pdfFormDocumentValidationState.value.clear()

    emailAttachments.value.forEach((attachment) => {
      if (attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
        const formProps = attachment.documentProperties as FormDocumentProperties & {
          template_type?: string
          form_fields?: any[]
        }

        if (formProps.template_type === 'pdf_form' && formProps.form_fields) {
          const requiredFields = formProps.form_fields.filter(
            (f: any) => f.required && f.assigned_to === 'lawyer'
          )

          currentValidatingDocId.value = formProps.id

          clearErrors()

          validateAllFields(requiredFields)

          // Store error count for this document
          const errorCount = validationErrors.value.size
          pdfFormDocumentValidationState.value.set(formProps.id, errorCount)
        }
      }
    })

    currentValidatingDocId.value = ''
  }

  const attachmentHasUnfilledRequiredFields = (attachment: AttachedDocument): boolean => {
    if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
      return false
    }

    const formProps = attachment.documentProperties as FormDocumentProperties & {
      template_type?: string
      form_fields?: any[]
    }

    // For PDF forms with form_fields, check validation state
    if (formProps.template_type === 'pdf_form' && formProps.form_fields) {
      const errorCount = pdfFormDocumentValidationState.value.get(formProps.id) ?? 0
      return errorCount > 0
    }

    // For legacy forms, use the existing PDF store check
    const pdfState = newPdfStore.selectedPdfs.get(formProps.id)
    return pdfState?.hasLawfirmUnfilledRequiredFields ?? false
  }

  const getAttachmentValidationErrorCount = (attachment: AttachedDocument): number => {
    if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
      return 0
    }

    const formProps = attachment.documentProperties as FormDocumentProperties & {
      template_type?: string
      form_fields?: any[]
    }

    // For PDF forms with form_fields, return error count
    if (formProps.template_type === 'pdf_form' && formProps.form_fields) {
      const errorCount = pdfFormDocumentValidationState.value.get(formProps.id) ?? 0
      return errorCount
    }

    return 0
  }

  const missingSignaturesMap = ref<Record<string, boolean>>({})

  const hasMissingSignatures = computed(() => {
    const pdfAttachments = emailAttachments.value.filter(
      (attachment) => attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE
    )

    return pdfAttachments.some((attachment) => {
      const formProps = attachment.documentProperties as FormDocumentProperties
      return newPdfStore.hasLawfirmMissingSignatures(formProps.id)
    })
  })

  const attachmentHasMissingSignatures = (attachment: AttachedDocument): boolean => {
    if (attachment.documentType.id !== DocumentTypeIdEnum.PDF_TEMPLATE) {
      return false
    }

    const formProps = attachment.documentProperties as FormDocumentProperties
    return newPdfStore.hasLawfirmMissingSignatures(formProps.id)
  }

  // Function to update signature validation for all attachments
  const updateSignatureValidation = async () => {
    const pdfAttachments = emailAttachments.value.filter(
      (attachment) => attachment.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE
    )

    const promises = pdfAttachments.map(async (attachment) => {
      const formProps = attachment.documentProperties as FormDocumentProperties
      const hasMissing = await signatureStore.hasMissingSignatures(formProps.id)
      return { formId: formProps.id, hasMissing }
    })

    const results = await Promise.all(promises)
    const newMap: Record<string, boolean> = {}
    results.forEach(({ formId, hasMissing }) => {
      newMap[formId] = hasMissing
    })

    missingSignaturesMap.value = newMap
  }

  // Mark the widget as edited when the user modifies email text after AI pre-fill
  watch(emailSubject, () => {
    if (isAiPreloaded.value) aiWidget.markEdited()
  })
  watch(emailContent, () => {
    if (isAiPreloaded.value) aiWidget.markEdited()
  })

  // Watch for selectedEmailTemplate changes to initialize attachments
  watch(
    () => selectedEmailTemplate.value,
    (template) => {
      if (template) {
        initializeAttachmentsFromTemplate(template)
        updateSignatureValidation()
      }
    },
    { immediate: true }
  )

  // Handle WebSocket messages for email delivery events
  watch(
    () => caseStore.caseWebSocket.lastMessage,
    async (message) => {
      if (!message || !caseRecord.value?.uuid) return
      console.log('📧 Email Delivery Event received:', message)
      // Handle email delivery event messages
      if (
        message.type === 'case_email_delivery_event' &&
        message.data?.case_uuid === caseRecord.value.uuid &&
        message.data?.email_uuid
      ) {
        console.log('📧 Email Delivery Event received:', {
          emailUuid: message.data.email_uuid,
          deliveryEvent: message.data.delivery_event_event,
          caseUuid: message.data.case_uuid
        })

        // Remove email from pending state since we received a delivery event
        if (message.data.email_uuid && pendingEmailUuids.value.has(message.data.email_uuid)) {
          pendingEmailUuids.value.delete(message.data.email_uuid)
          console.log('⏰ Removed email from pending state:', message.data.email_uuid)
        }

        // Refetch all emails to get the latest delivery status
        // Note: We refetch all emails because the API doesn't support retrieving individual emails by UUID
        try {
          await fetchEmails()
          console.log('✅ Email list refreshed after delivery event:', {
            emailUuid: message.data.email_uuid,
            deliveryEvent: message.data.delivery_event_event
          })
        } catch (error) {
          console.error('❌ Error refreshing emails after delivery event:', error)
        }
      }
    },
    { immediate: false }
  )

  // Handle WebSocket messages for agent activity events
  watch(
    () => caseStore.caseWebSocket.lastMessage,
    async (message) => {
      if (!message || !caseRecord.value?.uuid) return

      // Handle agent activity messages - reload AI suggestions when agent finishes
      if (
        message.type === 'case_agent_activity' &&
        message.data?.case_uuid === caseRecord.value.uuid &&
        message.data?.status === 'FINISHED'
      ) {
        console.log('🤖 Agent Activity Finished:', {
          activityType: message.data.activity_type,
          caseUuid: message.data.case_uuid
        })

        // Fetch new AI suggested email actions
        await fetchAiSuggestedEmailAction()
      }
    },
    { immediate: false }
  )

  watch(
    () => caseRecord.value?.data?.law?.field,
    (field) => {
      selectedLawField.value = field || ''
    },
    { immediate: true }
  )

  watch(
    () => caseRecord.value?.data?.law?.topic,
    (topic) => {
      selectedLawTopic.value = topic || ''
    },
    { immediate: true }
  )

  watch(
    () => (caseRecord.value as any)?.clerks,
    (clerks) => {
      selectedClerks.value = (clerks || []).map((c: any) => c.uuid)
    },
    { immediate: true }
  )

  watch(
    caseRecord,
    (caseRecord) => {
      if (caseRecord?.data) {
        const client = caseRecord.data.client
        if (client) {
          newPdfStore.setPrefillData({
            client_first_name: client.first_name ?? '',
            client_last_name: client.last_name ?? '',
            client_email: client.email ?? '',
            client_phone: client.phone ?? '',
            probably_client_city: client.city ?? '',
            probably_client_zip: client.zip ?? '',
            probably_client_street: client.street ?? '',
            probably_client_street_number: client.street_number ?? '',
            probably_client_date_of_birth: client.date_of_birth ?? ''
          })
        }
      }
    },
    { immediate: true }
  )

  watch(
    () => signatureStore.signatures,
    () => {
      updateSignatureValidation()
    },
    { deep: true }
  )

  onMounted(async () => {
    const caseUuid = router.currentRoute.value.params.uuid as string

    try {
      await refreshCaseRecord(caseUuid)
      updateLastSeenAt(caseUuid)

      // Check the multiple opponents flag before rendering to avoid layout flash
      await multipleOpponentsFlag.checkFlag()
      multipleOpponentsFlagChecked.value = true

      isInitializing.value = false

      // Load everything else in the background (non-blocking)
      Promise.all([
        userStore.fetchUser(),
        fetchLawFields(),
        fetchEmailTemplates(),
        checkAiFeatureFlags(),
        officeEditingFlag.checkFlag(),
        officePreviewFlag.checkFlag()
      ]).then(() => {
        fetchEmails()
        updateSignatureValidation()
        // Fetch AI suggested email action after everything is loaded
        fetchAiSuggestedEmailAction()
      })
    } catch (error) {
      console.error('Error loading case:', error)
      isInitializing.value = false
    }
  })

  // Dev-only: Ctrl+Shift+A triggers a mock AI email action for testing the widget flow
  if (import.meta.env.DEV) {
    const handleDevTrigger = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault()
        // Set a mock action so the launcher appears and widget is functional
        const mockAction = {
          uuid: 'dev-mock-action',
          action_type: 'SEND_CLIENT_TEMPLATE_EMAIL',
          payload: JSON.stringify({
            template_uuid: 'mock-template',
            questionnaire_uuids: [],
            pdf_form_uuids: []
          }),
          reasoning_translation:
            'Für diesen Fall habe ich eine E-Mail aus der Vorlage Mandatsaufnahme Arbeitsrecht vorbereitet. Angehängt sind der Kündigungs-Fragebogen sowie das PDF-Formular Vollmacht.',
          is_committed: false,
          is_reverted: false,
          is_commitable: true,
          delivery_mode: 'RECOMMENDATION'
        } as any
        aiWidget.currentAction.value = mockAction
        aiWidget.pendingActions.value = [mockAction]
        isAiAssistantEnabled.value = true
        aiWidget.isAiPreloaded.value = true
        aiWidget.isOpen.value = true
        aiWidget.step.value = 'summary'
        console.log('[DEV] Mock AI email action triggered — widget opened')
      }
    }
    onMounted(() => window.addEventListener('keydown', handleDevTrigger))
    onUnmounted(() => window.removeEventListener('keydown', handleDevTrigger))
  }

  // Clean up on unmount
  onUnmounted(() => {
    pendingEmailUuids.value.clear()
    aiWidget.close()
  })
</script>

<template>
  <SpaCompatComponent>
    <div
      v-if="caseRecord && !isInitializing"
      class="container px-0"
    >
      <LegacyCaseHeader />
      <div class="row pb-3">
        <div class="flex flex-row flex-wrap gap-3">
          <div class="mb-3 flex-1">
            <div class="form-group">
              <SelectField
                v-model="selectedLawField"
                name="lawField"
                :options="lawFieldOptions"
                :label="t('lawField')"
                @update:model-value="
                  (value) => handleCaseInfoSave({ lawField: value, lawTopic: selectedLawTopic })
                "
              />
            </div>
          </div>
          <div class="mb-3 flex-1">
            <div class="form-group">
              <SelectField
                v-model="selectedLawTopic"
                name="lawTopic"
                :options="lawTopicOptions"
                :label="t('lawTopic')"
                @update:model-value="
                  (value) => handleCaseInfoSave({ lawTopic: value, lawField: selectedLawField })
                "
              />
            </div>
          </div>
          <div class="mb-3 flex-1">
            <div class="form-group">
              <MultiSelectField
                v-model="selectedClerks"
                name="clerks"
                :options="clerkOptions"
                :label="t('clerk')"
                :show-avatars="true"
                @update:model-value="(value) => handleCaseInfoSave({ clerks: value })"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="row pb-3">
        <div class="col-12 col-lg-4 mt-lg-0">
          <div
            class="card"
            data-cy="clientCard"
          >
            <h3
              id="headingTwo"
              class="accordion-button mb-0 bg-white"
              :class="{
                'text-base font-medium text-neutral-800':
                  multipleOpponentsFlag.isEnabled.value && user?.org?.enable_law_specific_data
              }"
              aria-controls="clientCard"
              aria-expanded="true"
              data-bs-target="#clientCard"
              data-bs-toggle="collapse"
              data-cy="clientCardHeader"
            >
              {{ t('client') }}
            </h3>
            <div
              id="clientCard"
              class="card-body show collapse"
              aria-labelledby="headingOne"
            >
              <LegacyEntityForm fieldPrefix="client" />
            </div>
          </div>
          <!-- Multiple Opponents (when feature flag enabled and law specific data enabled) -->
          <LegacyOpponentCards
            v-if="
              multipleOpponentsFlagChecked &&
              multipleOpponentsFlag.isEnabled.value &&
              user?.org?.enable_law_specific_data &&
              caseRecord?.data?.entities
            "
            class="mt-3"
          />
          <div
            v-else-if="multipleOpponentsFlagChecked"
            class="card mt-3"
            data-cy="opponentCard"
          >
            <h3
              id="headingThree"
              class="accordion-button collapsed mb-0 bg-white"
              aria-controls="opponentCard"
              aria-expanded="false"
              data-bs-target="#opponentCard"
              data-bs-toggle="collapse"
              data-cy="opponentCardHeader"
            >
              {{ t('opponent') }}
            </h3>
            <div
              id="opponentCard"
              class="card-body collapse"
              aria-labelledby="headingThree"
            >
              <LegacyEntityForm fieldPrefix="opponent" />
            </div>
          </div>
          <div
            class="card mt-3"
            data-cy="legalInsuranceCard"
          >
            <div class="card-body">
              <div>
                <AutoSavingForm
                  :debounceDelay="1500"
                  :throttleInterval="2000"
                  @save="handleInsuranceInfoSave"
                >
                  <div
                    class="form-group mb-3 flex flex-col"
                    style="gap: 0.5rem"
                  >
                    <CheckboxField
                      v-model="hasLegalInsurance"
                      name="has_legal_insurance"
                      data-cy="checkbox-has_legal_insurance"
                      :label="t('legalInsurance')"
                    />
                    <InputField
                      v-if="hasLegalInsurance"
                      v-model="legalInsuranceName"
                      name="legal_insurance_name"
                      data-cy="input-legal_insurance_name"
                      :label="t('legalInsuranceName')"
                    />
                    <InputField
                      v-if="hasLegalInsurance"
                      v-model="legalInsuranceNumber"
                      name="legal_insurance_number"
                      data-cy="input-legal_insurance_number"
                      :label="t('legalInsuranceNumber')"
                    />
                  </div>
                </AutoSavingForm>
                <!-- Additional client insurance content would go here -->
              </div>
            </div>
          </div>
          <div
            class="card mt-3"
            data-cy="internalNotesCard"
          >
            <div class="card-body">
              <AutoSavingForm
                ref="notesFormRef"
                :debounceDelay="1500"
                :throttleInterval="2000"
                @save="handleNotesSave"
              >
                <div class="mb-3">
                  <div class="form-group">
                    <TextAreaField
                      v-model="internalNotes"
                      name="notes"
                      data-cy="textarea-notes"
                      :label="t('internalNotes')"
                      :placeholder="t('notesPlaceholder')"
                      rows="10"
                      @focus="() => notesFormRef?.handleFieldTouch('notes')"
                    />
                  </div>
                </div>
              </AutoSavingForm>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-8">
          <AgentActionWidget
            v-if="caseRecord && isAgentActionWidgetEnabled"
            ref="agentActionWidgetRef"
            :case-uuid="caseRecord.uuid"
            @email-sent="fetchEmails(false)"
          />
          <div class="flex-column flex gap-3">
            <!-- Inhalt der Anfrage Card -->
            <div
              class="card mt-3 lg:mt-0"
              data-cy="inquiryCard"
            >
              <div class="row">
                <div class="col-12">
                  <div class="card-header block">
                    <div class="row">
                      <div class="col-6">
                        <h3 class="card-title pt-1">{{ t('inquiry') }}</h3>
                      </div>
                      <div class="col-6">
                        <Button
                          v-if="
                            caseRecord.org.ai_enabled && !inquirySummary && !isGeneratingSummary
                          "
                          class="gradient-hover float-end"
                          variant="secondary"
                          @click="summarizeInquiryWithAI"
                        >
                          🪄
                          {{ t('summarizeWithAI') }}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div
                  v-if="caseRecord?.inquiry"
                  v-html="caseRecord.inquiry"
                ></div>
              </div>
            </div>
            <!-- Summary of Inquiry -->
            <div
              v-if="inquirySummary || isGeneratingSummary"
              class="card mt-3 lg:mt-0"
              data-cy="inquirySummaryCard"
            >
              <div class="row">
                <div class="col-12">
                  <div class="card-header block">
                    <div class="row">
                      <div class="col-6">
                        <h3 class="gradient card-title pt-1">{{ t('summaryOfInquiry') }}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <div class="space-y-3">
                  <div
                    v-if="isGeneratingSummary"
                    class="flex justify-center"
                  >
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>
                  <div
                    class="leading-loose"
                    v-html="inquirySummary"
                  ></div>
                </div>
              </div>
            </div>
            <!-- Termine Card -->
            <div
              v-if="user?.org?.enable_appointments"
              class="card"
              data-cy="appointmentsCard"
            >
              <div class="row">
                <div class="col-12">
                  <div class="card-header block">
                    <div class="row">
                      <div class="col-6">
                        <h3 class="card-title pt-1">{{ t('appointments') }}</h3>
                      </div>
                      <div class="col-6">
                        <span class="float-end">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger as-child>
                                <span>
                                  <Button
                                    variant="secondary"
                                    data-cy="createAppointmentBtn"
                                    :disabled="!hasClientInfo"
                                    @click="isAppointmentCreateModalOpen = true"
                                  >
                                    <CalendarPlusIcon class="me-2 h-4 w-4" />
                                    {{ t('createAppointment') }}
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent v-if="!hasClientInfo">
                                <p>{{ t('noClientInfoWarning') }}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive mb-0">
                      <table class="table-vcenter card-table table">
                        <tbody>
                          <!-- Appointment Proposals Section -->
                          <template
                            v-for="proposal in caseStore.appointmentProposals"
                            :key="proposal.uuid"
                          >
                            <tr
                              v-if="!proposal.booked"
                              class="bg-gray-50"
                              data-cy="appointmentProposalRow"
                            >
                              <td class="w-1/3">
                                <div class="flex items-center py-1">
                                  <div class="flex-1">
                                    <div class="flex flex-col text-xs font-medium">
                                      <div
                                        class="f"
                                        data-cy="proposalStatus"
                                      >
                                        {{ t('bookingLinkSent') }}
                                      </div>
                                      <div data-cy="proposalDate">
                                        <PencilIcon class="mr-1 inline-block h-3 w-3" />
                                        {{ proposal.start ? formatDate(proposal.start) : '' }}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td class="w-1/3">
                                <div class="flex items-center py-1">
                                  <div class="flex-1">
                                    <div
                                      class="text-xs text-black"
                                      data-cy="proposalCalendar"
                                    >
                                      <template v-if="proposal.calendar">
                                        <UserIcon class="mr-1 inline-block h-3.5 w-3.5" />
                                        {{ proposal.calendar.name }}
                                      </template>
                                    </div>
                                    <div
                                      class="whitespace-nowrap text-xs font-medium"
                                      data-cy="proposalService"
                                    >
                                      <InfoIcon class="mr-1 inline-block h-3.5 w-3.5" />
                                      {{ proposal.service?.name || t('noService') }}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td class="w-1/3">
                                <div class="flex items-center py-1">
                                  <div class="flex flex-1 items-center justify-between gap-3">
                                    <div
                                      class="font-medium"
                                      data-cy="proposalLocationType"
                                    >
                                      <MapPinIcon class="mr-1 inline-block h-4 w-4" />
                                      <span v-if="proposal.location_type === 'TELEPHONE'">
                                        {{ t('telephoneAppointment') || 'Telefon-Termin' }}
                                      </span>
                                      <span v-if="proposal.location_type === 'ONLINE'">
                                        {{ t('online') || 'Online-Termin' }}
                                      </span>
                                      <span v-if="proposal.location_type === 'IN_PERSON'">
                                        {{ t('inPerson') || 'Termin vor Ort' }}
                                      </span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      class="mr-2 text-xs text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="deleteAppointmentProposal(proposal)"
                                    >
                                      {{ t('delete') || 'Löschen' }}
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </template>

                          <!-- Appointments Section -->
                          <tr
                            v-for="appointment in appointments"
                            :key="appointment.uuid"
                            class="hover:bg-gray-50"
                            data-cy="appointmentRow"
                          >
                            <td class="w-1/3">
                              <div class="flex items-center py-1">
                                <div class="flex-1">
                                  <div class="text-[15px] font-medium">
                                    <Calendar1Icon class="mr-1 inline-block h-4 w-4" />
                                    {{ formatDate(appointment.start) }}
                                  </div>
                                  <div class="text-[15px] font-medium">
                                    <Clock5Icon class="mr-1 inline-block h-4 w-4" />
                                    {{ formatTime(appointment.start, appointment.end) }}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td class="w-1/3">
                              <div class="flex items-center py-1">
                                <div class="flex-1">
                                  <div
                                    class="text-[15px] font-medium"
                                    data-cy="appointmentCalendar"
                                  >
                                    <UserIcon class="mr-1 inline-block h-4 w-4" />
                                    {{ appointment.calendar?.name || t('unknown') }}
                                  </div>
                                  <div
                                    class="text-[15px] font-medium"
                                    data-cy="appointmentService"
                                  >
                                    <InfoIcon class="mr-1 inline-block h-4 w-4" />
                                    {{ appointment.service?.name || t('noService') }}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td class="w-1/3">
                              <div class="flex items-center py-1">
                                <div class="flex flex-1 items-center justify-between gap-3">
                                  <div>
                                    <div
                                      class="text-sm font-medium"
                                      data-cy="appointmentLocationType"
                                    >
                                      <MapPinIcon class="mr-1 inline-block h-4 w-4" />
                                      {{
                                        appointment.location_types === 'ONLINE'
                                          ? t('online')
                                          : appointment.location_types === 'TELEPHONE'
                                            ? t('telephoneAppointment')
                                            : t('inPerson')
                                      }}
                                    </div>
                                    <div class="mt-1 flex items-center gap-1">
                                      <CalendarIcon class="h-4 w-4" />
                                      <span
                                        class="rounded-sm px-1 py-[3px] text-[10px] font-semibold text-white"
                                        :class="
                                          appointment.status === 'CONFIRMED'
                                            ? 'bg-[#009b72]'
                                            : appointment.status === 'CANCELLED'
                                              ? 'bg-[#dc3545]'
                                              : 'bg-gray-500'
                                        "
                                        data-cy="appointmentStatus"
                                      >
                                        {{ getAppointmentStatusText(appointment) }}
                                      </span>
                                      <TooltipProvider v-if="appointment.cronofy_synced === false">
                                        <Tooltip>
                                          <TooltipTrigger as-child>
                                            <span>
                                              <AlertTriangleIcon
                                                class="h-5 w-5 text-red-600"
                                                data-cy="appointmentNotSyncedIcon"
                                              />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{{ t('calendarNotSyncedWarning') }}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </div>
                                  </div>
                                  <div
                                    v-if="new Date(appointment.start) > new Date()"
                                    class="flex gap-2"
                                  >
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.TENTATIVE"
                                      variant="secondary"
                                      class="text-green-600 hover:bg-green-600 hover:text-white"
                                      @click="confirmAppointmentBooking(appointment)"
                                    >
                                      {{ t('confirmAppointment') }}
                                    </Button>
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.TENTATIVE"
                                      variant="secondary"
                                      class="text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="cancelAppointmentBooking(appointment)"
                                    >
                                      <CircleXIcon class="h-4 w-4" />
                                    </Button>
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.CONFIRMED"
                                      variant="secondary"
                                      class="text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="cancelAppointmentBooking(appointment)"
                                    >
                                      {{ t('cancelAppointment') }}
                                    </Button>
                                    <Button
                                      v-if="appointment.status === AppointmentStatusEnum.CANCELLED"
                                      variant="secondary"
                                      class="text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="deleteAppointmentBooking(appointment)"
                                    >
                                      {{ t('delete') }}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                          <tr v-if="!appointments || appointments.length === 0">
                            <td
                              colspan="4"
                              class="py-4 text-center text-gray-500"
                              data-cy="noAppointmentsHint"
                            >
                              {{ t('noAppointments') || 'Keine Termine vorhanden' }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Received Documents Card -->
            <div
              id="received-documents"
              class="card"
              style="z-index: 0"
            >
              <Accordion
                type="single"
                collapsible
                class="w-full"
                defaultValue="received-documents"
              >
                <AccordionItem value="received-documents">
                  <AccordionHeader class="card-header flex grow">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="card-title pe-2">
                          {{ t('receivedDocuments') }}
                          <span class="badge bg-danger">{{ receivedDocuments.length }}</span>
                        </h3>
                      </div>
                      <div class="flex items-center">
                        <a
                          :href="`/app/cases/${caseRecord?.uuid}/documents`"
                          class="documents-edit-link me-3"
                        >
                          <span class="link-text">Dokumente bearbeiten</span>
                        </a>
                        <DropdownMenu>
                          <DropdownMenuTrigger as-child>
                            <Button
                              variant="secondary"
                              class="me-2 flex h-8 w-12"
                              @click.stop
                            >
                              <EllipsisVerticalIcon class="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            class="w-[180px]"
                            :align="'end'"
                          >
                            <DropdownMenuItem
                              class="cursor-pointer"
                              @click="isDocumentUploadModalOpen = true"
                            >
                              {{ t('addDocument') }}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent class="px-4 pb-4">
                    <!-- No Documents State -->
                    <div
                      v-if="receivedDocuments.length === 0"
                      class="p-3 pb-0"
                      data-cy="noDocsReceivedHint"
                    >
                      <em class="small">{{ t('noDocumentsReceived') }}</em>
                    </div>

                    <!-- Documents List -->
                    <table
                      v-else
                      class="table-vcenter card-table table table-fixed"
                    >
                      <tbody>
                        <tr>
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              {{ t('sortBy') || 'Sortieren nach:' }}
                            </div>
                          </td>
                          <td></td>
                          <td class="w-1/4">
                            <SelectField
                              v-model="receivedDocsSortBy"
                              name="sortBy"
                              :options="[
                                { value: 'name', title: t('name') },
                                { value: 'date', title: t('date') }
                              ]"
                            ></SelectField>
                          </td>
                        </tr>
                        <tr
                          v-for="document in receivedDocuments"
                          :key="document.uuid"
                          class="hover:bg-gray-50"
                          data-cy="receivedDocumentRow"
                        >
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              <div
                                class="avatar me-2 h-10 w-10 overflow-hidden rounded border border-gray-100 bg-white"
                              >
                                <img
                                  v-if="getThumbnailUrl(document)"
                                  :src="getThumbnailUrl(document)"
                                  alt="Document thumbnail"
                                  class="h-full w-full object-cover"
                                />
                                <div
                                  v-else
                                  class="flex h-full w-full items-center justify-center bg-gray-50"
                                >
                                  <FileIcon class="h-5 w-5 text-gray-400" />
                                </div>
                              </div>
                              <div class="min-w-0 flex-1">
                                <div
                                  :class="[
                                    'truncate font-medium',
                                    isOfficeFile(document)
                                      ? 'cursor-pointer text-blue-600 hover:text-blue-800 hover:underline'
                                      : 'cursor-pointer'
                                  ]"
                                  :title="
                                    isOfficeFile(document)
                                      ? `${document.filename} - Click to edit in office editor`
                                      : document.filename
                                  "
                                  data-cy="receivedDocumentTitle"
                                  @click="openDocument(document)"
                                >
                                  {{ document.filename.replace('.pdf', '') }}
                                </div>
                                <div class="truncate text-sm text-gray-500">
                                  {{ document.filename }}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4">
                            <div class="flex flex-col">
                              <div
                                :class="`badge w-fit ${getDocumentBadgeColor(document.document_type)}`"
                                data-cy="receivedDocumentStatus"
                              >
                                {{
                                  document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION
                                    ? 'Dokumentenvorlage'
                                    : document.document_type === DocumentTypeEnum.UPLOAD
                                      ? (document.upload_origin as string) ||
                                        (document.document_type as string)
                                      : (document.document_type as string)
                                }}
                              </div>
                              <div class="mt-1 text-sm text-gray-500">
                                {{ formatDocumentDate(document.created_at) }}
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4 text-right">
                            <div class="flex justify-end gap-2">
                              <Button
                                v-if="isEditableDocument(document)"
                                variant="secondary"
                                :title="t('edit') || 'Bearbeiten'"
                                @click="openOfficeFile(document)"
                              >
                                <PencilIcon class="h-4 w-4" />
                              </Button>
                              <Button
                                v-if="isOfficeFile(document)"
                                variant="secondary"
                                :title="t('preview') || 'Vorschau'"
                                @click="previewOfficeDocument(document)"
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <Button
                                v-else
                                variant="secondary"
                                :title="t('preview') || 'Vorschau'"
                                @click="previewDocument(document)"
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <Button
                                variant="secondary"
                                :title="t('download') || 'Herunterladen'"
                                @click="downloadDocument(document)"
                              >
                                <CloudDownloadIcon class="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <!-- Requested Documents Card -->
            <div
              id="requested-documents"
              class="card"
              style="z-index: 0"
            >
              <Accordion
                type="single"
                collapsible
                class="w-full"
                defaultValue="requested-documents"
              >
                <AccordionItem value="requested-documents">
                  <AccordionHeader class="card-header flex grow">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="card-title pe-2">
                          {{ t('requestedDocuments') }}
                          <span class="badge bg-danger">{{ requestedDocuments.length }}</span>
                        </h3>
                      </div>
                      <TooltipProvider
                        v-if="
                          requestedDocuments.length && caseRecord.org.send_document_email_reminders
                        "
                        class="flex-shrink-0"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              class="mx-4 flex items-center justify-end gap-1.5 rounded border border-purple-200 bg-purple-50 px-2 py-1
                                text-xs text-purple-700 dark:bg-purple-950 dark:text-purple-300"
                            >
                              <div class="flex items-center gap-2">
                                <span class="text-nowrap text-xs font-medium">
                                  {{ t('enableEmailReminder') }}
                                </span>
                                <template v-if="loadingChangeEmailReminderStatus">
                                  <span
                                    class="spinner-border spinner-border-sm"
                                    aria-hidden="true"
                                    role="status"
                                  ></span>
                                </template>
                                <Switch
                                  v-model:checked="isEmailReminderForOutstandingDocumentsEnabled"
                                  class="data-[state=checked]:bg-purple-700 data-[state=unchecked]:bg-gray-200"
                                  thumb-class=""
                                  :disabled="loadingChangeEmailReminderStatus"
                                  @click.stop
                                />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent class="max-w-xs border-black bg-black text-white">
                            <p class="text-white">{{ t('enableEmailReminderTitle') }}</p>
                            <p class="whitespace-normal text-xs text-gray-300">
                              {{ t('enableEmailReminderDescription') }}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div class="flex items-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger as-child>
                            <Button
                              variant="secondary"
                              class="me-2 flex h-8 w-12"
                              @click.stop
                            >
                              <EllipsisVerticalIcon class="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            class="w-[320px]"
                            :align="'end'"
                          >
                            <DropdownMenuItem
                              class="flex cursor-pointer items-center gap-2 whitespace-nowrap"
                              @click="handleGenerateQuestionnaireLink"
                            >
                              <div class="h-5 w-5 flex-shrink-0 rounded bg-[#17a2b8]"></div>
                              {{ t('createLinkToQuestionnaire') }}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              class="flex cursor-pointer items-center gap-2 whitespace-nowrap"
                              @click="handleGenerateUnifiedTemplateLink"
                            >
                              <div class="h-5 w-5 flex-shrink-0 rounded bg-[#dc3545]"></div>
                              {{ t('createLinkToDocumentTemplate') }}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              v-if="requestedDocuments.length > 0"
                              class="flex cursor-pointer items-center gap-2 whitespace-nowrap border-t"
                              @click="copyClientPortalLink"
                            >
                              <Link2 class="h-5 w-5 flex-shrink-0" />
                              {{ t('copyLinkToClientPortal') }}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent class="px-4 pb-4">
                    <!-- Loading State -->
                    <div
                      v-if="isLoadingDocuments"
                      class="flex justify-center py-6"
                    >
                      <div
                        class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
                      ></div>
                    </div>

                    <!-- No Documents State -->
                    <div
                      v-else-if="requestedDocuments.length === 0"
                      class="p-3 pb-0"
                      data-cy="noRequestedDocsHint"
                    >
                      <em class="small">{{ t('noRequestedDocuments') }}</em>
                    </div>

                    <!-- Documents List -->
                    <table
                      v-else
                      class="table-vcenter card-table table"
                    >
                      <tbody>
                        <tr>
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              {{ t('sortBy') || 'Sortieren nach:' }}
                            </div>
                          </td>
                          <td></td>
                          <td class="w-1/4">
                            <SelectField
                              v-model="requestedDocsSortBy"
                              name="sortBy"
                              :options="[
                                { value: 'name', title: t('name') },
                                { value: 'date', title: t('date') }
                              ]"
                            ></SelectField>
                          </td>
                        </tr>
                        <tr
                          v-for="document in requestedDocuments"
                          :key="document.uuid"
                          class="hover:bg-gray-50"
                          data-cy="requestedDocumentRow"
                        >
                          <td class="w-1/2">
                            <div class="flex items-center py-1">
                              <div
                                class="avatar me-2 h-10 w-10 overflow-hidden rounded border border-gray-100 bg-white"
                              >
                                <img
                                  v-if="getThumbnailUrl(document)"
                                  :src="getThumbnailUrl(document)"
                                  alt="Document thumbnail"
                                  class="h-full w-full object-cover"
                                />
                                <div
                                  v-else
                                  class="flex h-full w-full items-center justify-center bg-gray-50"
                                >
                                  <FileIcon class="h-5 w-5 text-gray-400" />
                                </div>
                              </div>
                              <div class="min-w-0 flex-1">
                                <div
                                  :class="[
                                    'truncate font-medium',
                                    isOfficeFile(document)
                                      ? 'cursor-pointer text-blue-600 hover:text-blue-800 hover:underline'
                                      : 'cursor-pointer'
                                  ]"
                                  :title="
                                    isOfficeFile(document)
                                      ? `${document.filename} - Click to edit in office editor`
                                      : document.filename
                                  "
                                  data-cy="requestedDocumentTitle"
                                  @click="
                                    document.document_type === DocumentTypeEnum.FRAGEBOGEN
                                      ? openQuestionnaireSubmissionByDocumentUuid(
                                          document.document_uuid
                                        )
                                      : openDocument(document)
                                  "
                                >
                                  {{ document.filename.replace('.pdf', '') }}
                                </div>
                                <div class="truncate text-sm text-gray-500">
                                  {{ document.filename }}
                                </div>
                                <!-- Question count with progress bar for questionnaires -->
                                <div v-if="document.document_type === DocumentTypeEnum.FRAGEBOGEN">
                                  <div class="mt-2 flex items-center">
                                    <div class="w-10/12">
                                      <div
                                        class="h-2 w-full overflow-hidden rounded-full bg-gray-200"
                                      >
                                        <div
                                          class="h-full bg-blue-500"
                                          role="progressbar"
                                          :style="`width: ${(document as RequestedQuestionnaire).answered_questions_percent || 0}%`"
                                          :aria-valuenow="
                                            (document as RequestedQuestionnaire)
                                              .answered_questions_percent || 0
                                          "
                                          aria-valuemin="0"
                                          aria-valuemax="100"
                                        ></div>
                                      </div>
                                    </div>
                                    <div class="w-2/12 text-right">
                                      <span class="text-xs text-gray-500">
                                        {{
                                          (document as RequestedQuestionnaire)
                                            .answered_question_count || 0
                                        }}/{{
                                          (document as RequestedQuestionnaire).question_count || 0
                                        }}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4">
                            <div class="flex flex-col">
                              <div
                                :class="`badge w-fit ${getDocumentBadgeColor(document.document_type)}`"
                                data-cy="requestedDocumentStatus"
                              >
                                {{
                                  document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION
                                    ? 'Dokumentenvorlage'
                                    : document.document_type === DocumentTypeEnum.UPLOAD
                                      ? ((document as PdfDocument).upload_origin as string)
                                      : (document.document_type as string)
                                }}
                              </div>
                              <div class="mt-1 text-sm text-gray-500">
                                {{ formatDocumentDate(document.created_at) }}
                              </div>
                            </div>
                          </td>
                          <td class="w-1/4 text-right">
                            <div class="flex justify-end gap-2">
                              <Button
                                v-if="document.document_type === DocumentTypeEnum.FRAGEBOGEN"
                                variant="secondary"
                                :title="t('preview') || 'Ansehen'"
                                @click="
                                  openQuestionnaireSubmissionByDocumentUuid(document.document_uuid)
                                "
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <Button
                                v-else-if="isOfficeFile(document)"
                                variant="secondary"
                                :title="t('preview') || 'Vorschau'"
                                @click="previewOfficeDocument(document)"
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <Button
                                v-else
                                variant="secondary"
                                :title="t('preview') || 'Vorschau'"
                                @click="previewDocument(document)"
                              >
                                <Eye class="h-4 w-4" />
                              </Button>
                              <Button
                                v-if="isEditableDocument(document)"
                                variant="secondary"
                                :title="t('edit') || 'Bearbeiten'"
                                @click="openOfficeFile(document)"
                              >
                                <PencilIcon class="h-4 w-4" />
                              </Button>
                              <DropdownMenu
                                v-if="
                                  document.document_type === DocumentTypeEnum.FRAGEBOGEN ||
                                  document.document_type === DocumentTypeEnum.DOKUMENTENVORLAGE ||
                                  document.document_type === DocumentTypeIdEnum.PDF_FORM_SUBMISSION
                                "
                              >
                                <DropdownMenuTrigger>
                                  <Button variant="secondary">
                                    <EllipsisVerticalIcon class="h-4 w-5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent :align="'end'">
                                  <DropdownMenuItem
                                    v-if="document.document_type === DocumentTypeEnum.FRAGEBOGEN"
                                    class="border-b"
                                    @click="copyQuestionnaireLink(document)"
                                  >
                                    <Link2 class="mr-2 h-4 w-4" />
                                    {{ t('copyLink') }}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    v-if="
                                      document.document_type ===
                                        DocumentTypeEnum.DOKUMENTENVORLAGE ||
                                      document.document_type ===
                                        DocumentTypeIdEnum.PDF_FORM_SUBMISSION
                                    "
                                    @click="copyDocumentTemplateLink(document)"
                                  >
                                    <Link2 class="mr-2 h-4 w-4" />
                                    {{ t('copyLink') }}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    v-if="document.document_type === DocumentTypeEnum.FRAGEBOGEN"
                                    @click="handleCloseQuestionnaire(document)"
                                  >
                                    {{ t('closeQuestionnaire') }}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <div
              id="email"
              class="card"
              style="z-index: 0"
            >
              <Accordion
                type="single"
                collapsible
                class="w-full"
                defaultValue="email"
              >
                <AccordionItem value="email">
                  <AccordionHeader class="card-header flex grow">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="card-title pe-2">
                          {{ t('email') }}
                        </h3>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent
                    class="px-4 pb-4"
                    :overflow="'visible'"
                  >
                    <!-- Template Selection -->
                    <div class="mb-4">
                      <div
                        class="mt-4 flex w-full items-center overflow-hidden rounded-[7px] border border-gray-200"
                      >
                        <label
                          for="email-template"
                          class="m-0 h-10 cursor-default bg-gray-50 px-3 text-sm"
                        >
                          {{ t('selectTemplate') }}
                        </label>
                        <div class="relative grow">
                          <SelectField
                            v-model="selectedTemplateId"
                            name="email-template"
                            :options="templateOptions"
                            @update:model-value="handleTemplateSelect"
                          />
                          <!-- KI Badge inside select field -->
                          <div
                            v-if="isAiPreloaded && aiSuggestedAction"
                            class="pointer-events-none absolute right-10 top-1/2 -translate-y-1/2 rounded bg-purple-100 px-1.5 py-0.5
                              text-xs font-medium text-purple-600"
                          >
                            {{ t('aiLabel') }}
                          </div>
                        </div>
                      </div>

                      <!-- AI Suggestion Info Box - shown when AI suggestion is active -->
                      <div
                        v-if="isAiPreloaded && aiSuggestedAction"
                        class="mt-3 rounded border border-purple-300 bg-purple-50 px-3 py-2"
                      >
                        <div class="flex items-center justify-between gap-2">
                          <div class="flex-1">
                            <p class="text-sm leading-relaxed text-gray-600">
                              {{ t('aiSuggestedAction') }}
                            </p>
                          </div>
                          <!-- Thumbs up/down buttons -->
                          <div class="flex items-center gap-1.5">
                            <button
                              class="flex h-6 w-6 items-center justify-center rounded border shadow-sm transition-colors"
                              :class="{
                                'border-purple-600 bg-purple-600 text-white':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'positive',
                                'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
                                  !aiActionRatings[aiSuggestedAction.uuid] && !isSubmittingAiRating,
                                'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'negative'
                              }"
                              :disabled="
                                !!aiActionRatings[aiSuggestedAction.uuid] || isSubmittingAiRating
                              "
                              @click="handleAiPositiveRating(aiSuggestedAction.uuid)"
                            >
                              <RotateCw
                                v-if="
                                  isSubmittingAiRating && !aiActionRatings[aiSuggestedAction.uuid]
                                "
                                class="h-3.5 w-3.5 animate-spin"
                              />
                              <ThumbsUp
                                v-else
                                class="h-3.5 w-3.5"
                              />
                            </button>
                            <button
                              class="flex h-6 w-6 items-center justify-center rounded border shadow-sm transition-colors"
                              :class="{
                                'border-purple-600 bg-purple-600 text-white':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'negative',
                                'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
                                  !aiActionRatings[aiSuggestedAction.uuid],
                                'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'positive'
                              }"
                              :disabled="
                                !!aiActionRatings[aiSuggestedAction.uuid] || isSubmittingAiRating
                              "
                              @click="openAiNegativeRatingModal(aiSuggestedAction.uuid)"
                            >
                              <ThumbsDown class="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- AI had a different suggestion - shown when user deviated -->
                      <div
                        v-else-if="userDeviatedFromAi && aiSuggestedAction"
                        class="mt-3 rounded border border-gray-200 bg-gray-50 px-3 py-2"
                      >
                        <div class="flex items-center justify-between gap-4">
                          <div class="flex flex-1 items-center gap-2 text-sm text-gray-500">
                            <Sparkles class="h-3.5 w-3.5 flex-shrink-0 text-purple-500" />
                            <span>{{ aiSuggestionMessage }}</span>
                            <button
                              class="flex-shrink-0 text-purple-600 hover:text-purple-700 hover:underline"
                              @click="reapplyAiSuggestion"
                            >
                              {{ t('switchToAiSuggestion') }}
                            </button>
                          </div>
                          <!-- Thumbs up/down buttons -->
                          <div class="flex flex-shrink-0 items-center gap-1.5">
                            <button
                              class="flex h-6 w-6 items-center justify-center rounded border shadow-sm transition-colors"
                              :class="{
                                'border-purple-600 bg-purple-600 text-white':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'positive',
                                'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
                                  !aiActionRatings[aiSuggestedAction.uuid] && !isSubmittingAiRating,
                                'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'negative'
                              }"
                              :disabled="
                                !!aiActionRatings[aiSuggestedAction.uuid] || isSubmittingAiRating
                              "
                              @click="handleAiPositiveRating(aiSuggestedAction.uuid)"
                            >
                              <RotateCw
                                v-if="
                                  isSubmittingAiRating && !aiActionRatings[aiSuggestedAction.uuid]
                                "
                                class="h-3.5 w-3.5 animate-spin"
                              />
                              <ThumbsUp
                                v-else
                                class="h-3.5 w-3.5"
                              />
                            </button>
                            <button
                              class="flex h-6 w-6 items-center justify-center rounded border shadow-sm transition-colors"
                              :class="{
                                'border-purple-600 bg-purple-600 text-white':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'negative',
                                'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
                                  !aiActionRatings[aiSuggestedAction.uuid],
                                'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
                                  aiActionRatings[aiSuggestedAction.uuid] === 'positive'
                              }"
                              :disabled="
                                !!aiActionRatings[aiSuggestedAction.uuid] || isSubmittingAiRating
                              "
                              @click="openAiNegativeRatingModal(aiSuggestedAction.uuid)"
                            >
                              <ThumbsDown class="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Subject Field -->
                    <div class="relative mb-4">
                      <input
                        id="subject-input"
                        v-model="emailSubject"
                        class="w-full rounded-sm border border-gray-200 px-3 pb-2 pt-6 focus:outline-none focus:ring-2
                          focus:ring-purple-500 focus:ring-offset-2"
                        type="text"
                      />
                      <label
                        class="absolute -top-1 left-2 cursor-auto text-sm text-gray-500"
                        for="subject-input"
                      >
                        Betreff
                      </label>
                    </div>

                    <!-- Rich Text Editor Placeholder -->
                    <RichTextEditor
                      v-model="emailContent"
                      :enable-placeholder-button="true"
                      :enabled-context-aware-placeholders="['questionnaires_and_documents']"
                      :enable-text-styling-buttons="true"
                      :font-color="user?.org?.email_text_color"
                      :font-size="user?.org?.email_text_size"
                      :font-family="user?.org?.email_text_font"
                      :full-height="false"
                      class="min-h-[160px]"
                      data-cy="emailContent"
                    />

                    <!-- Attachments Section -->
                    <div
                      class="my-4 rounded-md border"
                      data-cy="emailAttachments"
                    >
                      <div class="flex items-center justify-between border-b p-3">
                        <h5 class="text-sm font-medium">
                          {{ emailAttachments.length }}
                          {{
                            t('documentsAndQuestionnaires') || 'Document Templates & Questionnaires'
                          }}
                        </h5>
                        <div
                          class="flex items-center space-x-2"
                          data-cy="attachmentsDropdown"
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              class="flex h-8 w-8 items-center justify-center rounded-md border border-input bg-background text-sm
                                shadow-sm hover:bg-accent"
                            >
                              +
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              :align="'end'"
                              class="w-[190px]"
                            >
                              <DropdownMenuItem
                                class="flex cursor-pointer items-center gap-2"
                                data-cy="attachQuestionnaire"
                                @click="handleAttachQuestionnaire"
                              >
                                <div class="h-5 w-5 rounded bg-[#17a2b8]"></div>
                                {{ t('questionnaire') || 'Questionnaire' }}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                class="flex cursor-pointer items-center gap-2"
                                data-cy="attachDocument"
                                @click="handleAttachDocumentTemplate"
                              >
                                <div class="h-5 w-5 rounded bg-[#dc3545]"></div>
                                {{ t('documentTemplate') }}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      <!-- AI Preloaded Indicator -->
                      <div
                        v-if="isAiPreloaded && aiSuggestedAction && emailAttachments.length > 0"
                        class="flex items-center gap-2 border-b bg-purple-50 px-3 py-2 text-xs text-purple-700"
                      >
                        <Sparkles class="h-3 w-3" />
                        <span>{{ t('aiPreloadedAttachments') }}</span>
                      </div>

                      <!-- Attachments Table -->
                      <div class="overflow-x-auto">
                        <table class="w-full">
                          <tbody>
                            <template v-if="emailAttachments.length === 0">
                              <tr class="border-b">
                                <td class="p-3 text-sm">
                                  <p class="text-muted-foreground">
                                    {{ t('noAttachments') || 'No attachments added yet.' }}
                                  </p>
                                </td>
                              </tr>
                            </template>
                            <template v-else>
                              <tr
                                v-for="attachment in emailAttachments"
                                :key="getAttachmentKey(attachment)"
                                class="border-b"
                                :class="{
                                  'bg-amber-50':
                                    attachmentHasUnfilledRequiredFields(attachment) ||
                                    attachmentHasMissingSignatures(attachment)
                                }"
                              >
                                <td class="w-1/2 p-3">
                                  <div class="flex items-center py-1">
                                    <div class="flex-1">
                                      <div class="font-medium">
                                        {{ formatAttachmentName(attachment) }}
                                      </div>
                                      <div
                                        v-if="attachmentHasUnfilledRequiredFields(attachment)"
                                        class="mt-1 flex items-center gap-1 text-sm text-amber-700"
                                      >
                                        <InfoIcon class="h-4 w-4" />
                                        <span
                                          v-if="getAttachmentValidationErrorCount(attachment) > 0"
                                        >
                                          {{ t('requiredFieldsNotFilled') }}
                                        </span>
                                        <span v-else>{{ t('requiredFieldsNotFilled') }}</span>
                                      </div>
                                      <div
                                        v-if="attachmentHasMissingSignatures(attachment)"
                                        class="mt-1 flex items-center gap-1 text-sm text-amber-700"
                                      >
                                        <InfoIcon class="h-4 w-4" />
                                        <span>{{ t('signaturesNotFilled') }}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td class="w-1/4 p-3">
                                  <div
                                    :class="
                                      'badge ' + getDocumentBadgeColor(attachment.documentType.id)
                                    "
                                    class="rounded px-2 py-1 text-[10px] font-semibold text-white"
                                  >
                                    {{ attachment.documentType.name }}
                                  </div>
                                </td>
                                <td class="w-1/4 p-3 text-right">
                                  <div class="flex items-center justify-end gap-3">
                                    <div
                                      v-if="
                                        attachment.documentType.id ===
                                        DocumentTypeIdEnum.PDF_TEMPLATE
                                      "
                                    >
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        class="h-10 w-[60px] px-4 py-2 text-gray-500 hover:bg-gray-500 hover:text-white"
                                        @click="openPdfEditor(attachment)"
                                      >
                                        <PencilIcon class="h-5 w-5" />
                                      </Button>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      class="h-10 w-[60px] px-4 py-2 text-red-500 hover:bg-red-500 hover:text-white"
                                      @click="removeAttachment(attachment)"
                                    >
                                      <TrashIcon class="h-5 w-5" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <!-- Client Email Warning -->
                    <div
                      v-if="
                        !caseRecord?.data?.client?.email ||
                        caseRecord.data.client.email.trim() === ''
                      "
                      class="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800"
                    >
                      <div class="flex items-center">
                        <InfoIcon class="mr-2 h-4 w-4" />
                        <span class="text-sm font-medium">
                          {{ t('noClientEmailWarning') }}
                        </span>
                      </div>
                    </div>

                    <!-- Reject Template Warning -->
                    <div
                      v-if="
                        selectedEmailTemplate?.type === EmailTemplateType.REJECT &&
                        caseRecord?.state !== StateEnum.REJECTED
                      "
                      class="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800"
                    >
                      <div class="flex items-center">
                        <InfoIcon class="mr-2 h-4 w-4" />
                        <span class="text-sm font-medium">
                          {{ t('rejectEmailWarning') }}
                        </span>
                      </div>
                    </div>

                    <!-- Footer with Send Button -->
                    <div class="flex justify-end space-x-2 pt-2">
                      <!-- Regular Send Button (always shown) -->
                      <Button
                        variant="default"
                        class="h-9 px-4 text-white"
                        data-cy="emailCardSendButton"
                        :disabled="
                          isSendingEmail ||
                          emailSubject === '' ||
                          emailContent === '' ||
                          !caseRecord?.data?.client?.email ||
                          caseRecord.data.client.email.trim() === ''
                        "
                        @click="
                          handleSendEmail(selectedEmailTemplate?.type === EmailTemplateType.REJECT)
                        "
                      >
                        <template v-if="isSendingEmail">
                          <span
                            class="spinner-border spinner-border-sm me-3"
                            aria-hidden="true"
                            role="status"
                          ></span>
                        </template>
                        {{ t('send') || 'Send' }}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <div
              id="email-history"
              class="mt-4"
            >
              <Accordion
                v-model="emailHistoryAccordionValue"
                type="single"
                collapsible
                class="w-full"
              >
                <AccordionItem
                  value="email-history"
                  class="border-0 bg-transparent"
                >
                  <AccordionHeader class="flex items-center justify-between px-0 py-2">
                    <AccordionTrigger class="w-full py-0 hover:no-underline">
                      <div class="flex w-full items-center justify-between">
                        <h3 class="text-md font-medium">
                          {{ t('emailHistory') || 'Email History' }}
                        </h3>
                      </div>
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent class="px-0 pb-4 pt-2">
                    <!-- Loading state -->
                    <div
                      v-if="isLoadingEmails"
                      class="flex justify-center py-4"
                    >
                      <div
                        class="spinner-border text-primary"
                        role="status"
                      >
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>

                    <!-- Empty state -->
                    <div
                      v-else-if="emails.length === 0"
                      class="space-y-3"
                      data-cy="noEmailsHint"
                    >
                      <p class="text-sm text-muted-foreground">
                        {{ t('noEmailHistory') || 'No email history available.' }}
                      </p>
                    </div>

                    <!-- Email list -->
                    <div
                      v-else
                      class="space-y-4"
                    >
                      <div
                        v-for="email in emails"
                        :id="email.uuid"
                        :key="email.uuid"
                        class="card overflow-hidden rounded-md border shadow-sm"
                        data-cy="emailHistoryItem"
                      >
                        <div class="p-4">
                          <!-- Header with Avatar, Title, Date and Download -->
                          <div class="mb-1 flex items-start justify-between">
                            <div class="flex min-w-0 flex-1 items-start">
                              <div
                                class="avatar mr-3 flex h-10 w-10 shrink-0 items-center justify-center"
                              >
                                {{ getInitials(email.from_email.name) }}
                              </div>
                              <div class="min-w-0 flex-1">
                                <div
                                  class="truncate text-lg font-bold leading-tight"
                                  data-cy="emailSubject"
                                >
                                  {{ email.subject }}
                                </div>
                                <div class="text-sm leading-relaxed text-gray-700">
                                  <span class="font-medium">Von:</span>
                                  {{ email.from_email.name }} &lt;{{ email.from_email.email }}&gt;
                                </div>
                              </div>
                            </div>
                            <div class="ml-4 flex items-center gap-3">
                              <span class="whitespace-nowrap text-xs text-gray-600">
                                {{ formatDocumentDate(email.sent_at) }}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                class="h-8 w-8 shrink-0 p-0 text-gray-500 hover:text-gray-700"
                                :title="t('download') || 'Herunterladen'"
                                @click="openEmailInNewTab(email)"
                              >
                                <CloudDownloadIcon class="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <!-- To and Status Row -->
                          <div class="flex items-center justify-between">
                            <div
                              v-if="email.to && email.to.length > 0"
                              class="min-w-0 flex-1 text-sm text-gray-700"
                            >
                              <span class="font-medium">An:</span>
                              <span class="ml-1 truncate">
                                <template
                                  v-for="(recipient, index) in email.to"
                                  :key="recipient.email"
                                >
                                  {{ recipient.name }} &lt;{{ recipient.email }}&gt;
                                  <span v-if="index < email.to.length - 1">,</span>
                                </template>
                              </span>
                            </div>

                            <!-- Tracking Status -->
                            <div class="ml-4 flex shrink-0 items-center">
                              <!-- Pending State for Recently Sent Emails -->
                              <div
                                v-if="
                                  pendingEmailUuids.has(email.uuid) &&
                                  caseRecord.org.track_email_delivery_events
                                "
                                class="flex cursor-pointer items-center gap-1 rounded-sm bg-blue-100 px-2 py-1 text-xs font-medium
                                  text-blue-800 transition-colors hover:bg-blue-200"
                                :title="t('deliveryEventPending')"
                                @click="openDeliveryStatusModal(email)"
                              >
                                <Loader2 class="h-3 w-3 animate-spin" />
                                {{ t('deliveryEventPending') }}
                              </div>
                              <!-- Actual Delivery Status -->
                              <div
                                v-else-if="
                                  email.relevant_delivery_event &&
                                  caseRecord.org.track_email_delivery_events
                                "
                                class="flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1 text-xs font-medium transition-opacity
                                  hover:opacity-80"
                                :class="getDeliveryEventStyle(email.relevant_delivery_event.event)"
                                @click="openDeliveryStatusModal(email)"
                              >
                                <component
                                  :is="getDeliveryEventIcon(email.relevant_delivery_event.event)"
                                  class="h-3 w-3"
                                />
                                {{ getDeliveryEventLabel(email.relevant_delivery_event.event) }}
                              </div>
                            </div>
                          </div>

                          <!-- Email Content -->
                          <div class="mt-2 border-t pt-3">
                            <div
                              class="default-styled-container m-2"
                              data-cy="emailContent"
                              v-html="email.html"
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>
      <div class="fixed bottom-0 right-0 mb-4 mr-4">
        <AutoSavingIndicator />
      </div>
    </div>
    <div
      v-else
      class="container px-0"
    >
      <!-- Skeleton Loader -->
      <div>
        <!-- Header Skeleton -->
        <div class="flex flex-row flex-wrap items-start justify-between pt-4 md:flex-nowrap">
          <div class="ml-0 mt-2 h-auto grow">
            <div
              id="title"
              class="position-relative align-items-center justify-content-start flex gap-3"
            >
              <h1 class="text-h1 font-weight-bold flex items-center gap-3 text-nowrap">
                {{ t('requestFrom') }}
                <SkeletonLoader
                  height="32px"
                  width="150px"
                />
              </h1>
              <SkeletonLoader
                class="badge"
                height="24px"
                width="80px"
              />
            </div>
            <p class="flex items-center gap-2 text-sm text-stone-500">
              <SkeletonLoader
                height="16px"
                width="120px"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                aria-hidden="true"
                data-slot="icon"
                class="inline size-4"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                ></path>
              </svg>
              Erstellt am
              <SkeletonLoader
                height="16px"
                width="180px"
              />
            </p>
          </div>
          <div
            id="options"
            class="my-3 flex h-full items-start justify-end gap-2"
          >
            <SkeletonLoader
              height="36px"
              width="180px"
            />
            <SkeletonLoader
              height="36px"
              width="40px"
            />
          </div>
        </div>

        <!-- Navigation Tabs Skeleton -->
        <div class="w-full border-b border-gray-200 pb-0">
          <nav class="flex w-full flex-wrap gap-x-1 overflow-hidden pt-1">
            <button
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-purple-600 py-2 pr-1
                text-sm font-semibold text-purple-600"
            >
              {{ t('general') }}
            </button>
            <button
              v-if="user?.org?.enable_law_specific_data"
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              <span class="flex items-center gap-2">
                {{ t('lawSpecificData') }}
              </span>
            </button>
            <button
              v-if="user?.org?.enable_law_specific_data"
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              <span class="flex items-center gap-2">
                {{ t('entities') }}
              </span>
            </button>
            <button
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              {{ t('documents') }}
            </button>
            <button
              v-if="user?.org?.enable_law_specific_data"
              class="flex-shrink-0 flex-grow-0 cursor-pointer items-center gap-x-1 border-b-2 border-transparent px-1
                py-2 text-sm"
            >
              {{ t('aiChat') }}
            </button>
          </nav>
        </div>

        <!-- Top Form Row Skeleton -->
        <div class="row pb-3">
          <div class="flex flex-row flex-wrap gap-3">
            <div class="mb-3 flex-1">
              <label class="text-sm">{{ t('lawField') }}</label>
              <SkeletonLoader height="40px" />
            </div>
            <div class="mb-3 flex-1">
              <label class="text-sm">{{ t('lawTopic') }}</label>
              <SkeletonLoader height="40px" />
            </div>
            <div class="mb-3 flex-1">
              <label class="text-sm">{{ t('clerk') }}</label>
              <SkeletonLoader height="40px" />
            </div>
          </div>
        </div>

        <div class="row pb-3">
          <!-- Left Column Skeleton -->
          <div class="col-12 col-lg-4 mt-lg-0">
            <!-- Client Card Skeleton -->
            <div
              class="card mb-3"
              data-cy="clientCard"
            >
              <h3
                class="accordion-button mb-0 bg-white"
                aria-expanded="true"
                data-cy="clientCardHeader"
              >
                {{ t('client') }}
              </h3>
              <div class="card-body">
                <div class="space-y-3">
                  <div>
                    <label class="text-sm">{{ t('entityType') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('title') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('specificTitle') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('firstName') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('lastName') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('email') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('phone') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div
                    class="d-flex"
                    style="gap: 0.25rem"
                  >
                    <div style="flex: 3">
                      <label class="text-sm">{{ t('street') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                    <div style="flex: 1">
                      <label class="text-sm">{{ t('houseNumber') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                  </div>
                  <div
                    class="d-flex"
                    style="gap: 0.25rem"
                  >
                    <div style="flex: 1">
                      <label class="text-sm">{{ t('postalCode') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                    <div style="flex: 3">
                      <label class="text-sm">{{ t('city') }}</label>
                      <SkeletonLoader height="40px" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Opponent Card Skeleton -->
            <div
              class="card mb-3"
              data-cy="opponentCard"
            >
              <h3
                class="accordion-button collapsed mb-0 bg-white"
                aria-expanded="false"
                data-cy="opponentCardHeader"
              >
                {{ t('opponent') }}
              </h3>
            </div>

            <!-- Legal Insurance Card Skeleton -->
            <div
              class="card mb-3"
              data-cy="legalInsuranceCard"
            >
              <div class="card-body">
                <div class="space-y-3">
                  <div class="flex items-center gap-2">
                    <SkeletonLoader
                      height="20px"
                      width="20px"
                    />
                    <label class="text-sm">{{ t('legalInsurance') }}</label>
                  </div>
                  <div>
                    <label class="text-sm">{{ t('legalInsuranceName') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                  <div>
                    <label class="text-sm">{{ t('legalInsuranceNumber') }}</label>
                    <SkeletonLoader height="40px" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Internal Notes Card Skeleton -->
            <div
              class="card"
              data-cy="internalNotesCard"
            >
              <div class="card-body">
                <div>
                  <label class="text-sm">{{ t('internalNotes') }}</label>
                  <SkeletonLoader
                    class="mt-2"
                    height="160px"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column Skeleton -->
          <div class="col-12 col-lg-8">
            <div class="flex-column flex gap-3">
              <!-- Inquiry Card Skeleton -->
              <div
                class="card mt-3 lg:mt-0"
                data-cy="inquiryCard"
              >
                <div class="card-header block">
                  <div class="row">
                    <div class="col-6">
                      <h3 class="card-title pt-1">{{ t('inquiry') }}</h3>
                    </div>
                    <div class="col-6">
                      <SkeletonLoader
                        class="float-end"
                        height="40px"
                        width="192px"
                      />
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <div class="space-y-2">
                    <SkeletonLoader height="16px" />
                    <SkeletonLoader height="16px" />
                    <SkeletonLoader
                      height="16px"
                      width="75%"
                    />
                  </div>
                </div>
              </div>

              <!-- Appointments Card Skeleton -->
              <div
                class="card"
                data-cy="appointmentsCard"
              >
                <div class="card-header block">
                  <div class="row">
                    <div class="col-6">
                      <h3 class="card-title pt-1">{{ t('appointments') }}</h3>
                    </div>
                    <div class="col-6">
                      <span class="float-end">
                        <SkeletonLoader
                          height="40px"
                          width="192px"
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div class="card-body p-0">
                  <div class="table-responsive mb-0">
                    <table class="table-vcenter card-table table">
                      <tbody>
                        <tr class="hover:bg-gray-50">
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="140px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="100px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="130px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex flex-1 items-center justify-between gap-3">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="80px"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr class="hover:bg-gray-50">
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="140px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex-1 space-y-2">
                                <SkeletonLoader
                                  height="20px"
                                  width="100px"
                                />
                                <SkeletonLoader
                                  height="20px"
                                  width="130px"
                                />
                              </div>
                            </div>
                          </td>
                          <td class="w-1/3">
                            <div class="flex items-center py-1">
                              <div class="flex flex-1 items-center justify-between gap-3">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="80px"
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Received Documents Card Skeleton -->
              <div
                id="received-documents"
                class="card"
                style="z-index: 0"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                  defaultValue="received-documents"
                >
                  <AccordionItem value="received-documents">
                    <AccordionHeader class="card-header flex grow">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="card-title flex items-center gap-2 pe-2">
                            {{ t('receivedDocuments') }}
                            <SkeletonLoader
                              class="badge bg-danger"
                              height="20px"
                              width="24px"
                            />
                          </h3>
                        </div>
                        <div class="flex items-center">
                          <SkeletonLoader
                            class="me-2"
                            height="32px"
                            width="48px"
                          />
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent class="px-4 pb-4">
                      <table class="table-vcenter card-table table table-fixed">
                        <tbody>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="200px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="150px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="180px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="130px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <!-- Requested Documents Card Skeleton -->
              <div
                id="requested-documents"
                class="card"
                style="z-index: 0"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                  defaultValue="requested-documents"
                >
                  <AccordionItem value="requested-documents">
                    <AccordionHeader class="card-header flex grow">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="card-title flex items-center gap-2 pe-2">
                            {{ t('requestedDocuments') }}
                            <SkeletonLoader
                              class="badge bg-danger"
                              height="20px"
                              width="24px"
                            />
                          </h3>
                        </div>
                        <div class="flex items-center">
                          <SkeletonLoader
                            class="me-2"
                            height="32px"
                            width="48px"
                          />
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent class="px-4 pb-4">
                      <table class="table-vcenter card-table table">
                        <tbody>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="200px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="150px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                          <tr class="hover:bg-gray-50">
                            <td class="w-1/2">
                              <div class="flex items-center gap-3 py-1">
                                <SkeletonLoader
                                  height="40px"
                                  width="40px"
                                />
                                <div class="min-w-0 flex-1 space-y-1">
                                  <SkeletonLoader
                                    height="16px"
                                    width="180px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="130px"
                                  />
                                </div>
                              </div>
                            </td>
                            <td class="w-1/4">
                              <div class="flex flex-col space-y-1">
                                <SkeletonLoader
                                  height="20px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="14px"
                                  width="140px"
                                />
                              </div>
                            </td>
                            <td class="w-1/4 text-right">
                              <div class="flex justify-end gap-2">
                                <SkeletonLoader
                                  height="36px"
                                  width="36px"
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <!-- Email Card Skeleton -->
              <div
                id="email"
                class="card"
                style="z-index: 0"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                  defaultValue="email"
                >
                  <AccordionItem value="email">
                    <AccordionHeader class="card-header flex grow">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="card-title pe-2">
                            {{ t('email') }}
                          </h3>
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent
                      class="px-4 pb-4"
                      :overflow="'visible'"
                    >
                      <div class="space-y-4">
                        <div class="mb-4">
                          <div
                            class="mt-4 flex w-full items-center overflow-hidden rounded-[7px] border border-gray-200"
                          >
                            <label
                              for="email-template"
                              class="m-0 h-10 cursor-default bg-gray-50 px-3 text-sm"
                            >
                              {{ t('selectTemplate') }}
                            </label>
                            <div class="flex-1 px-2">
                              <SkeletonLoader height="32px" />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label class="text-sm">Betreff</label>
                          <SkeletonLoader height="40px" />
                        </div>
                        <SkeletonLoader height="128px" />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <!-- Email History Skeleton -->
              <div
                id="email-history"
                class="mt-4"
              >
                <Accordion
                  type="single"
                  collapsible
                  class="w-full"
                >
                  <AccordionItem
                    value="email-history"
                    class="border-0 bg-transparent"
                  >
                    <AccordionHeader class="flex items-center justify-between px-0 py-2">
                      <AccordionTrigger class="w-full py-0 hover:no-underline">
                        <div class="flex w-full items-center justify-between">
                          <h3 class="text-md font-medium">
                            {{ t('emailHistory') }}
                          </h3>
                        </div>
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent class="px-0 pb-4 pt-2">
                      <div class="space-y-4">
                        <div class="card overflow-hidden rounded-md border shadow-sm">
                          <div class="p-4">
                            <div class="mb-1 flex items-start justify-between">
                              <div class="flex min-w-0 flex-1 items-start">
                                <SkeletonLoader
                                  class="mr-3"
                                  height="40px"
                                  width="40px"
                                  type="circle"
                                />
                                <div class="min-w-0 flex-1 space-y-2">
                                  <SkeletonLoader
                                    height="18px"
                                    width="250px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="300px"
                                  />
                                </div>
                              </div>
                              <div class="ml-4 flex items-center gap-3">
                                <SkeletonLoader
                                  height="12px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="32px"
                                />
                              </div>
                            </div>
                            <div class="mt-2 space-y-2">
                              <SkeletonLoader
                                height="14px"
                                width="100%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="100%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="80%"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="card overflow-hidden rounded-md border shadow-sm">
                          <div class="p-4">
                            <div class="mb-1 flex items-start justify-between">
                              <div class="flex min-w-0 flex-1 items-start">
                                <SkeletonLoader
                                  class="mr-3"
                                  height="40px"
                                  width="40px"
                                  type="circle"
                                />
                                <div class="min-w-0 flex-1 space-y-2">
                                  <SkeletonLoader
                                    height="18px"
                                    width="220px"
                                  />
                                  <SkeletonLoader
                                    height="14px"
                                    width="280px"
                                  />
                                </div>
                              </div>
                              <div class="ml-4 flex items-center gap-3">
                                <SkeletonLoader
                                  height="12px"
                                  width="120px"
                                />
                                <SkeletonLoader
                                  height="32px"
                                  width="32px"
                                />
                              </div>
                            </div>
                            <div class="mt-2 space-y-2">
                              <SkeletonLoader
                                height="14px"
                                width="100%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="95%"
                              />
                              <SkeletonLoader
                                height="14px"
                                width="70%"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SpaCompatComponent>

  <CreateAppointmentModal
    v-model:open="isAppointmentCreateModalOpen"
    :case="
      caseRecord?.uuid
        ? {
            uuid: caseRecord.uuid,
            services: Array.isArray(caseRecord.services) ? caseRecord.services : []
          }
        : { uuid: '', services: [] }
    "
    @appointment-created="
      async () => {
        refreshCaseRecord()
        await fetchEmails()
      }
    "
  />

  <!-- Upload Document Model -->
  <Dialog
    :open="isDocumentUploadModalOpen"
    @update:open="isDocumentUploadModalOpen = $event"
  >
    <DialogContent class="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>{{ t('uploadDocuments') }}</DialogTitle>
      </DialogHeader>
      <DialogDescription>
        {{ t('uploadDocumentsDescription') }}
      </DialogDescription>

      <div class="py-4">
        <DocumentUpload
          v-if="caseRecord"
          :case-data="caseRecord"
          :auto-remove-after-upload="false"
          @file-uploaded="handleFileUploaded"
        />
      </div>

      <DialogFooter>
        <Button
          variant="secondary"
          @click="isDocumentUploadModalOpen = false"
        >
          {{ t('cancel') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Unified Document Template Selection Modal for Link generation -->
  <TemplateDialog
    v-model="showUnifiedTemplateModal"
    :title="t('documentTemplate')"
    :description="t('createLinkDocumentTemplateDescription')"
    :items="unifiedTemplates"
    :is-loading="loadingUnifiedTemplates"
    :search-placeholder="t('searchTemplate')"
    @select-item="createLinkUnifiedTemplate"
    @preview-item="handleUnifiedTemplatePreview"
  />

  <!-- Questionnaire Selection Modal for link generation -->
  <TemplateDialog
    v-model="showQuestionnaireModal"
    :title="t('questionnaire')"
    :description="t('createLinkQuestionnaireDescription')"
    :items="questionnaires"
    :is-loading="loadingQuestionnaires"
    :search-placeholder="t('searchQuestionnaire')"
    @select-item="createLinkQuestionnaire"
    @preview-item="handleQuestionnairePreview"
  />

  <!-- Document Template Selection Dialog for Email attachments -->
  <SelectUnifiedDocumentsDialog
    v-model:show="showDocumentAttachDialog"
    :title="t('documentTemplate')"
    :description="t('questionnaireDescription')"
    :initial-selected-documents="initialSelectedUnifiedDocuments"
    :initial-field-values="initialFieldValuesForDialog"
    :document-form-state="documentFormState"
    @select="handleUnifiedDocumentsSelect"
    @field-value-changed="handleFieldValueChanged"
    @validation-changed="handleValidationChanged"
  />

  <!-- Questionnaire Selection Dialogs for Email attachments -->
  <SelectDocumentsDialog
    v-model="showQuestionnaireAttachDialog"
    :title="t('questionnaireTitle')"
    :description="t('questionnaireDescription')"
    :items="questionnaires"
    :is-loading="loadingQuestionnaires"
    :search-placeholder="t('searchQuestionnaire')"
    :total-count="questionnaireTotalCount"
    :current-page="questionnaireCurrentPage"
    :page-size="questionnairePageSize"
    :initial-selected-items="
      emailAttachments
        .filter((attachment) => attachment.documentType?.id === DocumentTypeIdEnum.QUESTIONNAIRE)
        .map((attachment) => {
          const props = attachment.documentProperties as QuestionnaireDocumentProperties
          return props?.id ? questionnaires.find((q) => q.uuid === props.id) || null : null
        })
        .filter(Boolean)
    "
    @select-items="handleQuestionnairesSelect"
    @preview-item="handleQuestionnairePreview"
    @page-change="handleQuestionnairePage"
    @search="handleQuestionnaireSearch"
  />

  <PDFViewer
    v-model:isOpen="showPdfViewer"
    v-model:url="currentPdfUrl"
    :filename="currentPdfFilename"
  />

  <!-- PDFViewer as Editor Modal -->
  <div class="flex h-full w-full">
    <NewPdfViewer
      :key="'new-pdf-viewer-' + showPdfForms.toString()"
      v-model:isOpen="showPdfEditor"
      :filename="(selectedDocument as any)?.filename || (selectedDocument as any)?.title || ''"
      :url="
        (selectedDocument as any)?.current_pdf_url ||
        (selectedDocument as any)?.file ||
        (selectedDocument as any)?.url ||
        ''
      "
      :hide-navigation="false"
      :annotation="true"
      :hide-forms="!showPdfForms"
      :form-id="(selectedDocument as any)?.id || (selectedDocument as any)?.uuid || ''"
      :document="selectedDocument as any"
      :form-fields="(selectedDocument as any)?.form_fields || []"
      :show-form-fields="(selectedDocument as any)?.template_type === 'pdf_form'"
      :initial-field-values="(selectedDocument as any)?.formFieldValues || {}"
      :external-validation-errors="
        documentFormState.get(
          (selectedDocument as any)?.id || (selectedDocument as any)?.uuid || ''
        )?.validationErrors
      "
      :is-filled-by="'lawyer'"
      @document-updated="handleDocumentUpdated"
      @field-values-changed="handleFieldValuesChanged"
      @validation-changed="handleValidationChanged"
    />

    <SignModal v-if="showPdfEditor && signatureStore.showSignModal" />
  </div>

  <!-- Office Document Preview Modal -->
  <Dialog
    v-if="officeEditingFlag.isEnabled || officePreviewFlag.isEnabled"
    v-model:open="showOfficePreview"
  >
    <DialogContent class="h-[85vh] w-full max-w-5xl overflow-hidden p-0">
      <div class="flex h-full flex-col">
        <DialogHeader class="border-b px-6 py-4">
          <DialogTitle>
            {{
              currentOfficeDocument?.title || currentOfficeDocument?.filename || 'Document Preview'
            }}
          </DialogTitle>
        </DialogHeader>
        <div class="w-full flex-1 overflow-hidden">
          <EmbeddableOfficePreview
            v-if="currentOfficeDocument"
            :document="currentOfficeDocument"
            :token="currentOfficeToken"
            height="100%"
            :allow-download="false"
            :allow-print="false"
            :show-border="false"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Questionnaire Submission Modal -->
  <Dialog
    :open="showQuestionnaireSubmissionModal"
    @update:open="showQuestionnaireSubmissionModal = $event"
  >
    <DialogContent class="gap-0 sm:max-w-[800px]">
      <DialogHeader class="border-b pb-3">
        <DialogTitle>{{ selectedQuestionnaireSubmission?.title || '' }}</DialogTitle>
      </DialogHeader>
      <div class="max-h-[60vh] overflow-y-auto px-2">
        <table class="w-full">
          <tbody>
            <template
              v-if="
                selectedQuestionnaireSubmission?.schema &&
                Array.isArray(selectedQuestionnaireSubmission.schema)
              "
            >
              <template
                v-for="(section, sectionIdx) in selectedQuestionnaireSubmission.schema"
                :key="sectionIdx"
              >
                <h3 class="mt-3 text-lg font-semibold">{{ section?.title || '' }}</h3>
                <tr
                  v-for="(item, itemIdx) in section?.items || []"
                  :key="`${sectionIdx}-${itemIdx}`"
                  class="border-b"
                >
                  <td class="w-1/2 px-4 py-3 text-left align-top">
                    <div class="max-w-[230px] break-words font-medium">
                      {{ item?.label || '' }}
                      <span
                        v-if="item?.optional"
                        class="ml-1 rounded bg-green-100 px-1 py-0.5 text-xs text-green-800"
                      >
                        optional
                      </span>
                    </div>
                  </td>
                  <td class="w-1/2 p-3 font-normal">
                    <div>{{ item?.value || '' }}</div>
                  </td>
                </tr>
              </template>
            </template>
            <tr v-else>
              <td
                colspan="2"
                class="p-4 text-center text-gray-500"
              >
                {{ t('noDataAvailable') || 'No data available' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Delivery Status Modal -->
  <EmailDeliveryStatusModal
    :open="showDeliveryStatusModal"
    :email="selectedEmailForDeliveryStatus"
    @update:open="showDeliveryStatusModal = $event"
  />

  <PendingProposedChangesDialog
    v-model:open="showPendingChangesDialog"
    source="email"
    :case-uuid="caseRecord?.uuid"
    @cancel="handlePendingChangesCancel"
    @review="handlePendingChangesReview"
  />

  <!-- AI Action Negative Rating Modal -->
  <div
    v-if="aiRatingModalActionUuid"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    @click.self="closeAiNegativeRatingModal"
  >
    <div class="mx-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
      <!-- Modal Header -->
      <div class="flex items-center justify-between border-b border-gray-200 p-4">
        <h3 class="text-lg font-semibold text-gray-900">{{ t('negativeFeedback') }}</h3>
        <button
          class="text-gray-400 transition-colors hover:text-gray-600"
          @click="closeAiNegativeRatingModal"
        >
          <X :size="20" />
        </button>
      </div>

      <!-- Modal Content -->
      <div class="space-y-4 p-4">
        <!-- Variant Selection -->
        <div>
          <select
            v-model="aiRatingNegativeVariant"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500
              focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            <option
              value=""
              disabled
            >
              {{ t('selectReasonPlaceholder') }}
            </option>
            <option
              v-for="opt in negativeFeedbackReasonOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Comment -->
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">
            {{ t('additionalComments') }}
          </label>
          <textarea
            v-model="aiRatingNegativeComment"
            rows="3"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500
              focus:outline-none focus:ring-1 focus:ring-purple-500"
          ></textarea>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="flex justify-end gap-2 border-t border-gray-200 p-4">
        <Button
          variant="secondary"
          @click="closeAiNegativeRatingModal"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          :disabled="!aiRatingNegativeVariant || isSubmittingAiRating"
          class="bg-purple-600 text-white hover:bg-purple-700"
          @click="submitAiNegativeRating"
        >
          <RotateCw
            v-if="isSubmittingAiRating"
            :size="14"
            class="mr-2 animate-spin"
          />
          {{ t('submitFeedback') }}
        </Button>
      </div>
    </div>
  </div>

  <!-- AI Assistant Widget — floating overlay -->
  <AiAssistantWidget
    :open="aiWidget.isOpen.value"
    :step="aiWidget.step.value"
    :title="aiWidgetTitle"
    :description="aiWidgetDescription"
    :edited="aiWidget.draftEdited.value"
    :review-label="t('reviewEmail')"
    :send-label="t('sendEmail')"
    :discard-label="t('discard')"
    :success-title="t('aiWidgetSuccessTitle')"
    :success-description="t('aiWidgetSuccessDescription')"
    :close-label="t('close')"
    :edited-label="t('edited')"
    :feedback-title="t('feedbackTitle')"
    :feedback-description="t('feedbackDescription')"
    :feedback-reasoning-link-label="t('feedbackReasoningLink')"
    :feedback-select-placeholder="t('feedbackSelectPlaceholder')"
    :feedback-textarea-placeholder="t('feedbackTextareaPlaceholder')"
    :feedback-submit-label="t('feedbackSubmitLabel')"
    :feedback-cancel-label="t('feedbackCancelLabel')"
    :feedback-reason-options="negativeFeedbackReasonOptions"
    :show-reasoning-link="isAgentActionWidgetEnabled"
    :feedback-submitting="isSubmittingFeedback"
    :feedback-success-title="t('feedbackSuccessTitle')"
    :feedback-success-description="t('feedbackSuccessDescription')"
    @close="handleAiWidgetClose"
    @review-click="aiWidget.handleReview"
    @send-click="aiWidget.handleSend"
    @discard-click="handleAiDiscard"
    @thumbs-up="handleAiWidgetThumbsUp"
    @thumbs-down="handleAiWidgetThumbsDown"
    @retry="aiWidget.handleSend"
    @show-reasoning="agentActionWidgetRef?.openReasoningModal?.()"
    @feedback-submit="handleAiWidgetFeedbackSubmit"
    @feedback-cancel="handleAiWidgetFeedbackCancel"
  />

  <!-- AI Launcher Button — shown when AI lifecycle is active or pending actions exist -->
  <AiLauncherButton
    :widget-open="aiWidget.isOpen.value"
    :visible="hasPendingAgentActions && isAiAssistantEnabled"
    @click="aiWidget.toggle"
  />
</template>

<style>
  .badge-square {
    @apply inline-flex h-5 min-w-5 items-center justify-center overflow-hidden rounded-sm bg-[#ae3ec9] p-1 text-[0.625rem] font-semibold text-white;
  }

  /* Card styles from dashboard.css */
  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    word-wrap: break-word;
    background-color: #ffffff;
    background-clip: border-box;
    border: 1px solid rgba(98, 105, 118, 0.16);
    border-radius: 4px;
  }

  .card > hr,
  .card > .hr {
    margin-right: 0;
    margin-left: 0;
  }

  .card-body {
    flex: 1 1 auto;
    padding: 1rem 1rem;
  }

  .card-title {
    margin-bottom: 1.25rem;
  }

  .card-header {
    padding: 0.75rem 1rem;
    margin-bottom: 0;
    color: #051b35;
    background-color: #ffffff;
    border-bottom: 1px solid rgba(98, 105, 118, 0.16);
  }

  .card-header:first-child {
    border-radius: 3px 3px 0 0;
  }

  .card-footer {
    padding: 0.75rem 1rem;
    color: #051b35;
    background-color: #ffffff;
    border-top: 1px solid rgba(98, 105, 118, 0.16);
  }

  .card-footer:last-child {
    border-radius: 0 0 3px 3px;
  }

  label {
    padding: 5px 5px 5px 0;
  }

  .documents-edit-link {
    color: rgb(147 51 234);
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
  }

  .documents-edit-link:hover {
    color: rgb(147 51 234);
    text-decoration: none;
  }

  .link-text {
    border-bottom: 1px solid rgb(147 51 234);
    padding-bottom: 1px;
    line-height: 1;
    white-space: nowrap;
  }
</style>
